import useAppLanguage from '../i18n';
import localizedPeriodChipRowSx from './medication-form/localizedPeriodChipRowSx';
import { translationKey } from '../i18n';
import { localize } from '../i18n';
import { formatMedicationClockList } from '../utils/medicationClockCopy';
import { checkpointPermissionDraft, readPermissionDraft } from '../utils/permissionDraft';
import useRegistrationDraftCheckpoint from '../hooks/useRegistrationDraftCheckpoint';
import { useRegistrationDraft } from '../components/RegistrationDraftProvider';
import useMedicationAddGate from '../hooks/useMedicationAddGate';
import RegistrationDraftChoice, { draftPrimaryButton, draftSecondaryButton } from '../components/RegistrationDraftChoice';
import RegistrationResultRecovery from '../components/RegistrationResultRecovery';
import { readRegistrationDraft, freezeRegistrationRequest, resolveRegistrationRequest, registrationOwner, discardRegistrationDraft } from '../utils/registrationDraft';
import useNativeImageRequests from '../hooks/useNativeImageRequests';
import useAiDataConsent from '../hooks/useAiDataConsent';
import { getScreenCacheContextRevision } from '../utils/localCache';
import { supportsCorrelatedImagePicker } from '../utils/nativeImageRequests';
import DateInputCard from '../components/ui/DateInputCard';
import AppButton from '../components/ui/AppButton';
import { colors } from '../design/tokens';
import useNavigate from '../hooks/useSafeNavigate';
import useBackHandler from '../hooks/useBackHandler';
import DialogTitle from '../components/ui/FitDialogTitle';
import SingleLineText from '../components/ui/InternationalText';
import Button from '../components/ui/FitButton';
import FeatureUpdateDialog from '../components/FeatureUpdateDialog';
import { needsRelease105Update } from '../utils/release105Access';
import { createRegistrationJourney } from '../utils/productJourneys';
import { trackProduct } from '../utils/productAnalytics';
import Dialog from '../components/StableDialog';
import LoadingLabel from '../components/LoadingLabel';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Box, Typography, TextField, IconButton, Chip, Card, CircularProgress, DialogActions, DialogContent, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MedicationIcon from '@mui/icons-material/Medication';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AppToast from '../components/AppToast';
import BottomSheet from '../components/BottomSheet';
import ScrollDownIndicator from '../components/ScrollDownIndicator';
import PetCharacter from '../components/character/PetCharacter';
import {
    analyzePrescriptionImage,
    analyzeMedicationNames,
    checkMedicationBundleInteractions,
    claimMedicationSlotRewardBonus,
    createMedicationsBulk,
    getDeviceUuid,
    getMedications,
    getMedicationLimit,
    searchMedicines,
    uploadMedicationImage,
} from '../api';
import { syncWidget } from '../utils/widgetSync';
import WebviewMessenger from '../utils/WebviewMessenger';
import { requestAd, rewardedAdFailureMessage } from '../utils/adRequests';
import { compareVersions } from '../utils/version';
import { areRelease105FeaturesAvailable } from '../utils/accountAccess';
import { clearMedicationCaches } from '../utils/localCache';

import AlertMethodSection from './medication-form/reminder/AlertMethodSection';
import AlertPermissionNotices from '../components/AlertPermissionNotices';
import SnoozeSection from './medication-form/reminder/SnoozeSection';
import MedicationFrequencyEditor from './medication-form/MedicationFrequencyEditor';
import MedicationTimesEditor, {
    MedicationTimeOptionList,
    buildMedicationTimeOptions,
    MAX_DAILY_REMINDER_TIMES,
    MEDICATION_TIME_COUNT_PRESETS,
    hasCustomMedicationTimes,
    normalizeMedicationTimes,
    sameMedicationTimes,
} from './medication-form/MedicationTimesEditor';
import { MedicationNameEditor } from './medication-form/MedicationNameEditSheet';
import MedicationPeriodEditor from './medication-form/MedicationPeriodEditor';
import MedicationPhotoSheet, { hiddenPhotoInputStyle } from './medication-form/MedicationPhotoSheet';

import { readPhotoSchedule, mergePhotoSchedule, photoRegimenSchedule, photoScheduleSetup } from './medication-form/photoSchedule';
import { reviewDraftError } from './medication-form/reviewDraftError';
import {
    MEDICATION_CATEGORY_OPTIONS,
    MEDICATION_DOSAGE_METHOD_OPTIONS,
    MEDICATION_INTAKE_FORM_OPTIONS,
    MedicationDosagePerTimeEditor,
    MedicationMemoEditor,
    MedicationOptionEditor,
} from './medication-form/MedicationDetailEditors';

const DAY_PRESETS = [3, 5, 7];
const COUNT_PRESETS = MEDICATION_TIME_COUNT_PRESETS;
const DAY_OPTIONS = [
    { key: 'MON', label: translationKey('월') },
    { key: 'TUE', label: translationKey('화') },
    { key: 'WED', label: translationKey('수') },
    { key: 'THU', label: translationKey('목') },
    { key: 'FRI', label: translationKey('금') },
    { key: 'SAT', label: translationKey('토') },
    { key: 'SUN', label: translationKey('일') },
];
const DAY_ORDER = DAY_OPTIONS.map(day => day.key);

function openNativePicker(input) {
    if (!input) return;
    try {
        if (typeof input.showPicker === 'function') {
            input.showPicker();
            return;
        }
    } catch (_) {}
    try { input.focus(); } catch (_) {}
    try { input.click(); } catch (_) {}
}

const DOSAGE_METHODS = [
    { value: 'AFTER_MEAL', label: translationKey('식후') },
    { value: 'BEFORE_MEAL', label: translationKey('식전') },
    { value: 'BEFORE_SLEEP', label: translationKey('취침 전') },
    { value: 'ANYTIME', label: translationKey('상관없음') },
];
const ALERT_TYPE_LABELS = {
    PUSH: translationKey('일반 알림'),
    FULLSCREEN: translationKey('벨소리 알람'),
    VIBRATION: translationKey('진동'),
};
const MEDICINE_SOURCE_LABELS = {
    DUR: translationKey('식약처'),
    MFDS_PERMISSION: translationKey('식약처'),
    REVIEWED: translationKey('식약처'),
    SUPPLEMENT: translationKey('건기식'),
    ALIAS: translationKey('등록 이름'),
};
const MEDICINE_CATEGORY_LABELS = {
    PRESCRIPTION: translationKey('처방약'),
    OTC: translationKey('일반약'),
    SUPPLEMENT: translationKey('영양제'),
    HERBAL: translationKey('한약'),
    OTHER: translationKey('기타'),
};
const INTAKE_FORM_LABELS = {
    ORAL: translationKey('먹는 약'),
    TOPICAL: translationKey('바르는 약'),
    PATCH: translationKey('붙이는 약'),
    SPRAY: translationKey('뿌리는 약'),
    INSERT: translationKey('넣는 약'),
    OTHER: translationKey('기타'),
};
const INTAKE_FORM_META = {
    ORAL: { label: translationKey('먹는 약'), title: translationKey('복용 정보'), methodLabel: translationKey('복용법'), doseLabel: translationKey('1회 복용량'), unit: translationKey('정') },
    TOPICAL: { label: translationKey('바르는 약'), title: translationKey('사용 정보'), methodLabel: translationKey('사용 시점'), doseLabel: translationKey('1회 사용량'), unit: translationKey('회') },
    PATCH: { label: translationKey('붙이는 약'), title: translationKey('사용 정보'), methodLabel: translationKey('교체 시점'), doseLabel: translationKey('1회 사용량'), unit: translationKey('매') },
    SPRAY: { label: translationKey('뿌리는 약'), title: translationKey('사용 정보'), methodLabel: translationKey('사용 시점'), doseLabel: translationKey('1회 사용량'), unit: translationKey('회') },
    INSERT: { label: translationKey('넣는 약'), title: translationKey('사용 정보'), methodLabel: translationKey('사용 시점'), doseLabel: translationKey('1회 사용량'), unit: translationKey('개') },
    OTHER: { label: translationKey('기타'), title: translationKey('사용 정보'), methodLabel: translationKey('사용 방법'), doseLabel: translationKey('1회 사용량'), unit: translationKey('회') },
};
const DEFAULT_MED_SCHEDULE = {
    frequencyType: 'DAILY',
    specificDays: [],
    hourlyInterval: '',
    intervalDays: '',
    cycleDaysOn: '',
    cycleDaysOff: '',
    monthlyDays: [],
    durationDays: 3,
    startDate: null,
    endDate: null,
    times: ['08:00'],
    dosageMethod: 'ANYTIME',
    category: 'OTHER',
    intakeForm: 'ORAL',
    source: 'default',
};
const MIN_NATIVE_PHOTO_VERSION = '1.0.5';
const APP_RUNTIME_INFO_KEY = 'pill_reminder_app_runtime_info';
const REWARD_FEATURE = 'medication_slot_reward';
const MAX_BULK_MEDICATIONS = 22;
const MAX_AUTO_MEMO_LINES = 3;
const MAX_AUTO_MEMO_CHARS = 250;
const GENERIC_AUTO_MEMO_PHRASES = [
    translationKey('효능및주의사항요약'),
    translationKey('효능과주의사항요약'),
    translationKey('효능주의사항요약'),
    translationKey('효능정보없음'),
    translationKey('효능정보가없음'),
    translationKey('효능없음'),
    translationKey('주의사항요약'),
    translationKey('주의사항정보없음'),
    translationKey('주의사항정보가없음'),
    translationKey('주의사항없음'),
    translationKey('주의사항확인필요'),
    translationKey('약정보확인필요'),
    translationKey('약정보부족'),
    translationKey('약의정보부족'),
    translationKey('약품정보부족'),
    translationKey('의약품정보부족'),
    translationKey('공식약정보없음'),
    translationKey('약정보없음'),
    translationKey('정보부족'),
    translationKey('정보가부족'),
    translationKey('정보가충분하지'),
    translationKey('자료부족'),
    translationKey('추가정보필요'),
    translationKey('식별어려'),
    translationKey('확인불가'),
    translationKey('확인되지않'),
    translationKey('알수없'),
    translationKey('의사또는약사와상담'),
    translationKey('전문가와상담'),
];

const SMART_ANALYSIS_STEPS = [
    translationKey('약 이름 확인 중'),
    translationKey('복용법 찾는 중'),
    translationKey('같이 쓰는 약 확인 중'),
    translationKey('쉽게 정리하는 중'),
];
const SMART_ANALYSIS_MESSAGES = [
    translationKey('약 정보를 바탕으로 복용 횟수를 보고 있어요'),
    translationKey('식전·식후 여부를 확인하고 있어요'),
    translationKey('다른 약과 함께 써도 괜찮은지 살펴보고 있어요'),
    translationKey('확인하기 쉽게 정리하고 있어요'),
];
const KOREAN_ORDER_LABELS = [
    translationKey('첫 번째'),
    translationKey('두 번째'),
    translationKey('세 번째'),
    translationKey('네 번째'),
    translationKey('다섯 번째'),
    translationKey('여섯 번째'),
    translationKey('일곱 번째'),
    translationKey('여덟 번째'),
    translationKey('아홉 번째'),
    translationKey('열 번째'),
    translationKey('열한 번째'),
    translationKey('열두 번째'),
    translationKey('열세 번째'),
    translationKey('열네 번째'),
    translationKey('열다섯 번째'),
    translationKey('열여섯 번째'),
    translationKey('열일곱 번째'),
    translationKey('열여덟 번째'),
    translationKey('열아홉 번째'),
    translationKey('스무 번째'),
];

function sanitizeAutoMemo(raw) {
    const lines = String(raw || '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .split('\n')
        .map(line => line.trim().replace(/^[-•*]\s*/, '').trim())
        .filter(Boolean)
        .filter((line) => {
            const normalized = line.replace(/\s+/g, '');
            return !GENERIC_AUTO_MEMO_PHRASES.some(phrase => normalized.includes(phrase));
        })
        .slice(0, MAX_AUTO_MEMO_LINES);

    return lines.join('\n').slice(0, MAX_AUTO_MEMO_CHARS).trim();
}

const reviewEditIconButtonSx = {
    width: 48,
    height: 48,
    color: '#8f8f8f',
    flexShrink: 0,
    borderRadius: 2.2,
    bgcolor: '#f8fafc',
    border: '1px solid #eef2f7',
    '&:hover': { bgcolor: '#f1f5f9' },
    '&:active': { bgcolor: '#e5e7eb' },
};
const medicationSelectionScrollSx = {
    display: 'flex',
    gap: 0.9,
    overflowX: 'auto',
    pt: 0.9,
    pb: 0.7,
    mx: -0.2,
    px: 0.2,
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
    scrollbarColor: '#d6d6d6 transparent',
    '&::-webkit-scrollbar': {
        height: 4,
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#d6d6d6',
        borderRadius: 999,
    },
};

function readRuntimeInfo() {
    try {
        const raw = localStorage.getItem(APP_RUNTIME_INFO_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function medicationBadgeLabel(med) {
    return MEDICINE_SOURCE_LABELS[med.medicineSource]
        || MEDICINE_CATEGORY_LABELS[med.schedule?.category || med.category]
        || translationKey('직접 입력');
}

function parseMedicineDisplayName(item) {
    if (item?.source === 'PHOTO_ORIGINAL') {
        return {
            title: (item?.itemName || '').trim() || localize('사진에서 읽은 이름'),
            subtitle: localize('사진에서 읽은 이름'),
        };
    }
    const fullName = (item?.itemName || '').trim().replace(/\s+/g, ' ');
    const parenMatch = fullName.match(/[\(（]([^\)）]+)[\)）]/);
    const ingredient = parenMatch && !/수출|군납|내수용|포장단위/.test(parenMatch[1])
        ? parenMatch[1].trim()
        : '';
    const name = fullName
        .replace(/[\(（][^\)）]+[\)）]/g, part => /\d/.test(part) ? part : '')
        .replace(/\s+/g, ' ')
        .trim();
    return {
        title: name || fullName || localize('이름 없는 약'),
        subtitle: ingredient || item?.entpName || localize(MEDICINE_SOURCE_LABELS[item?.source] || translationKey('식약처 의약품')),
    };
}

function photoCandidateIdentity(item) {
    const itemSeq = (item?.itemSeq || '').toString().trim();
    if (itemSeq) return `seq:${itemSeq}`;
    return `name:${normalizePhotoCandidateName(item?.itemName)}`;
}

function isSameVisiblePhotoCandidateName(candidate, originalName) {
    const originalNormalized = normalizePhotoCandidateName(originalName);
    const candidateName = normalizePhotoCandidateName(candidate?.itemName);
    const candidateTitle = normalizePhotoCandidateName(parseMedicineDisplayName(candidate).title);
    return !!originalNormalized && (candidateName === originalNormalized || candidateTitle === originalNormalized);
}

function normalizePhotoCandidateName(value) {
    return (value || '')
        .toString()
        .replace(/\s+/g, '')
        .replace(/[^\p{L}\p{N}]/gu, '')
        .toLowerCase();
}

function buildPhotoCandidateOptions(med) {
    const originalName = (med?.photoOriginalName || med?.name || '').trim();
    const seenIdentities = new Set();
    const originalOption = {
        itemName: originalName,
        entpName: translationKey('사진에서 읽은 이름'),
        itemSeq: null,
        source: 'PHOTO_ORIGINAL',
    };
    const dbOptions = (med?.photoCandidates || [])
        .filter((candidate) => {
            const candidateName = normalizePhotoCandidateName(candidate?.itemName);
            const identity = photoCandidateIdentity(candidate);
            if (!candidateName || !identity) return false;
            if (isSameVisiblePhotoCandidateName(candidate, originalName)) return false;
            if (seenIdentities.has(identity)) return false;
            seenIdentities.add(identity);
            return true;
        })
        .slice(0, 4);
    return [originalOption, ...dbOptions];
}

function uniquePhotoDbCandidates(med) {
    const originalName = (med?.photoOriginalName || med?.name || '').trim();
    const seenIdentities = new Set();
    return (med?.photoCandidates || [])
        .filter((candidate) => {
            const candidateName = normalizePhotoCandidateName(candidate?.itemName);
            const identity = photoCandidateIdentity(candidate);
            if (!candidateName || !identity) return false;
            if (isSameVisiblePhotoCandidateName(candidate, originalName)) return false;
            if (seenIdentities.has(identity)) return false;
            seenIdentities.add(identity);
            return true;
        })
        .slice(0, 4);
}

function isSamePhotoCandidateName(left, right) {
    const normalizedLeft = normalizePhotoCandidateName(left);
    const normalizedRight = normalizePhotoCandidateName(right);
    if (!normalizedLeft || !normalizedRight) return false;
    return normalizedLeft === normalizedRight;
}

function buildPhotoMedicationSchedule(med, base = {}) {
    const { durationDays, times: photoTimes, timesSource } = mergePhotoSchedule(med, base);
    const startDateValue = base.startDate || todayKey();
    return {
        frequencyType: 'DAILY',
        ...photoRegimenSchedule(med.regimenText),
        durationDays,
        indefinite: false,
        startDate: startDateValue,
        endDate: durationDays ? addDays(startDateValue, durationDays - 1) : null,
        times: photoTimes,
        timesSource,
        dosageMethod: med?.dosageMethod || base.dosageMethod || null,
        category: 'PRESCRIPTION',
        intakeForm: 'ORAL',
        alertType: base.alertType || 'PUSH',
        alarmVolume: base.alarmVolume,
        reminderEnabled: base.reminderEnabled,
        reminderIntervalMinutes: base.reminderIntervalMinutes,
        maxReminderMinutes: base.maxReminderMinutes,
        source: 'photo',
    };
}

function todayKey() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDays(dateStr, days) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + days);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function daysBetweenInclusive(startStr, endStr) {
    const [sy, sm, sd] = startStr.split('-').map(Number);
    const [ey, em, ed] = endStr.split('-').map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    const diffDays = Math.round((end - start) / (24 * 60 * 60 * 1000));
    return Math.max(diffDays + 1, 1);
}

function normalizeTimes(times = []) {
    return normalizeMedicationTimes(times);
}

function sameTimes(a = [], b = []) {
    return sameMedicationTimes(a, b);
}

function hasCustomTimes(times = []) {
    return hasCustomMedicationTimes(times);
}

function formatDateKorean(dateStr) {
    if (!dateStr) return '';
    const [, month, day] = dateStr.split('-');
    return localize("{v0}월 {v1}일", { v0: Number(month), v1: Number(day) });
}


function formatTimesKorean(times = []) {
    return formatMedicationClockList(times);
}


function medSchedule(med) {
    return { ...DEFAULT_MED_SCHEDULE, ...(med.schedule || {}) };
}

function formatFrequencyKorean(schedule = {}) {
    const s = { ...DEFAULT_MED_SCHEDULE, ...(schedule || {}) };
    switch (s.frequencyType || 'DAILY') {
        case 'EVERY_OTHER_DAY':
            return localize("격일");
        case 'SPECIFIC_DAYS': {
            const labels = (s.specificDays || []).map(day => DAY_OPTIONS.find(item => item.key === day)?.label || day);
            return labels.length > 0 ? localize("{v0}요일", { v0: labels.join(', ') }) : localize("특정 요일");
        }
        case 'EVERY_N_DAYS':
            return localize("{v0}일마다", { v0: s.intervalDays || 2 });
        case 'HOURLY_INTERVAL':
            return localize("{v0}시간마다", { v0: s.hourlyInterval || 1 });
        case 'CYCLE':
            return localize("{v0}일 복용 / {v1}일 휴약", { v0: s.cycleDaysOn || 1, v1: s.cycleDaysOff || 1 });
        case 'MONTHLY':
            return (s.monthlyDays || []).length > 0 ? localize("매월 {v0}일", { v0: (s.monthlyDays || []).join(', ') }) : localize("매월 특정일");
        case 'AS_NEEDED':
            return localize("필요할 때만");
        case 'DAILY':
        default:
            return localize("매일");
    }
}

function formatPeriodKorean(schedule = {}, start) {
    const s = { ...DEFAULT_MED_SCHEDULE, ...(schedule || {}) };
    const periodStart = s.startDate || start;
    if (s.frequencyType === 'AS_NEEDED') return localize("필요할 때만");
    if (s.indefinite) return localize("{v0}부터 계속", { v0: formatDateKorean(periodStart) });
    if (s.endDate) return localize("{v0}부터 {v1}까지", { v0: formatDateKorean(periodStart), v1: formatDateKorean(s.endDate) });
    return localize("{v0}부터 {v1}일 동안", { v0: formatDateKorean(periodStart), v1: s.durationDays || 3 });
}

function intakeMeta(value) {
    return INTAKE_FORM_META[value] || INTAKE_FORM_META.ORAL;
}

function scheduleKey(schedule) {
    const s = { ...DEFAULT_MED_SCHEDULE, ...(schedule || {}) };
    return [
        s.groupId || '',
        s.frequencyType || 'DAILY',
        (s.specificDays || []).join('|'),
        s.hourlyInterval || '',
        s.intervalDays || '',
        s.cycleDaysOn || '',
        s.cycleDaysOff || '',
        (s.monthlyDays || []).join('|'),
        s.durationDays,
        s.startDate || '',
        s.indefinite ? 'indefinite' : '',
        s.endDate || '',
        (s.times || []).join('|'),
        s.dosageMethod,
        s.source,
    ].join('__');
}

function Section({ icon, title, children }) {
    return (
        <Box sx={{ mb: 2.2 }}>
            {title && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                    <Box sx={{ color: '#8a7000', display: 'flex' }}>{icon}</Box>
                    <SingleLineText sx={{ fontSize: '1rem', fontWeight: 850, color: '#1a1a1a' }}>
                        {title}
                    </SingleLineText>
                </Box>
            )}
            {children}
        </Box>
    );
}

function NameInputGuideCard({ photoSupported, onPhotoClick }) {

    return (
        <Box sx={{ px: 0.1, mt: 0.2 }}>
            <SingleLineText sx={{ fontSize: '0.9rem', color: '#888', fontWeight: 650, lineHeight: 1.55, wordBreak: 'keep-all' }}>
                {localize("같이 써도 괜찮은지 살펴볼게요.")}
            </SingleLineText>
            {photoSupported && (
                <Button
                    variant="text"
                    role="button"
                    tabIndex={0}
                    onClick={onPhotoClick}
                    startIcon={<CameraAltIcon sx={{ fontSize: 17 }} />}
                    endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
                    sx={{
                        mt: 1.25,
                        ml: -0.3,
                        px: 0.7,
                        py: 0.55,
                        minWidth: 0,
                        justifyContent: 'flex-start',
                        color: '#8a7000',
                        fontSize: '0.86rem',
                        fontWeight: 800,
                        borderRadius: 2.2,
                        bgcolor: '#fffbeb',
                        '& .MuiButton-startIcon': { mr: 0.45 },
                        '& .MuiButton-endIcon': { ml: 0.2 },
                        '&:hover': { bgcolor: '#fff6cc', color: '#6f5a00' },
                    }}
                >
                    {localize('처방전/약봉투로 한 번에 입력하기')}
                </Button>
            )}
        </Box>
    );
}

function MedicationLimitCard({ onRewardClick }) {
    return (
        <Box sx={{ px: 0.1, mt: 0.2 }}>
            <SingleLineText sx={{ fontSize: '0.9rem', color: '#888', fontWeight: 650, lineHeight: 1.55, wordBreak: 'keep-all' }}>{localize("더 추가할 수 없어요.")}</SingleLineText>
            <Button
                variant="text"
                role="button"
                tabIndex={0}
                onClick={onRewardClick}
                startIcon={<PlayCircleIcon sx={{ fontSize: 17 }} />}
                endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
                sx={{
                    mt: 1.25,
                    ml: -0.3,
                    px: 0.7,
                    py: 0.55,
                    minWidth: 0,
                    justifyContent: 'flex-start',
                    color: '#8a7000',
                    fontSize: '0.86rem',
                    fontWeight: 800,
                    borderRadius: 2.2,
                    bgcolor: '#fffbeb',
                    '& .MuiButton-startIcon': { mr: 0.45 },
                    '& .MuiButton-endIcon': { ml: 0.2 },
                    '&:hover': { bgcolor: '#fff6cc', color: '#6f5a00' },
                }}
            >{localize("광고 보고 더 등록하기")}</Button>
        </Box>
    );
}

function SmartAnalysisLoading({ medCount = 1 }) {
    const { language } = useAppLanguage();
    const analysisSteps = SMART_ANALYSIS_STEPS;
    const analysisMessages = language === 'ko' ? SMART_ANALYSIS_MESSAGES : SMART_ANALYSIS_MESSAGES.map((label, index) => index === 2 ? localize('AI로 약 조합을 살펴보고 있어요') : label);
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveStep(step => Math.min(step + 1, SMART_ANALYSIS_STEPS.length - 1));
        }, 1200);
        return () => clearInterval(interval);
    }, []);

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: '#fff',
            px: 3,
            py: 'calc(env(safe-area-inset-top, 0px) + 36px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            '@keyframes analysisCardIn': {
                from: { opacity: 0, transform: 'translateY(10px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
            },
            '@keyframes analysisSweep': {
                '0%': { transform: 'translateX(-120%)' },
                '100%': { transform: 'translateX(220%)' },
            },
            '@keyframes analysisDot': {
                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.18)', opacity: 0.82 },
            },
        }}>
            <Box sx={{
                position: 'relative',
                width: 136,
                height: 116,
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <Box sx={{
                    position: 'absolute',
                    bottom: 3,
                    width: 112,
                    height: 34,
                    borderRadius: '50%',
                    bgcolor: '#fff6bf',
                    opacity: 0.8,
                }} />
                <Box sx={{
                    position: 'absolute',
                    right: 8,
                    top: 10,
                    width: 42,
                    height: 30,
                    borderRadius: 3,
                    bgcolor: '#f9fafb',
                    border: '1px solid #eee',
                    boxShadow: '0 8px 18px rgba(0,0,0,0.06)',
                    transform: 'rotate(8deg)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 9,
                        right: 9,
                        top: 9,
                        height: 3,
                        borderRadius: 2,
                        bgcolor: '#ffe566',
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 9,
                        right: 15,
                        top: 17,
                        height: 3,
                        borderRadius: 2,
                        bgcolor: '#e5e7eb',
                    },
                }} />
                <PetCharacter level={3} size={84} wander={false} />
            </Box>

            <SingleLineText sx={{ fontSize: '1.35rem', fontWeight: 850, color: '#1a1a1a', textAlign: 'center', mb: 0.8 }}>{localize("약 정보를 꼼꼼히 확인하고 있어요")}</SingleLineText>
            <Typography sx={{ fontSize: '1rem', color: '#777', textAlign: 'center', lineHeight: 1.55, mb: 3, wordBreak: 'keep-all' }}>
                {medCount > 1 ? localize("약 {v0}개의 복용 시간과 주의사항을 정리하는 중이에요", { v0: medCount }) : localize("복용 시간과 주의사항을 정리하는 중이에요")}
            </Typography>

            <Box sx={{
                width: '100%',
                maxWidth: 360,
                bgcolor: '#fff',
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                boxShadow: '0 12px 30px rgba(0,0,0,0.06)',
                p: 2,
                animation: 'analysisCardIn 0.25s ease-out',
            }}>
                <Box sx={{
                    height: 5,
                    borderRadius: 999,
                    bgcolor: '#f2f2f2',
                    overflow: 'hidden',
                    mb: 2,
                    position: 'relative',
                }}>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '48%',
                        borderRadius: 999,
                        bgcolor: '#ffe566',
                        animation: 'analysisSweep 1.5s ease-in-out infinite',
                    }} />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.15 }}>
                    {analysisSteps.map((label, index) => {
                        const done = index < activeStep;
                        const active = index === activeStep;
                        return (
                            <Box key={localize(label)} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.2,
                                minHeight: 26,
                                color: done || active ? '#1a1a1a' : '#aaa',
                                fontSize: '0.93rem',
                                fontWeight: active ? 750 : 600,
                                transition: 'color 0.2s ease',
                            }}>
                                <Box component="span" sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    flex: '0 0 auto',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: done ? '#22c55e' : active ? '#ffe566' : '#f4f4f5',
                                    color: done ? '#fff' : active ? '#5a4a00' : '#c4c4c4',
                                    fontSize: '0.72rem',
                                    fontWeight: 900,
                                    animation: active ? 'analysisDot 1.2s ease-in-out infinite' : 'none',
                                }}>
                                    {done ? '✓' : index + 1}
                                </Box>
                                <Box component="span">{localize(label)}</Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Box sx={{
                width: '100%',
                maxWidth: 360,
                mt: 1.3,
                p: '13px 15px',
                borderRadius: 3,
                bgcolor: '#fffbeb',
                border: '1px solid #f7e7a5',
                color: '#5a4a00',
                fontSize: '0.9rem',
                fontWeight: 650,
                lineHeight: 1.5,
                textAlign: 'center',
                wordBreak: 'keep-all',
            }}>
                {localize(analysisMessages[activeStep])}
            </Box>

            <SingleLineText sx={{ mt: 2, fontSize: '0.84rem', color: '#aaa', textAlign: 'center', lineHeight: 1.5 }}>{localize("AI 분석 결과는 참고용이에요")}</SingleLineText>
        </Box>
    );
}

async function dataUrlToBlob(dataUrl) {
    const res = await fetch(dataUrl);
    return res.blob();
}

async function dataUrlToFile(dataUrl, filename = 'medication-photo.jpg') {
    const blob = await dataUrlToBlob(dataUrl);
    return new File([blob], filename, { type: blob.type || 'image/jpeg' });
}

function compressImage(file, maxSize = 1024, quality = 0.8) {
    return new Promise((resolve) => {
        try {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);
            img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
            img.onload = () => {
                URL.revokeObjectURL(objectUrl);
                try {
                    const canvas = document.createElement('canvas');
                    let { width, height } = img;
                    if (width > maxSize || height > maxSize) {
                        if (width > height) {
                            height = Math.round(height * maxSize / width);
                            width = maxSize;
                        } else {
                            width = Math.round(width * maxSize / height);
                            height = maxSize;
                        }
                    }
                    canvas.width = width;
                    canvas.height = height;
                    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                    canvas.toBlob((blob) => {
                        if (!blob) { resolve(file); return; }
                        resolve(new File([blob], file.name, { type: 'image/jpeg' }));
                    }, 'image/jpeg', quality);
                } catch {
                    resolve(file);
                }
            };
            img.src = objectUrl;
        } catch {
            resolve(file);
        }
    });
}

async function compressImageUnderLimit(file, maxSize = 2400, quality = 0.94, maxBytes = 7.5 * 1024 * 1024) {
    let nextMaxSize = maxSize;
    let nextQuality = quality;
    let compressed = await compressImage(file, nextMaxSize, nextQuality);

    for (let attempt = 0; attempt < 5 && compressed.size > maxBytes; attempt += 1) {
        nextQuality = Math.max(0.76, nextQuality - 0.06);
        if (attempt >= 2) nextMaxSize = Math.max(1800, Math.round(nextMaxSize * 0.86));
        compressed = await compressImage(file, nextMaxSize, nextQuality);
    }

    return compressed;
}

export default function MedicationBundleFormPage() {
    const location = useLocation();
    return <RegistrationEntry key={location.key} />;
}

function RegistrationEntry() {
    const state = useRegistrationDraft();
    const location = useLocation();
    const navigate = useNavigate();
    const gate = useMedicationAddGate();
    const [admitted, setAdmitted] = useState(undefined);
    useEffect(() => {
        if (!state || admitted !== undefined || !state.ready) return;
        const current = readRegistrationDraft();
        if (!current || location.state?.registrationDraftId === current.id || location.state?.permissionRecovery) setAdmitted(current || null);
    }, [state, admitted, location.state]);
    if (state && !state.ready) return <Box sx={{ px: 2.5, py: 6 }}>
        <Typography sx={{ mb: 2 }}>{state.failed ? localize("작성 내용을 확인하지 못했어요. 연결을 확인해주세요.") : localize("작성 내용을 확인하고 있어요")}</Typography>
        {state.failed && <Button variant="contained" sx={draftPrimaryButton} onClick={state.refresh}>{localize("다시 확인하기")}</Button>}
        <Button variant="contained" sx={{ ...draftSecondaryButton, ml: 1 }} onClick={() => navigate('/')}>{localize('홈으로')}</Button>
    </Box>;
    if (admitted?.status === 'pending') return <RegistrationResultRecovery draft={admitted} />;
    if (state && admitted === undefined && state.draft) return <>
        <Box sx={{ p: 3 }}><Typography>{localize("약 등록")}</Typography><Button variant="contained" sx={draftSecondaryButton} onClick={() => navigate('/')}>{localize("홈으로")}</Button></Box>
        {state.draft.status === 'pending' ? <RegistrationResultRecovery draft={state.draft} /> : <RegistrationDraftChoice open draft={state.draft}
            onClose={() => navigate(typeof location.state?.from === 'string' ? location.state.from : '/', { replace: true })}
            onResume={() => navigate(state.draft.path, { replace: true, state: { registrationDraftId: state.draft.id } })}
            onReplace={() => gate.replaceExistingDraft(state.draft, location.pathname + location.search, { replace: true })}
            onDiscard={() => { if (discardRegistrationDraft(state.draft)) navigate('/', { replace: true }); }} />}
        <gate.MedicationAddGateDialog />
    </>;
    if (state && admitted === undefined) return null;
    return <MedicationBundleForm initialRegistrationDraft={admitted || null} />;
}

function MedicationBundleForm({ initialRegistrationDraft }) {
    const { requestAiConsent, aiConsentDialog } = useAiDataConsent('registration');
    const { language, t } = useAppLanguage();

    useEffect(() => { if (!initialRegistrationDraft) trackProduct('registration_start', { feature: 'medication' }); }, []);
    const navigate = useNavigate();
    const location = useLocation();
    const permissionDraft = useRef(undefined);
    if (permissionDraft.current === undefined) {
        const checkpoint = readPermissionDraft(getDeviceUuid(), location.pathname + location.search)?.payload;
        permissionDraft.current = initialRegistrationDraft?.input
            || (checkpoint?.registrationContext === registrationOwner()?.context ? checkpoint : null) || {};
    }
    const [needsDateConfirmation, setNeedsDateConfirmation] = useState(() => Boolean(initialRegistrationDraft &&
        (initialRegistrationDraft.input.startDate < todayKey() || initialRegistrationDraft.input.medications.some(m =>
            m.schedule?.startDate < todayKey() || (m.schedule?.endDate && m.schedule.endDate < todayKey())))));
    const [searchParams] = useSearchParams();
    const cameraInputRef = useRef(null);
    const photoConsentRef = useRef(false);
    const libraryInputRef = useRef(null);
    const medicationCameraInputRef = useRef(null);
    const medicationLibraryInputRef = useRef(null);
    const pendingMedImageKeyRef = useRef(null);
    const nativeImages = useNativeImageRequests('medication-bundle');
    const [medications, setMedications] = useState(permissionDraft.current.medications ?? ([]));
    const [nextKey, setNextKey] = useState(permissionDraft.current.nextKey ?? (1));
    const [nameDraft, setNameDraft] = useState(permissionDraft.current.nameDraft ?? (''));
    const nameInputRef = useRef(null);
    const nameSearchCardRef = useRef(null);
    const [nameDraftNeedsAdding, setNameDraftNeedsAdding] = useState(false);
    const [nameSearchState, setNameSearchState] = useState({ open: false, results: [], loading: false });
    const [startDate, setStartDate] = useState(permissionDraft.current.startDate ?? (todayKey()));
    const [durationDays, setDurationDays] = useState(permissionDraft.current.durationDays ?? (3));
    const [times, setTimes] = useState(permissionDraft.current.times ?? (['08:00']));
    const [dosageMethod, setDosageMethod] = useState(permissionDraft.current.dosageMethod ?? ('ANYTIME'));
    const [alertType, setAlertType] = useState(permissionDraft.current.alertType ?? ('PUSH'));
    const [alarmVolume, setAlarmVolume] = useState(permissionDraft.current.alarmVolume ?? (80));
    const [reminderEnabled, setReminderEnabled] = useState(permissionDraft.current.reminderEnabled ?? (true));
    const [reminderIntervalMinutes, setReminderIntervalMinutes] = useState(permissionDraft.current.reminderIntervalMinutes ?? (30));
    const [maxReminderMinutes, setMaxReminderMinutes] = useState(permissionDraft.current.maxReminderMinutes ?? (120));
    const [showSnoozeEditor, setShowSnoozeEditor] = useState(false);
    const [saving, setSaving] = useState(false);
    const [preparingReview, setPreparingReview] = useState(false);
    const [medicationLimit, setMedicationLimit] = useState(null);
    const [checkingMedicationLimit, setCheckingMedicationLimit] = useState(false);
    const medicationLimitCheckRef = useRef(false);
    const [limitDialogOpen, setLimitDialogOpen] = useState(false);
    const [rewardSubmitting, setRewardSubmitting] = useState(false);
    const rewardClaimInFlightRef = useRef(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [photoPreparing, setPhotoPreparing] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [analysisResult, setAnalysisResult] = useState(permissionDraft.current.analysisResult ?? (null));
	    const [photoCandidatesConfirmed, setPhotoCandidatesConfirmed] = useState(permissionDraft.current.photoCandidatesConfirmed ?? (false));
	    const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
	    const [runtimeInfo, setRuntimeInfo] = useState(() => readRuntimeInfo());
    const isNativeApp = WebviewMessenger.isInWebView();
    const nativeVersion = runtimeInfo?.appVersion || runtimeInfo?.runtimeVersion || '';
    const needsFeatureUpdate = needsRelease105Update(runtimeInfo);
    const [featureUpdateOpen, setFeatureUpdateOpen] = useState(false);
    useEffect(() => {
        if (needsFeatureUpdate && searchParams.get('mode') === 'photo') setFeatureUpdateOpen(true);
    }, [needsFeatureUpdate, searchParams]);
    const nativePhotoSupported = areRelease105FeaturesAvailable(runtimeInfo) && isNativeApp && nativeVersion && compareVersions(nativeVersion, MIN_NATIVE_PHOTO_VERSION) >= 0;
	    const initialMode = searchParams.get('mode') === 'manual'
	        ? 'manual'
	        : searchParams.get('mode') === 'photo' && nativePhotoSupported
	            ? 'photo'
	            : 'manual';
    const isAsNeededRegistration = searchParams.get('frequency') === 'AS_NEEDED';
    const [entryMode, setEntryMode] = useState(needsDateConfirmation ? 'manual' : permissionDraft.current.entryMode ?? (initialMode)); // 'photo' | 'manual' | 'review'
    const [registrationJourney] = useState(() => createRegistrationJourney(initialRegistrationDraft?.source || initialMode, initialRegistrationDraft?.flowId, isAsNeededRegistration ? 'as_needed' : 'scheduled'));
    useEffect(() => { registrationJourney.mount(); return () => registrationJourney.unmount(); }, [registrationJourney]);
    const [manualStep, setManualStep] = useState(needsDateConfirmation ? 'period' : permissionDraft.current.manualStep ?? ('names')); // names | period | reminder | schedule
    const [usagePeriodType, setUsagePeriodType] = useState(permissionDraft.current.usagePeriodType ?? (null)); // ongoing | fixed
    useEffect(() => {
        if (entryMode === 'manual' && ['names', 'period', 'reminder'].includes(manualStep)) registrationJourney.event('view', `registration_${manualStep}`);
    }, [entryMode, manualStep, registrationJourney]);
    const [customDurationOpen, setCustomDurationOpen] = useState(false);
    const [customDurationAutoOpened, setCustomDurationAutoOpened] = useState(false);
    const [customDurationDraft, setCustomDurationDraft] = useState(permissionDraft.current.customDurationDraft ?? '3');
    const [groupCustomDurationOpen, setGroupCustomDurationOpen] = useState({});
    const [groupCustomDurationDraft, setGroupCustomDurationDraft] = useState(permissionDraft.current.groupCustomDurationDraft ?? {});
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
    const [timeLimitDialogOpen, setTimeLimitDialogOpen] = useState(false);
    const [photoSheetOpen, setPhotoSheetOpen] = useState(false);
    const [medImageSheetOpen, setMedImageSheetOpen] = useState(false);
    const [medListSheetOpen, setMedListSheetOpen] = useState(false);
	    const initialPhotoRequestedRef = useRef(Object.keys(permissionDraft.current).length > 0);
	    const snoozeEditorRef = useRef(null);
    const timeInputRefs = useRef([]);
    const reviewCardRefs = useRef({});
    const periodMedicationChipRefs = useRef({});
    const reminderMedicationChipRefs = useRef({});
    const searchTimersRef = useRef({});
    const nameSearchTimerRef = useRef(null);
    const searchSeqRef = useRef(0);
    // Preserve search requests and their analytics even though this legacy result is not rendered.
    const [, setMedicineSearchState] = useState({});
    const [showMedicineEditor, setShowMedicineEditor] = useState(false);
    const [showScheduleEditor, setShowScheduleEditor] = useState(false);
    const [showAlertEditor, setShowAlertEditor] = useState(false);
	    const [selectedReviewMedKey, setSelectedReviewMedKey] = useState(null);
	    const [showPerMedicationPeriod, setShowPerMedicationPeriod] = useState(permissionDraft.current.showPerMedicationPeriod ?? (false));
	    const [selectedPeriodMedKey, setSelectedPeriodMedKey] = useState(permissionDraft.current.selectedPeriodMedKey ?? (null));
	    const [showPerMedicationReminder, setShowPerMedicationReminder] = useState(permissionDraft.current.showPerMedicationReminder ?? (false));
	    const [selectedReminderMedKey, setSelectedReminderMedKey] = useState(permissionDraft.current.selectedReminderMedKey ?? (null));
	    const [manualPeriodCustomOpen, setManualPeriodCustomOpen] = useState({});
	    const [manualPeriodCustomDraft, setManualPeriodCustomDraft] = useState(permissionDraft.current.manualPeriodCustomDraft ?? {});
	    const [periodConfiguredMedKeys, setPeriodConfiguredMedKeys] = useState(permissionDraft.current.periodConfiguredMedKeys ?? ([]));
	    const [reminderConfiguredMedKeys, setReminderConfiguredMedKeys] = useState(permissionDraft.current.reminderConfiguredMedKeys ?? ([]));
    const [reviewEditSection, setReviewEditSection] = useState(null); // Recovery never reopens a sheet automatically.
    const [reviewEditDraft, setReviewEditDraft] = useState(permissionDraft.current.reviewEditDraft ?? (null));
    const [recoveredReviewEdit, setRecoveredReviewEdit] = useState(permissionDraft.current.pendingReviewEdit || null);
    const [expandedReviewDetails, setExpandedReviewDetails] = useState(permissionDraft.current.expandedReviewDetails ?? ({}));
    const [missingMedAddReturnStep, setMissingMedAddReturnStep] = useState(null); // candidate | period
    const [checkingInteractions, setCheckingInteractions] = useState(false);
    const [interactionWarnings, setInteractionWarnings] = useState(permissionDraft.current.interactionWarnings ?? ([]));
    const [interactionCheckStatus, setInteractionCheckStatus] = useState(permissionDraft.current.interactionCheckStatus ?? ('idle')); // idle | checking | success | failed
    const journeyReviewSeen = useRef(false);
    const journeyWarningSeen = useRef(false);
    useEffect(() => {
        if (entryMode === 'review' && !journeyReviewSeen.current) { journeyReviewSeen.current = true; registrationJourney.event('review', 'registration_review'); }
        if (entryMode === 'review' && interactionWarnings.length > 0 && !journeyWarningSeen.current) { journeyWarningSeen.current = true; registrationJourney.event('view', 'interaction'); }
    }, [entryMode, interactionWarnings.length, registrationJourney]);
    const [existingMedications, setExistingMedications] = useState([]);
    const [selectedExistingTimeKey, setSelectedExistingTimeKey] = useState(permissionDraft.current.selectedExistingTimeKey ?? (null));
    const [selectedReminderExistingTimeKeys, setSelectedReminderExistingTimeKeys] = useState(permissionDraft.current.selectedReminderExistingTimeKeys ?? ({}));
    const [pendingFocusTimeIndex, setPendingFocusTimeIndex] = useState(null);
    const [pendingTimePreset, setPendingTimePreset] = useState(null);
    const [pendingReviewTimePreset, setPendingReviewTimePreset] = useState(null);
    const [pendingSelectedReminderTimePreset, setPendingSelectedReminderTimePreset] = useState(null);
    const [photoAnalysisNotice, setPhotoAnalysisNotice] = useState(null);
    const manualAnalysisCacheRef = useRef(null);
    const imageAnalysisGenerationRef = useRef(0);
    const bulkCreateRequestIdRef = useRef(permissionDraft.current.bulkCreateRequestId || null);
    const bulkCreatePayloadRef = useRef(permissionDraft.current.bulkCreatePayload || null);
    const saveInFlightRef = useRef(false);

    const dismissKeyboard = useCallback(() => {
        const activeElement = document.activeElement;
        if (activeElement && typeof activeElement.blur === 'function') {
            activeElement.blur();
        }
    }, []);

    const openPhotoSheet = useCallback(() => {
        if (needsFeatureUpdate) { setFeatureUpdateOpen(true); return; }
        if (!nativePhotoSupported) return;
        dismissKeyboard();
        setPhotoSheetOpen(true);
    }, [dismissKeyboard, nativePhotoSupported, needsFeatureUpdate]);
    const [bundleInteractionWarnings, setBundleInteractionWarnings] = useState(null);
    const [bundleInteractionCheckStatus, setBundleInteractionCheckStatus] = useState(null);
    const [lastCheckedInteractionInputKey, setLastCheckedInteractionInputKey] = useState(null);
    const [scheduleSource, setScheduleSource] = useState(permissionDraft.current.scheduleSource ?? ('default'));
    const draftCheckpoint = useRegistrationDraftCheckpoint(initialRegistrationDraft, location.pathname + location.search,
        () => ({ hasTemporaryPhotos: Boolean(imagePreview || medications.some(med => med.imageFile || med.imagePreview)) || permissionDraft.current.hasTemporaryPhotos,
            medications, nextKey, nameDraft, startDate, durationDays, times, dosageMethod, alertType, alarmVolume,
            reminderEnabled, reminderIntervalMinutes, maxReminderMinutes, photoCandidatesConfirmed, entryMode, manualStep,
            usagePeriodType, periodConfiguredMedKeys, reminderConfiguredMedKeys, selectedExistingTimeKey,
            selectedReminderExistingTimeKeys, customDurationDraft, groupCustomDurationDraft, manualPeriodCustomDraft,
            pendingReviewEdit: reviewEditSection && reviewEditDraft ? { section: reviewEditSection, draft: reviewEditDraft, key: selectedReviewMedKey } : recoveredReviewEdit,
            scheduleSource, showPerMedicationPeriod, selectedPeriodMedKey, showPerMedicationReminder, selectedReminderMedKey }), registrationJourney);
    useEffect(() => {
        if (Object.keys(permissionDraft.current).length > 0) setToast({ open: true, severity: 'info', message: permissionDraft.current.hasTemporaryPhotos ? localize('작성하던 내용을 불러왔어요. 사진은 다시 첨부해주세요.') : localize('작성하던 내용을 불러왔어요.') });
    }, []);

    useEffect(() => {
        if (pendingFocusTimeIndex === null) return undefined;
        const timer = setTimeout(() => {
            const input = timeInputRefs.current[pendingFocusTimeIndex];
            openNativePicker(input);
            setPendingFocusTimeIndex(null);
        }, 80);
        return () => clearTimeout(timer);
    }, [pendingFocusTimeIndex, times.length]);

    useEffect(() => {
        let cancelled = false;
        getMedications(getDeviceUuid())
            .then((meds) => {
                if (!cancelled) {
                    const nextMeds = Array.isArray(meds) ? meds : [];
                    setExistingMedications(nextMeds);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setExistingMedications([]);
                }
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!showSnoozeEditor) return undefined;
        const scrollToSnoozeEditor = (smooth = true) => {
            const target = snoozeEditorRef.current;
            if (!target) return;
            const rect = target.getBoundingClientRect();
            const bottomSafeArea = 128;
            const targetTop = window.scrollY + rect.bottom - window.innerHeight + bottomSafeArea;
            if (targetTop > window.scrollY + 4) {
                window.scrollTo({ top: targetTop, behavior: smooth ? 'smooth' : 'auto' });
            }
        };
        const first = setTimeout(() => scrollToSnoozeEditor(true), 120);
        const second = setTimeout(() => scrollToSnoozeEditor(false), 520);
        return () => {
            clearTimeout(first);
            clearTimeout(second);
        };
	    }, [showSnoozeEditor]);

    useEffect(() => {
        if (manualStep !== 'period' || !showPerMedicationPeriod || !selectedPeriodMedKey) return undefined;
        const timer = window.setTimeout(() => {
            periodMedicationChipRefs.current[selectedPeriodMedKey]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }, 80);
        return () => window.clearTimeout(timer);
    }, [manualStep, selectedPeriodMedKey, showPerMedicationPeriod]);

    useEffect(() => {
        if (manualStep !== 'reminder' || !showPerMedicationReminder || !selectedReminderMedKey) return undefined;
        const timer = window.setTimeout(() => {
            reminderMedicationChipRefs.current[selectedReminderMedKey]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }, 80);
        return () => window.clearTimeout(timer);
    }, [manualStep, selectedReminderMedKey, showPerMedicationReminder]);

    const filledMeds = useMemo(
        () => medications
            .map(med => ({ ...med, name: med.name.trim() }))
            .filter(med => med.name.length > 0),
        [medications],
    );
    const existingTimeOptions = useMemo(
        () => buildMedicationTimeOptions(existingMedications),
        [existingMedications],
    );
	    useEffect(() => {
	        const optionKeys = new Set(existingTimeOptions.map(option => option.key));
	        if (selectedExistingTimeKey && !optionKeys.has(selectedExistingTimeKey)) {
	            setSelectedExistingTimeKey(null);
	        }
	        setSelectedReminderExistingTimeKeys(prev => {
	            const next = Object.fromEntries(
	                Object.entries(prev).filter(([, optionKey]) => optionKeys.has(optionKey)),
	            );
	            return Object.keys(next).length === Object.keys(prev).length ? prev : next;
	        });
	    }, [existingTimeOptions, selectedExistingTimeKey]);
	    useEffect(() => {
	        if (filledMeds.length === 0) {
	            setSelectedReviewMedKey(null);
	            setSelectedPeriodMedKey(null);
	            setSelectedReminderMedKey(null);
	            return;
	        }
	        if (!filledMeds.some(med => med.key === selectedReviewMedKey)) {
	            setSelectedReviewMedKey(filledMeds[0].key);
	        }
	        if (!filledMeds.some(med => med.key === selectedPeriodMedKey)) {
	            setSelectedPeriodMedKey(filledMeds[0].key);
	        }
		        if (!filledMeds.some(med => med.key === selectedReminderMedKey)) {
		            setSelectedReminderMedKey(filledMeds[0].key);
		        }
		        setPeriodConfiguredMedKeys(prev => prev.filter(key => filledMeds.some(med => med.key === key)));
		        setReminderConfiguredMedKeys(prev => prev.filter(key => filledMeds.some(med => med.key === key)));
	    }, [filledMeds, selectedPeriodMedKey, selectedReminderMedKey, selectedReviewMedKey]);
    const endDate = useMemo(() => addDays(startDate, Math.max(durationDays - 1, 0)), [durationDays, startDate]);
    const snoozeInterval = Number(reminderIntervalMinutes);
    const snoozeMax = Number(maxReminderMinutes);
    const snoozeReady = !reminderEnabled || (snoozeInterval > 0 && snoozeMax >= snoozeInterval);
    const formatSnoozeSummary = (interval, max) => localize("{v0}분마다, 최대 {v1}", { v0: interval, v1: Number(max) >= 60 && Number(max) % 60 === 0
            ? localize("{v0}시간 동안", { v0: Number(max) / 60 })
            : localize("{v0}분 동안", { v0: max }) });
    const snoozeSummaryText = reminderEnabled && snoozeReady
        ? formatSnoozeSummary(snoozeInterval, snoozeMax)
        : reminderEnabled
            ? localize('집요한 알림 시간을 확인해주세요')
            : localize('첫 알림 한 번만');
    const formatAlertMethod = (type = alertType, volume = alarmVolume) => (
        type === 'FULLSCREEN'
            ? `${localize(ALERT_TYPE_LABELS[type])} (${volume}%)`
            : localize(ALERT_TYPE_LABELS[type] || translationKey('일반 알림'))
    );
    const canSave = filledMeds.length > 0 && startDate;
    const scheduleGroups = useMemo(() => {
        const groups = [];
        filledMeds.forEach((med) => {
            const schedule = medSchedule(med);
            const key = scheduleKey(schedule);
            const existing = groups.find(group => group.key === key);
            if (existing) existing.meds.push(med);
            else groups.push({ key, schedule, meds: [med] });
        });
        return groups;
    }, [filledMeds]);
	    const selectedReviewMed = filledMeds.find(med => med.key === selectedReviewMedKey) || filledMeds[0] || null;
	    const manualDefaultSchedule = useMemo(() => ({
	        ...DEFAULT_MED_SCHEDULE,
	        frequencyType: isAsNeededRegistration ? 'AS_NEEDED' : 'DAILY',
	        startDate,
	        durationDays: usagePeriodType === 'fixed' ? durationDays : null,
	        endDate: usagePeriodType === 'fixed' ? endDate : null,
	        indefinite: isAsNeededRegistration || usagePeriodType === 'ongoing',
	        times: isAsNeededRegistration ? [] : normalizeTimes(times).slice(0, MAX_DAILY_REMINDER_TIMES),
	        dosageMethod,
	        alertType: isAsNeededRegistration ? 'PUSH' : alertType,
	        alarmVolume: isAsNeededRegistration ? null : alarmVolume,
	        reminderEnabled: isAsNeededRegistration ? false : reminderEnabled,
	        reminderIntervalMinutes: isAsNeededRegistration ? 0 : reminderIntervalMinutes,
	        maxReminderMinutes: isAsNeededRegistration ? 0 : maxReminderMinutes,
	    }), [
	        alertType,
	        alarmVolume,
	        dosageMethod,
	        durationDays,
	        endDate,
	        isAsNeededRegistration,
	        maxReminderMinutes,
	        reminderEnabled,
	        reminderIntervalMinutes,
	        startDate,
	        times,
	        usagePeriodType,
	    ]);
	    const effectiveManualSchedule = useCallback((med) => ({
	        ...manualDefaultSchedule,
	        ...(med?.schedule || {}),
	        times: med?.schedule?.times || manualDefaultSchedule.times,
	    }), [manualDefaultSchedule]);
	    const effectiveScheduleForManualAnalysis = useCallback((med) => {
	        const schedule = effectiveManualSchedule(med);
	        return {
	            ...schedule,
	            ...(!showPerMedicationPeriod ? {
	                startDate: manualDefaultSchedule.startDate,
	                durationDays: manualDefaultSchedule.durationDays,
	                endDate: manualDefaultSchedule.endDate,
	                indefinite: manualDefaultSchedule.indefinite,
	            } : {}),
	            ...(!showPerMedicationReminder ? {
	                times: manualDefaultSchedule.times,
	                alertType: manualDefaultSchedule.alertType,
	                alarmVolume: manualDefaultSchedule.alarmVolume,
	                reminderEnabled: manualDefaultSchedule.reminderEnabled,
	                reminderIntervalMinutes: manualDefaultSchedule.reminderIntervalMinutes,
	                maxReminderMinutes: manualDefaultSchedule.maxReminderMinutes,
	            } : {}),
	        };
	    }, [effectiveManualSchedule, manualDefaultSchedule, showPerMedicationPeriod, showPerMedicationReminder]);
	    const selectedPeriodMed = filledMeds.find(med => med.key === selectedPeriodMedKey) || filledMeds[0] || null;
	    const selectedReminderMed = filledMeds.find(med => med.key === selectedReminderMedKey) || filledMeds[0] || null;
	    const selectedPeriodSchedule = selectedPeriodMed ? effectiveManualSchedule(selectedPeriodMed) : manualDefaultSchedule;
	    const selectedReminderSchedule = selectedReminderMed ? effectiveManualSchedule(selectedReminderMed) : manualDefaultSchedule;
	    const isMedicationPeriodConfigured = (med) => {
	        const schedule = effectiveManualSchedule(med);
	        const hasPeriod = schedule.indefinite || Number(schedule.durationDays) > 0 || !!schedule.endDate;
	        return periodConfiguredMedKeys.includes(med.key) && hasPeriod;
	    };
	    const isMedicationReminderConfigured = (med) => {
	        const schedule = effectiveManualSchedule(med);
	        const medTimes = Array.isArray(schedule.times) ? schedule.times : [];
	        const interval = Number(schedule.reminderIntervalMinutes ?? reminderIntervalMinutes);
	        const maxMinutes = Number(schedule.maxReminderMinutes ?? maxReminderMinutes);
	        const reminderCheckReady = !(schedule.reminderEnabled ?? reminderEnabled) || (interval > 0 && maxMinutes >= interval);
	        return reminderConfiguredMedKeys.includes(med.key)
	            && medTimes.length > 0
	            && medTimes.every(time => /^\d{2}:\d{2}$/.test(time))
	            && reminderCheckReady;
	    };
	    const selectedPeriodTouched = selectedPeriodMed ? periodConfiguredMedKeys.includes(selectedPeriodMed.key) : false;
	    const selectedReminderTouched = selectedReminderMed ? reminderConfiguredMedKeys.includes(selectedReminderMed.key) : false;
	    const allPeriodMedsConfigured = !showPerMedicationPeriod
	        || filledMeds.every(isMedicationPeriodConfigured);
	    const allReminderMedsConfigured = !showPerMedicationReminder
	        || filledMeds.every(isMedicationReminderConfigured);
	    const selectedPeriodReady = showPerMedicationPeriod && selectedPeriodMed ? isMedicationPeriodConfigured(selectedPeriodMed) : false;
	    const selectedReminderReady = showPerMedicationReminder && selectedReminderMed ? isMedicationReminderConfigured(selectedReminderMed) : false;
	    const nextUnconfiguredPeriodMed = showPerMedicationPeriod
	        ? (filledMeds.find(med => !isMedicationPeriodConfigured(med) && med.key !== selectedPeriodMed?.key)
	            || filledMeds.find(med => !isMedicationPeriodConfigured(med))
	            || null)
	        : null;
	    const nextUnconfiguredReminderMed = showPerMedicationReminder
	        ? (filledMeds.find(med => !isMedicationReminderConfigured(med) && med.key !== selectedReminderMed?.key)
	            || filledMeds.find(med => !isMedicationReminderConfigured(med))
	            || null)
	        : null;
	    const activeUsagePeriodType = showPerMedicationPeriod
	        ? (selectedPeriodTouched ? (selectedPeriodSchedule.indefinite ? 'ongoing' : 'fixed') : null)
	        : usagePeriodType;
	    const activePeriodStartDate = showPerMedicationPeriod
	        ? (selectedPeriodSchedule.startDate || startDate)
	        : startDate;
	    const activeDurationDays = showPerMedicationPeriod
	        ? (selectedPeriodSchedule.durationDays || durationDays || 3)
	        : durationDays;
	    const activeEndDate = showPerMedicationPeriod
	        ? (selectedPeriodSchedule.endDate || addDays(activePeriodStartDate, Math.max(activeDurationDays - 1, 0)))
	        : endDate;
	    const activeTimes = showPerMedicationReminder
	        ? (selectedReminderTouched ? (selectedReminderSchedule.times || []) : [])
	        : times;
	    const activeSnoozeEnabled = showPerMedicationReminder
	        ? (selectedReminderSchedule.reminderEnabled ?? reminderEnabled)
	        : reminderEnabled;
    const selectedReviewSchedule = selectedReviewMed ? medSchedule(selectedReviewMed) : DEFAULT_MED_SCHEDULE;
    const selectedReviewDraftSchedule = reviewEditDraft?.schedule || selectedReviewSchedule;
    const selectedReviewDraftIntakeMeta = intakeMeta(selectedReviewDraftSchedule.intakeForm || reviewEditDraft?.intakeForm || selectedReviewMed?.intakeForm || 'ORAL');
    const reviewInteractionWarnings = useMemo(() => {
        const seen = new Set();
        return interactionWarnings.filter((warning) => {
            const pair = [warning.checkedName || '', warning.medicationName || ''].sort().join('|');
            const key = `${pair}|${warning.severity || ''}|${warning.description || warning.reason || warning.message || ''}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [interactionWarnings]);
    const hasReviewInteractionWarnings = reviewInteractionWarnings.length > 0;
    const hasCriticalReviewInteraction = reviewInteractionWarnings.some(warning => warning.severity === localize('금기'));
    const interactionInputKey = JSON.stringify(filledMeds.map(med => ({
        key: med.key, name: med.name, itemSeq: med.itemSeq || null,
        medicineSource: med.medicineSource || null,
    })));
    const isPhotoCandidateReview = entryMode === 'review' && !!analysisResult && !photoCandidatesConfirmed;
    const interactionCheckPending = entryMode === 'review' && !isPhotoCandidateReview && filledMeds.length > 0
        && !Array.isArray(bundleInteractionWarnings)
        && (lastCheckedInteractionInputKey !== interactionInputKey || checkingInteractions
            || interactionCheckStatus === 'idle' || interactionCheckStatus === 'checking');
	    const isPhotoScheduleReview = !!analysisResult && photoCandidatesConfirmed;
	    const hasAnyPhotoDbCandidates = isPhotoCandidateReview && filledMeds.some(med => (
	        buildPhotoCandidateOptions(med).some(candidate => candidate.source !== 'PHOTO_ORIGINAL')
	    ));
    const showReviewInteractionCard = !isPhotoCandidateReview && hasReviewInteractionWarnings;
    const [acknowledgedInteractionSignature, setAcknowledgedInteractionSignature] = useState(null);
    const interactionWarningSignature = JSON.stringify(reviewInteractionWarnings);
    const showInteractionWarningScreen = entryMode === 'review' && showReviewInteractionCard
        && acknowledgedInteractionSignature !== interactionWarningSignature;
    const finalReviewVisible = entryMode === 'review' && !isPhotoCandidateReview && !showInteractionWarningScreen
        && !preparingReview && !draftCheckpoint.conflict && lastCheckedInteractionInputKey === interactionInputKey
        && !checkingInteractions && ['success', 'failed'].includes(interactionCheckStatus);
    useEffect(() => {
        // Count the actual final screen, after photo candidates and any interaction warning.
        if (finalReviewVisible) registrationJourney.event('view', 'registration_review');
    }, [finalReviewVisible, registrationJourney]);
    const showScheduleGroupCards = showScheduleEditor && entryMode !== 'review';
    const nativeMedicationPhotoBridgeSupported = supportsCorrelatedImagePicker(runtimeInfo) && isNativeApp
        && nativeVersion
        && compareVersions(nativeVersion, MIN_NATIVE_PHOTO_VERSION) >= 0;
    const photoBusy = photoPreparing || analyzing;
    const showPhotoMissingMedAction = !!analysisResult && (isPhotoCandidateReview || (photoCandidatesConfirmed && scheduleSource === 'photo'));
    const showEditableForm = entryMode === 'manual' || entryMode === 'review';
    const showScheduleFields = entryMode === 'review';
    const isNameOnlyStep = entryMode === 'manual' && manualStep === 'names';
    const hasNameDraft = nameDraft.trim().length > 0;
    const showPendingNameNext = isNameOnlyStep && hasNameDraft && filledMeds.length > 0;
    const isUsagePeriodStep = entryMode === 'manual' && manualStep === 'period';
    const isReminderStep = entryMode === 'manual' && manualStep === 'reminder';
	    const usagePeriodSelected = activeUsagePeriodType === 'ongoing' || activeUsagePeriodType === 'fixed';
	    const customDurationValid = !customDurationOpen || Number(customDurationDraft) > 0;
	    const usagePeriodReady = showPerMedicationPeriod
	        ? allPeriodMedsConfigured
	        : (usagePeriodType === 'ongoing' || (usagePeriodType === 'fixed' && customDurationValid));
	    const reminderReady = showPerMedicationReminder
	        ? allReminderMedsConfigured
	        : (times.length > 0 && times.every(time => /^\d{2}:\d{2}$/.test(time)) && snoozeReady);
	    const usagePeriodActionReady = showPerMedicationPeriod ? selectedPeriodReady : usagePeriodReady;
	    const reminderActionReady = showPerMedicationReminder ? selectedReminderReady : reminderReady;
    const primaryActionDisabled = (isUsagePeriodStep && needsDateConfirmation) || (entryMode === 'review' && recoveredReviewEdit) || (isPhotoCandidateReview && medications.some(med => !med.name.trim())) ? true : isNameOnlyStep
        ? filledMeds.length === 0
	        : isUsagePeriodStep
	            ? !usagePeriodActionReady
	            : isReminderStep
	                ? !reminderActionReady
	                : (!canSave || (!isPhotoCandidateReview && interactionCheckPending));
	    const primaryActionLabel = (() => {
	        if (saving || preparingReview) return null;
	        if (isNameOnlyStep) return translationKey('다음으로');
	        if (isUsagePeriodStep) {
	            if (showPerMedicationPeriod) {
	                if (allPeriodMedsConfigured) return translationKey('다음으로');
	                return selectedPeriodReady ? translationKey('다음 약 설정') : translationKey('이 약의 기간을 설정해주세요');
	            }
	            return usagePeriodReady ? translationKey('다음으로') : translationKey('복용 기간을 선택해주세요');
	        }
	        if (isReminderStep) {
	            if (showPerMedicationReminder) {
	                if (allReminderMedsConfigured) return translationKey('다음으로');
	                return selectedReminderReady ? translationKey('다음 약 설정') : translationKey('이 약의 알림을 설정해주세요');
	            }
	            return reminderReady ? translationKey('다음으로') : translationKey('알림 시간을 선택해주세요');
	        }
	        return filledMeds.length > 0 ? translationKey('등록하기') : translationKey('약 이름을 입력해주세요');
	    })();
    const remainingSlots = medicationLimit?.remaining;
    const limitExempt = medicationLimit?.limitExempt === true;
    const hasKnownLimit = !limitExempt && typeof remainingSlots === 'number';
    const reachedMedicationLimit = hasKnownLimit && filledMeds.length >= remainingSlots;
    const usedLastAvailableSlot = reachedMedicationLimit && filledMeds.length > 0;

    useEffect(() => {
        if (!isNameOnlyStep || !hasNameDraft || reachedMedicationLimit) return;
        const input = nameInputRef.current;
        const card = nameSearchCardRef.current;
        if (!input || !card) return;
        const viewport = window.visualViewport;
        let frame;
        const revealNameAction = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                if (document.activeElement !== input) return;
                const visibleTop = viewport?.offsetTop || 0;
                const visibleBottom = visibleTop + (viewport?.height || window.innerHeight);
                // Keep the action clear of the keyboard accessory/compatibility area, and the
                // focused input below the 64px fixed header with 12px of breathing room.
                const keyboardIsOpen = window.innerHeight - (viewport?.height || window.innerHeight) > 100;
                const bottomClearance = keyboardIsOpen ? 64 : 12;
                const hiddenHeight = card.getBoundingClientRect().bottom + bottomClearance - visibleBottom;
                const inputSpace = input.getBoundingClientRect().top - visibleTop - 76;

                // Search results can arrive after iOS has already scrolled the focused input.
                if (hiddenHeight > 0 && inputSpace > 0) {
                    window.scrollBy({ top: Math.min(hiddenHeight, inputSpace), behavior: 'auto' });
                }
            });
        };
        const observer = new ResizeObserver(revealNameAction);
        observer.observe(card);
        input.addEventListener('focus', revealNameAction);
        viewport?.addEventListener('resize', revealNameAction);
        window.addEventListener('resize', revealNameAction);
        revealNameAction();
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            input.removeEventListener('focus', revealNameAction);
            viewport?.removeEventListener('resize', revealNameAction);
            window.removeEventListener('resize', revealNameAction);
        };
    }, [isNameOnlyStep, hasNameDraft, reachedMedicationLimit]);

    const normalizeDurationInput = (value) => String(value || '').replace(/\D/g, '').slice(0, 4);
    const preventNonNumericInput = (event) => {
        if (event.data && /\D/.test(event.data)) event.preventDefault();
    };
    const searchItemMemo = (item) => sanitizeAutoMemo(
        item?.efcyQesitm
        || item?.efcy
        || item?.effect
        || ''
    );
	    const commitDurationDraft = (draft, fallback = durationDays) => {
	        const parsed = Number(draft);
	        return Math.max(1, Math.min(3650, parsed || fallback || 1));
	    };

    const configurePhotoScheduleState = useCallback((nextMeds, result) => {
        const commonPhotoSchedule = readPhotoSchedule(result?.schedule || {});
        const firstSchedule = nextMeds[0]?.schedule || buildPhotoMedicationSchedule({}, {
            startDate,
            alertType,
            alarmVolume,
            reminderEnabled,
            reminderIntervalMinutes,
            maxReminderMinutes,
            durationDays: commonPhotoSchedule.durationDays,
            times: commonPhotoSchedule.times,
            timesSource: commonPhotoSchedule.timesSource,
            dosageMethod: result?.schedule?.dosageMethod || null,
        });
	        const commonDuration = firstSchedule.durationDays;
	        const commonTimes = firstSchedule.times || [];
	        const { periodKeys, reminderKeys, perMedicationPeriod, perMedicationReminder } = photoScheduleSetup(nextMeds);
	        const configuredKeys = nextMeds.map(med => med.key);
	        const usesCustomDuration = commonDuration > 0 && !DAY_PRESETS.includes(Number(commonDuration));

	        setDurationDays(commonDuration || 3);
	        setCustomDurationOpen(!perMedicationPeriod && usesCustomDuration);
	        setCustomDurationAutoOpened(!perMedicationPeriod && usesCustomDuration);
	        setCustomDurationDraft(commonDuration ? String(commonDuration) : '');
	        setTimes(commonTimes);
	        setDosageMethod(firstSchedule.dosageMethod || 'ANYTIME');
        setUsagePeriodType(commonDuration ? 'fixed' : null);
	        setShowPerMedicationPeriod(perMedicationPeriod);
	        setShowPerMedicationReminder(perMedicationReminder);
	        setPeriodConfiguredMedKeys(periodKeys);
	        setReminderConfiguredMedKeys(reminderKeys);
	        if (configuredKeys.length > 0) {
	            setSelectedPeriodMedKey(configuredKeys[0]);
	            setSelectedReminderMedKey(configuredKeys[0]);
	        }
	    }, [
	        alertType,
	        alarmVolume,
	        durationDays,
	        maxReminderMinutes,
	        reminderEnabled,
	        reminderIntervalMinutes,
	        startDate,
	        times,
	    ]);

	    const applyAnalysisResult = useCallback(async (result) => {
	        setPhotoAnalysisNotice(null);
        const foundMeds = (result?.medications || []).filter(med => med.name?.trim());
        if (foundMeds.length === 0) {
            setAnalysisResult(null);
            setPhotoAnalysisNotice({
                type: 'noMeds',
                message: localize("약 이름을 찾지 못했어요"),
            });
            return false;
	        }
	        const parsedMeds = foundMeds.slice(0, MAX_BULK_MEDICATIONS);
	        const meds = parsedMeds
	            .map((med, index) => {
	                const originalName = med.name.trim();
	                const key = Date.now() + index;
	                const dbCandidates = uniquePhotoDbCandidates({ photoCandidates: med.candidates || [] });
	                const autoDbCandidate = dbCandidates.length === 1
	                    && isSamePhotoCandidateName(originalName, parseMedicineDisplayName(dbCandidates[0]).title || dbCandidates[0]?.itemName)
	                    ? dbCandidates[0]
	                    : null;
	                const selectedDisplay = autoDbCandidate ? parseMedicineDisplayName(autoDbCandidate) : null;
	                const commonPhotoSchedule = readPhotoSchedule(result?.schedule || {});
	                const schedule = buildPhotoMedicationSchedule(med, {
	                    startDate,
	                    alertType,
	                    alarmVolume,
	                    reminderEnabled,
	                    reminderIntervalMinutes,
	                    maxReminderMinutes,
	                    durationDays: commonPhotoSchedule.durationDays,
	                    times: commonPhotoSchedule.times,
	                    timesSource: commonPhotoSchedule.timesSource,
	                    dosageMethod: result?.schedule?.dosageMethod || null,
	                });
	                return {
	                    key,
	                    name: autoDbCandidate ? (selectedDisplay?.title || autoDbCandidate.itemName || originalName) : originalName,
	                    photoOriginalName: originalName,
                        unsupportedRegimen: !!med.unsupportedRegimen,
                        regimenText: med.regimenText || '',
	                    photoCandidates: med.candidates || [],
	                    photoNeedsCandidateReview: dbCandidates.length > 1 || (dbCandidates.length === 1 && !autoDbCandidate),
	                    itemSeq: autoDbCandidate?.itemSeq || null,
	                    medicineSource: autoDbCandidate?.source || (autoDbCandidate?.itemSeq ? 'DUR' : null),
	                    dosagePerTime: med.dosagePerTime ?? null,
	                    memo: autoDbCandidate ? searchItemMemo(autoDbCandidate) : '',
	                    schedule,
	                };
	            });
	        if (meds.length > 0) {
	            setMedications(meds);
	            setNextKey(Date.now() + meds.length + 1);
        }
        if (foundMeds.length > MAX_BULK_MEDICATIONS) {
            setPhotoAnalysisNotice({
                type: 'limit',
                message: localize("한 번에 확인할 수 있는 {v0}개만 보여드렸어요.", { v0: MAX_BULK_MEDICATIONS }),
            });
            setToast({
                open: true,
                severity: 'info',
                message: localize("한 번에 {v0}개까지 확인할 수 있어요", { v0: MAX_BULK_MEDICATIONS }),
            });
        }
		        configurePhotoScheduleState(meds, result);
		                setScheduleSource('photo');
		                setAnalysisResult(result);
			        setPhotoCandidatesConfirmed(false);
			        setMissingMedAddReturnStep(null);
			        setPhotoPreviewOpen(false);
			        setEntryMode('review');
		        setManualStep('schedule');
	        setShowMedicineEditor(false);
	        setShowScheduleEditor(false);
	        setShowAlertEditor(false);
	        window.scrollTo({ top: 0, behavior: 'smooth' });
	        return true;
	    }, [
	        alertType,
	        alarmVolume,
	        configurePhotoScheduleState,
	        maxReminderMinutes,
	        reminderEnabled,
	        reminderIntervalMinutes,
	        searchItemMemo,
	        startDate,
	    ]);

	    const confirmPhotoCandidatesAndContinue = useCallback(() => {
	        setPhotoCandidatesConfirmed(true);
	        setEntryMode('manual');
	        setManualStep('period');
        setShowMedicineEditor(false);
        setShowScheduleEditor(false);
	        setShowAlertEditor(false);
	        window.scrollTo({ top: 0, behavior: 'smooth' });
	    }, []);

    const analyzeImageBlob = useCallback(async (blob, precision = false) => {
        if (!blob || analyzing) return;
        if (!photoConsentRef.current) { setPhotoSheetOpen(true); return; }
        photoConsentRef.current = false;
        registrationJourney.photo();
        registrationJourney.event('submit', 'photo_analysis');
        const generation = imageAnalysisGenerationRef.current + 1;
        imageAnalysisGenerationRef.current = generation;
        setPhotoAnalysisNotice(null);
        setAnalyzing(true);
        try {
	            const result = await analyzePrescriptionImage(getDeviceUuid(), blob, precision, 'photo-analysis-v1');
                if (imageAnalysisGenerationRef.current !== generation) return;
	            const applied = await applyAnalysisResult(result);
	            if (!applied) return;
        } catch (err) {
            if (imageAnalysisGenerationRef.current !== generation) return;
            registrationJourney.event('error', 'photo_analysis');
            // 서버/AI 원문 오류(예: 400 Bad Request)는 사용자에게 노출하지 않는다.
            console.error('Prescription image analysis failed:', err?.response?.status, err?.response?.data || err?.message);
            const status = err?.response?.status;
            const msg = status === 429
                ? translationKey('오늘 사진 등록 횟수를 모두 사용했어요.')
                : status === 413
                    ? translationKey('사진 용량이 너무 커요. 8MB 이하 사진을 선택해주세요.')
                    : translationKey('사진 분석 요청을 완료하지 못했어요. 잠시 후 다시 시도해주세요.');
            setPhotoAnalysisNotice({
                type: status === 429 ? 'quota' : 'error',
                message: msg,
            });
            setToast({ open: true, severity: status === 429 ? 'warning' : 'error', message: msg });
        } finally {
            if (imageAnalysisGenerationRef.current === generation) setAnalyzing(false);
        }
    }, [analyzing, applyAnalysisResult, registrationJourney]);

    const handleNativeImage = useCallback(async (message) => {
        if (message.type !== 'PRESCRIPTION_IMAGE_RESULT' && message.type !== 'MEDICATION_IMAGE_RESULT') return;
        const request = nativeImages.take(message);
        if (!request) return;
        if (message.status === 'cancelled') {
            if (message.type === 'PRESCRIPTION_IMAGE_RESULT') registrationJourney.event('cancel', 'photo_analysis');
            setAnalyzing(false);
            return;
        }
        if (message.status !== 'success') {
            setAnalyzing(false);
            setToast({
                open: true,
                severity: message.status === 'permission_denied' ? 'warning' : 'error',
                message: message.message || localize("사진을 가져오지 못했어요"),
            });
            return;
        }
        try {
            const dataUrl = message.image?.dataUrl;
            if (!dataUrl) throw new Error('missing image');
            if (request.isCurrent() && message.type === 'MEDICATION_IMAGE_RESULT') {
                const key = request.target;
                if (!key) return;
                setMedications(prev => prev.map(med => med.key === key ? { ...med, imageLoading: true } : med));
                const file = await dataUrlToFile(dataUrl);
                if (!request.isCurrent()) return;
                if (file.size > 20 * 1024 * 1024) {
                    setToast({ open: true, severity: 'error', message: localize("20MB 이하 이미지만 추가할 수 있어요") });
                    return;
                }
                const compressed = await compressImage(file);
                if (!request.isCurrent()) return;
                const preview = URL.createObjectURL(compressed);
                setMedications(prev => prev.map(med => {
                    if (med.key !== key) return med;
                    if (med.imagePreview?.startsWith('blob:')) URL.revokeObjectURL(med.imagePreview);
                    return { ...med, imageFile: compressed, imagePreview: preview, imageLoading: false };
                }));
                return;
            }
            setPhotoPreparing(true);
            setAnalysisResult(null);
            setPhotoCandidatesConfirmed(false);
            const file = await dataUrlToFile(dataUrl, 'prescription-photo.jpg');
            if (!request.isCurrent()) return;
            const compressed = await compressImageUnderLimit(file, 2400, 0.94);
            if (!request.isCurrent()) return;
            const preview = URL.createObjectURL(compressed);
            if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
            setImagePreview(preview);
            setPhotoPreparing(false);
            await analyzeImageBlob(compressed, false);
        } catch {
            if (!request.isCurrent()) return;
            setPhotoPreparing(false);
            setPhotoAnalysisNotice({
                type: 'error',
                message: localize("사진을 읽지 못했어요"),
            });
            setToast({ open: true, severity: 'error', message: localize("사진을 읽지 못했어요") });
        } finally {
            if (request.isCurrent() && message.type === 'MEDICATION_IMAGE_RESULT') {
                const key = request.target;
                if (key) {
                    setMedications(prev => prev.map(med => med.key === key ? { ...med, imageLoading: false } : med));
                }
                pendingMedImageKeyRef.current = null;
                setMedImageSheetOpen(false);
            }
        }
    }, [analyzeImageBlob, imagePreview, nativeImages]);

    useEffect(() => WebviewMessenger.onMessage(handleNativeImage), [handleNativeImage]);

    useEffect(() => () => {
        Object.values(searchTimersRef.current).forEach(timer => clearTimeout(timer));
        if (nameSearchTimerRef.current) clearTimeout(nameSearchTimerRef.current);
        medications.forEach((med) => {
            if (med.imagePreview?.startsWith('blob:')) URL.revokeObjectURL(med.imagePreview);
        });
        if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    }, []);

    useEffect(() => {
        const handler = (event) => {
            setRuntimeInfo(event.detail || readRuntimeInfo());
        };
        window.addEventListener('app-runtime-info-updated', handler);
        return () => window.removeEventListener('app-runtime-info-updated', handler);
    }, []);

    useEffect(() => {
        let cancelled = false;
        getMedicationLimit(getDeviceUuid())
            .then(limit => {
                if (!cancelled) setMedicationLimit(limit);
            })
            .catch(() => {
                if (!cancelled) setMedicationLimit(null);
            });
        return () => { cancelled = true; };
    }, []);

    const claimRewardSlots = useCallback(async (reason = null) => {
        if (rewardClaimInFlightRef.current) return;
        rewardClaimInFlightRef.current = true;
        setRewardSubmitting(true);
        try {
            const limit = await claimMedicationSlotRewardBonus(getDeviceUuid());
            if (!limit.success) {
                setToast({
                    open: true,
                    severity: 'warning',
                    message: limit.message || localize("보너스를 받을 수 없어요"),
                });
                return;
            }
            setMedicationLimit(limit);
            setLimitDialogOpen(false);
            setToast({
                open: true,
                severity: 'success',
                message: reason === 'ad_free'
                    ? localize("광고 제거 혜택으로 약 {v0}개를 더 등록할 수 있어요", { v0: limit.rewardSlots || 3 })
                    : localize("약 {v0}개를 더 등록할 수 있어요", { v0: limit.rewardSlots || 3 }),
            });
        } catch {
            setToast({
                open: true,
                severity: 'error',
                message: localize("보너스 처리 중 오류가 발생했어요"),
            });
        } finally {
            rewardClaimInFlightRef.current = false;
            setRewardSubmitting(false);
        }
    }, []);

    useEffect(() => {
        const handler = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.feature !== REWARD_FEATURE) return;
                if (data.type === 'REWARDED_AD_COMPLETE') claimRewardSlots(data.reason || null);
                if (data.type === 'REWARDED_AD_UNAVAILABLE') {
                    setToast({
                        open: true,
                        severity: 'warning',
                        message: rewardedAdFailureMessage(data.reason),
                    });
                }
            } catch {}
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [claimRewardSlots]);

    useEffect(() => {
        if (initialPhotoRequestedRef.current) return;
        if (entryMode !== 'photo') return;
        const source = searchParams.get('source');
        if (source !== 'camera' && source !== 'library') return;
        initialPhotoRequestedRef.current = true;
        setTimeout(() => requestNativeImage(source), 250);
    }, [entryMode, searchParams]);

    useEffect(() => {
        if (isPhotoCandidateReview || entryMode !== 'review' || interactionInputKey === '[]') {
            setInteractionWarnings([]);
            setCheckingInteractions(false);
            setInteractionCheckStatus('idle');
            return;
        }
        if (Array.isArray(bundleInteractionWarnings)) {
            setLastCheckedInteractionInputKey(interactionInputKey);
            setInteractionWarnings(bundleInteractionWarnings);
            setCheckingInteractions(false);
            setInteractionCheckStatus(bundleInteractionCheckStatus === 'success' ? 'success' : 'failed');
            return;
        }
        let cancelled = false;
        const consentContextRevision = getScreenCacheContextRevision();
        setCheckingInteractions(true);
        setInteractionCheckStatus('checking');
        (language === 'ko' ? Promise.resolve(true) : requestAiConsent()).then(accepted => {
            if (cancelled || consentContextRevision !== getScreenCacheContextRevision()) return null;
            if (!accepted) return { medications: [], interactionCheckStatus: 'failed' };
            return checkMedicationBundleInteractions(getDeviceUuid(), JSON.parse(interactionInputKey));
        }).then(result => {
            if (cancelled || !result) return;
            setLastCheckedInteractionInputKey(interactionInputKey);
            setInteractionWarnings((result.medications || []).flatMap(med => (
                (med.warnings || []).map(warning => ({ ...warning, checkedName: med.name }))
            )));
            setInteractionCheckStatus(result.interactionCheckStatus === 'success' ? 'success' : 'failed');
        }).catch(() => {
            if (cancelled) return;
            setLastCheckedInteractionInputKey(interactionInputKey);
            setInteractionWarnings([]);
            setInteractionCheckStatus('failed');
        }).finally(() => {
            if (!cancelled) setCheckingInteractions(false);
        });
        return () => { cancelled = true; };
    }, [entryMode, interactionInputKey, bundleInteractionWarnings, bundleInteractionCheckStatus, isPhotoCandidateReview, requestAiConsent, language]);

    const updateMedication = (key, patch) => {
        setMedications(prev => prev.map(med => med.key === key ? { ...med, ...patch } : med));
    };

    const updateMedicationName = (key, value) => {
        setBundleInteractionWarnings(null);
        updateMedication(key, {
            name: value,
            itemSeq: null,
            medicineSource: null,
            localizedLanguage: null,
        });

        if (searchTimersRef.current[key]) clearTimeout(searchTimersRef.current[key]);
        if (language !== 'ko' || value.trim().length < 1) {
            setMedicineSearchState(prev => ({
                ...prev,
                [key]: { open: false, results: [], loading: false },
            }));
            return;
        }

        const requestSeq = searchSeqRef.current + 1;
        searchSeqRef.current = requestSeq;
        setMedicineSearchState(prev => ({
            ...prev,
            [key]: { ...(prev[key] || {}), open: true, loading: true },
        }));
        searchTimersRef.current[key] = setTimeout(async () => {
            try {
                const data = await searchMedicines(value.trim(), language);
                if (requestSeq !== searchSeqRef.current) return;
                setMedicineSearchState(prev => ({
                    ...prev,
                    [key]: {
                        open: true,
                        loading: false,
                        results: (data?.items || []).slice(0, 6),
                    },
                }));
            } catch {
                if (requestSeq !== searchSeqRef.current) return;
                setMedicineSearchState(prev => ({
                    ...prev,
                    [key]: { open: false, loading: false, results: [] },
                }));
            }
        }, 250);
    };

    const selectPhotoCandidateForReview = (key, item) => {
        if (!item?.itemName) return;
        setBundleInteractionWarnings(null);
        setMedications(prev => prev.map(med => {
            if (med.key !== key) return med;
            return {
                ...med,
                name: item.itemName,
                itemSeq: item.itemSeq || null,
                medicineSource: item.source || (item.itemSeq ? 'DUR' : null),
                memo: searchItemMemo(item) || med.memo || '',
                schedule: {
                    ...medSchedule(med),
                    category: item.source === 'SUPPLEMENT' ? 'SUPPLEMENT' : medSchedule(med).category,
                },
            };
        }));
    };

    const usePhotoOriginalNameForReview = (key) => {
        setBundleInteractionWarnings(null);
        setMedications(prev => prev.map(med => {
            if (med.key !== key || !med.photoOriginalName) return med;
            return {
                ...med,
                name: med.photoOriginalName,
                itemSeq: null,
                medicineSource: null,
            };
        }));
    };


    const closeLimitDialog = () => {
        if (rewardSubmitting) return;
        setLimitDialogOpen(false);
    };

    const requestMedicationSlotReward = () => {
        const requested = requestAd('rewarded', REWARD_FEATURE);
        if (!requested) {
            setToast({
                open: true,
                severity: 'warning',
                message: localize("앱에서만 광고를 보고 등록 개수를 늘릴 수 있어요"),
            });
        }
    };

    const addMedicationFromName = (name, extra = {}) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        const duplicate = medications.some(med => med.name.trim() === trimmed && (extra.itemSeq ? med.itemSeq === extra.itemSeq : true));
        if (duplicate) {
            setToast({ open: true, severity: 'info', message: localize("이미 추가한 약이에요") });
            return;
        }
        if (reachedMedicationLimit) {
            requestMedicationSlotReward();
            return;
        }
        setBundleInteractionWarnings(null);
        setMedications(prev => [
            ...prev,
            {
                key: nextKey,
                name: trimmed,
                dosagePerTime: 1,
                ...extra,
            },
        ]);
        setNextKey(prev => prev + 1);
        setNameDraft('');
        setNameDraftNeedsAdding(false);
        setNameSearchState({ open: false, results: [], loading: false });
    };

    const removeMedication = (key) => {
        setBundleInteractionWarnings(null);
        setMedications(prev => prev.filter(med => med.key !== key));
    };

    const openMedicationListSheet = () => {
        const active = document.activeElement;
        if (active && typeof active.blur === 'function') {
            active.blur();
        }
        setTimeout(() => setMedListSheetOpen(true), 80);
    };

    const handleNameDraftChange = (value) => {
        setNameDraft(value);
        setNameDraftNeedsAdding(false);
        if (nameSearchTimerRef.current) clearTimeout(nameSearchTimerRef.current);
        if (language !== 'ko' || value.trim().length < 1) {
            setNameSearchState({ open: false, results: [], loading: false });
            return;
        }

        const requestSeq = searchSeqRef.current + 1;
        searchSeqRef.current = requestSeq;
        setNameSearchState(prev => ({ ...prev, open: true, loading: true, failed: false }));
        nameSearchTimerRef.current = setTimeout(async () => {
            try {
                const data = await searchMedicines(value.trim(), language);
                if (requestSeq !== searchSeqRef.current) return;
                setNameSearchState({
                    open: true,
                    loading: false,
                    results: (data?.items || []).slice(0, 20),
                });
            } catch {
                if (requestSeq !== searchSeqRef.current) return;
                setNameSearchState({ open: true, loading: false, results: [], failed: true });
            }
        }, 250);
    };

	    const selectMedicineFromDraft = (item) => {
            registrationJourney.event('select', 'medicine_search');
		        addMedicationFromName(item.itemName, {
	            itemSeq: item.itemSeq || null,
	            medicineSource: item.source || (item.itemSeq ? 'DUR' : null),
	            localizedLanguage: 'ko',
	            memo: searchItemMemo(item),
	        });
	    };

    const chooseMedImage = (key) => {
        nativeImages.invalidate();
        dismissKeyboard();
        pendingMedImageKeyRef.current = key;
        const currentMed = medications.find(med => med.key === key);
        if (!isNativeApp || nativeMedicationPhotoBridgeSupported || currentMed?.imagePreview) {
            setMedImageSheetOpen(true);
            return;
        }
        const input = medicationLibraryInputRef.current;
        if (!input) return;
        input.value = '';
        checkpointPermissionDraft();
        input.click();
    };

    const requestNativeMedicationImage = (source) => {
        if (source !== 'camera' && source !== 'library') return;
        if (WebviewMessenger.isInWebView()) {
            setMedImageSheetOpen(false);
            WebviewMessenger.send({ type: source === 'camera' ? 'TAKE_MEDICATION_PHOTO' : 'PICK_MEDICATION_IMAGE',
                requestId: nativeImages.begin('MEDICATION_IMAGE_RESULT', pendingMedImageKeyRef.current) });
        }
    };

    const handleMedicationImageSelect = async (event) => {
        const key = pendingMedImageKeyRef.current;
        const file = event.target.files?.[0];
        if (!key || !file) {
            setMedImageSheetOpen(false);
            return;
        }
        setMedications(prev => prev.map(med => med.key === key ? { ...med, imageLoading: true } : med));
        try {
            if (file.size > 20 * 1024 * 1024) {
                setToast({ open: true, severity: 'error', message: localize("20MB 이하 이미지만 추가할 수 있어요") });
                return;
            }
            const compressed = await compressImage(file);
            const preview = URL.createObjectURL(compressed);
            setMedications(prev => prev.map(med => {
                if (med.key !== key) return med;
                if (med.imagePreview?.startsWith('blob:')) URL.revokeObjectURL(med.imagePreview);
                return { ...med, imageFile: compressed, imagePreview: preview, imageLoading: false };
            }));
        } catch {
            setToast({ open: true, severity: 'error', message: localize("사진을 불러오지 못했어요") });
        } finally {
            setMedications(prev => prev.map(med => med.key === key ? { ...med, imageLoading: false } : med));
            event.target.value = '';
            pendingMedImageKeyRef.current = null;
            setMedImageSheetOpen(false);
        }
    };

    const clearMedicationImage = (key) => {
        setMedications(prev => prev.map(med => {
            if (med.key !== key) return med;
            if (med.imagePreview?.startsWith('blob:')) URL.revokeObjectURL(med.imagePreview);
            return { ...med, imageFile: null, imagePreview: null, imageLoading: false };
        }));
    };

    const clearPendingMedicationImage = () => {
        const key = pendingMedImageKeyRef.current;
        if (!key) return;
        clearMedicationImage(key);
        pendingMedImageKeyRef.current = null;
    };

    const setPresetCount = (preset, options = {}) => {
        const nextTimes = normalizeTimes(preset.times);
        setPendingFocusTimeIndex(null);
        if (!options.keepExistingTimeSelection) {
            setSelectedExistingTimeKey(null);
        }
        setTimes(nextTimes);
        if (entryMode === 'review') {
            setMedications(prev => prev.map(med => ({
                ...med,
                schedule: { ...medSchedule(med), times: nextTimes, source: 'manual' },
            })));
        }
    };

    const handlePresetCountClick = (preset) => {
        if (sameTimes(times, preset.times)) {
            setPresetCount(preset);
            return;
        }
        if (hasCustomTimes(times)) {
            setPendingTimePreset(preset);
            return;
        }
        setPresetCount(preset);
    };

    const handleExistingTimeOptionClick = (option) => {
        const preset = { ...option, times: normalizeTimes(option.times), source: 'existing-medication' };
        if (selectedExistingTimeKey === option.key) {
            setSelectedExistingTimeKey(null);
            return;
        }
        setSelectedExistingTimeKey(option.key);
        setPresetCount(preset, { keepExistingTimeSelection: true });
    };

    const addTime = () => {
        setSelectedExistingTimeKey(null);
        setTimes(prev => {
            if ((prev || []).length >= MAX_DAILY_REMINDER_TIMES) {
                setTimeLimitDialogOpen(true);
                return prev;
            }
            if ((prev || []).some(time => !time)) {
                setToast({ open: true, severity: 'warning', message: localize("비어있는 시간을 먼저 선택해주세요") });
                return prev;
            }
            setPendingFocusTimeIndex(prev.length);
            const next = [...prev, ''];
            if (entryMode === 'review') {
                setMedications(meds => meds.map(med => ({
                    ...med,
                    schedule: { ...medSchedule(med), times: next, source: 'manual' },
                })));
            }
            return next;
        });
    };

    const updateTime = (index, value) => {
        setSelectedExistingTimeKey(null);
        setTimes(prev => {
            if (value && prev.some((time, i) => i !== index && time === value)) {
                setToast({ open: true, severity: 'warning', message: localize("이미 같은 시간이 있어요") });
                return prev;
            }
            const next = prev.map((time, i) => i === index ? value : time);
            if (entryMode === 'review') {
                setMedications(meds => meds.map(med => ({
                    ...med,
                    schedule: { ...medSchedule(med), times: next, source: 'manual' },
                })));
            }
            return next;
        });
    };

    const removeTime = (index) => {
        setSelectedExistingTimeKey(null);
        setTimes(prev => {
            const next = prev.length <= 1 ? prev : normalizeTimes(prev.filter((_, i) => i !== index));
            if (entryMode === 'review') {
                setMedications(meds => meds.map(med => ({
                    ...med,
                    schedule: { ...medSchedule(med), times: next, source: 'manual' },
                })));
            }
            return next;
        });
    };


    const updateGroupSchedule = (groupKey, patch) => {
        setMedications(prev => prev.map((med) => {
            if (scheduleKey(medSchedule(med)) !== groupKey) return med;
            return {
                ...med,
                schedule: { ...medSchedule(med), ...patch, source: 'manual' },
            };
        }));
    };


	    const updateManualMedicationSchedule = (key, patch) => {
	        setMedications(prev => prev.map((med) => {
	            if (med.key !== key) return med;
	            return {
	                ...med,
	                schedule: { ...effectiveManualSchedule(med), ...patch, source: 'manual' },
	            };
	        }));
	    };

	    const updateSelectedPeriodSchedule = (patch) => {
	        if (!selectedPeriodMed) return;
	        updateManualMedicationSchedule(selectedPeriodMed.key, patch);
	    };

	    const markPeriodConfigured = (key = selectedPeriodMed?.key) => {
	        if (!key) return;
	        setPeriodConfiguredMedKeys(prev => prev.includes(key) ? prev : [...prev, key]);
	    };

	    const markReminderConfigured = (key = selectedReminderMed?.key) => {
	        if (!key) return;
	        setReminderConfiguredMedKeys(prev => prev.includes(key) ? prev : [...prev, key]);
	    };

	    const setSelectedPeriodType = (type) => {
	        if (!selectedPeriodMed) return;
	        markPeriodConfigured(selectedPeriodMed.key);
	        if (type === 'ongoing') {
	            updateSelectedPeriodSchedule({
	                indefinite: true,
	                durationDays: null,
	                endDate: null,
	                startDate: selectedPeriodSchedule.startDate || startDate,
	            });
	            return;
	        }
	        const nextDuration = selectedPeriodSchedule.durationDays || durationDays || 3;
	        const nextStartDate = selectedPeriodSchedule.startDate || startDate;
	        updateSelectedPeriodSchedule({
	            indefinite: false,
	            durationDays: nextDuration,
	            endDate: addDays(nextStartDate, Math.max(nextDuration - 1, 0)),
	            startDate: nextStartDate,
	        });
	    };

	    const setSelectedPeriodDuration = (days) => {
	        if (!selectedPeriodMed) return;
	        markPeriodConfigured(selectedPeriodMed.key);
	        const nextDuration = Number(days) > 0 ? Number(days) : 3;
	        const nextStartDate = selectedPeriodSchedule.startDate || startDate;
	        updateSelectedPeriodSchedule({
	            indefinite: false,
	            durationDays: nextDuration,
	            endDate: addDays(nextStartDate, Math.max(nextDuration - 1, 0)),
	            startDate: nextStartDate,
	        });
	    };

	    const updateSelectedReminderSchedule = (patch) => {
	        if (!selectedReminderMed) return;
	        markReminderConfigured(selectedReminderMed.key);
	        updateManualMedicationSchedule(selectedReminderMed.key, {
	            groupId: selectedReminderSchedule.groupId || `reminder-${selectedReminderMed.key}-${Date.now()}`,
	            ...patch,
	        });
	    };

	    const setSelectedReminderTimes = (nextTimes) => {
	        updateSelectedReminderSchedule({ times: normalizeTimes(nextTimes).slice(0, MAX_DAILY_REMINDER_TIMES) });
	    };

	    const clearSelectedReminderExistingTimeKey = (medKey = selectedReminderMed?.key) => {
	        if (!medKey) return;
	        setSelectedReminderExistingTimeKeys(prev => {
	            if (!prev[medKey]) return prev;
	            const next = { ...prev };
	            delete next[medKey];
	            return next;
	        });
	    };

	    const handleSelectedReminderExistingTimeOptionClick = (option) => {
	        if (!selectedReminderMed) return;
	        const medKey = selectedReminderMed.key;
	        if (selectedReminderExistingTimeKeys[medKey] === option.key) {
	            clearSelectedReminderExistingTimeKey(medKey);
	            return;
	        }
	        setSelectedReminderExistingTimeKeys(prev => ({ ...prev, [medKey]: option.key }));
	        setSelectedReminderTimes(option.times);
	    };

	    const applyReminderPresetToMedication = (medKey, preset) => {
	        if (!medKey || !preset) return;
	        markReminderConfigured(medKey);
	        setMedications(prev => prev.map((med) => {
	            if (med.key !== medKey) return med;
	            const schedule = effectiveManualSchedule(med);
	            return {
	                ...med,
	                schedule: {
	                    ...schedule,
	                    groupId: schedule.groupId || `reminder-${medKey}-${Date.now()}`,
	                    times: normalizeTimes(preset.times).slice(0, MAX_DAILY_REMINDER_TIMES),
	                    source: 'manual',
	                },
	            };
	        }));
	    };

	    const handleSelectedReminderPresetClick = (preset) => {
	        if (!selectedReminderMed) return;
	        clearSelectedReminderExistingTimeKey(selectedReminderMed.key);
	        const currentTimes = selectedReminderTouched ? (selectedReminderSchedule.times || []) : [];
	        if (sameTimes(currentTimes, preset.times)) {
	            setSelectedReminderTimes(preset.times);
	            return;
	        }
	        if (currentTimes.length > 0 && hasCustomTimes(currentTimes)) {
	            setPendingSelectedReminderTimePreset({
	                medKey: selectedReminderMed.key,
	                times: normalizeTimes(preset.times),
	            });
	            return;
	        }
	        setSelectedReminderTimes(preset.times);
	    };

	    const updateSelectedReminderTime = (index, value) => {
	        clearSelectedReminderExistingTimeKey();
	        const currentTimes = selectedReminderTouched ? (selectedReminderSchedule.times || []) : [];
	        if (value && currentTimes.some((time, timeIndex) => timeIndex !== index && time === value)) {
	            setToast({ open: true, severity: 'warning', message: localize("이미 같은 시간이 있어요") });
	            return;
	        }
	        const updated = currentTimes.map((time, timeIndex) => timeIndex === index ? value : time);
	        updateSelectedReminderSchedule({ times: updated });
	    };

	    const addSelectedReminderTime = () => {
	        clearSelectedReminderExistingTimeKey();
	        const currentTimes = selectedReminderTouched ? (selectedReminderSchedule.times || []) : [];
	        if (currentTimes.length >= MAX_DAILY_REMINDER_TIMES) {
	            setTimeLimitDialogOpen(true);
	            return;
	        }
	        if (currentTimes.some(time => !time)) {
	            setToast({ open: true, severity: 'warning', message: localize("비어있는 시간을 먼저 선택해주세요") });
	            return;
	        }
	        updateSelectedReminderSchedule({ times: [...currentTimes, ''] });
	        setPendingFocusTimeIndex(currentTimes.length);
	    };

	    const removeSelectedReminderTime = (index) => {
	        clearSelectedReminderExistingTimeKey();
	        const currentTimes = selectedReminderTouched ? (selectedReminderSchedule.times || []) : [];
	        if (currentTimes.length <= 1) return;
	        updateSelectedReminderSchedule({ times: normalizeTimes(currentTimes.filter((_, timeIndex) => timeIndex !== index)) });
	    };


    const buildReviewEditDraft = (med) => {
        const schedule = medSchedule(med);
        return {
            name: med.name,
            dosagePerTime: med.dosagePerTime ?? 1,
            memo: med.memo || '',
            itemSeq: med.itemSeq || null,
            medicineSource: med.medicineSource || null,
            category: med.category || 'OTHER',
            intakeForm: med.intakeForm || 'ORAL',
            schedule: {
                ...schedule,
                times: [...(schedule.times || ['08:00'])],
                specificDays: [...(schedule.specificDays || [])],
                monthlyDays: [...(schedule.monthlyDays || [])],
            },
        };
    };

    const updateReviewEditDraft = (patch) => {
        setReviewEditDraft(prev => prev ? { ...prev, ...patch } : prev);
    };

    const updateSelectedReviewSchedule = (patch) => {
        setReviewEditDraft(prev => {
            if (!prev || !selectedReviewMed) return prev;
            return {
                ...prev,
                schedule: {
                    ...prev.schedule,
                    groupId: prev.schedule.groupId || `review-${selectedReviewMed.key}-${Date.now()}`,
                    ...patch,
                },
            };
        });
    };

    const applyReviewTimePreset = (preset) => {
        updateSelectedReviewSchedule({ times: normalizeTimes(preset.times) });
    };

    const closeReviewEditSheet = () => {
        setReviewEditSection(null);
        setReviewEditDraft(null);
    };

    const saveReviewEditSheet = () => {
        if (!selectedReviewMed || !reviewEditDraft) {
            closeReviewEditSheet();
            return;
        }
        const error = reviewDraftError(reviewEditDraft);
        if (error) {
            setToast({ open: true, severity: 'warning', message: error });
            return;
        }
        setMedications(prev => prev.map((med) => {
            if (med.key !== selectedReviewMed.key) return med;
            const nameChanged = reviewEditDraft.name.trim() !== med.name.trim();
            return {
                ...med,
                name: reviewEditDraft.name,
                dosagePerTime: reviewEditDraft.dosagePerTime,
                memo: reviewEditDraft.memo,
                category: reviewEditDraft.category,
                intakeForm: reviewEditDraft.intakeForm,
                itemSeq: nameChanged ? null : reviewEditDraft.itemSeq,
                medicineSource: nameChanged ? null : reviewEditDraft.medicineSource,
                schedule: {
                    ...medSchedule(med),
                    ...reviewEditDraft.schedule,
                    source: 'manual',
                },
            };
        }));
        if (reviewEditDraft.name.trim() !== selectedReviewMed.name.trim()) {
            setBundleInteractionWarnings(null);
        }
        closeReviewEditSheet();
    };
    const reviewEditTitleMap = {
        name: translationKey('약 이름 수정'),
        category: translationKey('종류 수정'),
        intakeForm: translationKey('사용 방법 수정'),
        dosage: localize("{v0} 수정", { v0: localize(selectedReviewDraftIntakeMeta.doseLabel) }),
        memo: translationKey('메모 수정'),
        frequency: translationKey('주기 수정'),
        times: translationKey('시간 수정'),
        dosageMethod: localize("{v0} 수정", { v0: localize(selectedReviewDraftIntakeMeta.methodLabel) }),
        period: translationKey('복용 기간 수정'),
        alertMethod: translationKey('알림 방식 수정'),
        snooze: translationKey('집요한 알림 수정'),
    };
    const reviewEditTitle = reviewEditTitleMap[reviewEditSection] || translationKey('정보 수정');
    const renderReviewEditContent = () => {
        if (!selectedReviewMed || !reviewEditDraft) return null;
        const draft = reviewEditDraft;
        const draftSchedule = draft.schedule || selectedReviewSchedule;
        const draftStartDate = draftSchedule.startDate || startDate;
        const draftIntakeMeta = intakeMeta(draftSchedule.intakeForm || draft.intakeForm || 'ORAL');
        if (reviewEditSection === 'name') {
            return (
                <MedicationNameEditor
                    value={draft.name}
                    onChange={(name) => updateReviewEditDraft({ name })}
                />
            );
        }
        if (reviewEditSection === 'category') {
            return (
                <MedicationOptionEditor
                    options={MEDICATION_CATEGORY_OPTIONS}
                    value={draftSchedule.category || draft.category || 'OTHER'}
                    onChange={(value) => {
                        updateReviewEditDraft({ category: value });
                        updateSelectedReviewSchedule({ category: value });
                    }}
                />
            );
        }
        if (reviewEditSection === 'intakeForm') {
            return (
                <MedicationOptionEditor
                    options={MEDICATION_INTAKE_FORM_OPTIONS}
                    value={draftSchedule.intakeForm || draft.intakeForm || 'ORAL'}
                    onChange={(value) => {
                        updateReviewEditDraft({ intakeForm: value });
                        updateSelectedReviewSchedule({ intakeForm: value });
                    }}
                />
            );
        }
        if (reviewEditSection === 'dosage') {
            return (
                <MedicationDosagePerTimeEditor
                    label={localize(draftIntakeMeta.doseLabel)}
                    unit={draftIntakeMeta.unit}
                    value={draft.dosagePerTime ?? 1}
                    onChange={(value) => updateReviewEditDraft({ dosagePerTime: value })}
                />
            );
        }
        if (reviewEditSection === 'memo') {
            return (
                <MedicationMemoEditor
                    value={draft.memo || ''}
                    onChange={(memo) => updateReviewEditDraft({ memo })}
                />
            );
        }
        if (reviewEditSection === 'frequency') {
            return (
                <MedicationFrequencyEditor
                    value={draftSchedule}
                    onChange={updateSelectedReviewSchedule}
                />
            );
        }
        if (reviewEditSection === 'period') {
            return (
                <MedicationPeriodEditor
                    value={{ ...draftSchedule, startDate: draftStartDate }}
                    onChange={updateSelectedReviewSchedule}
                />
            );
        }
        if (reviewEditSection === 'times') {
            return (
                <MedicationTimesEditor
                    value={draftSchedule.times || ['08:00']}
                    onChange={(timesValue) => updateSelectedReviewSchedule({ times: timesValue })}
                    onWarning={(message) => {
                        if (message.includes(localize("{v0}번", { v0: MAX_DAILY_REMINDER_TIMES }))) setTimeLimitDialogOpen(true);
                        else setToast({ open: true, severity: 'warning', message });
                    }}
                    onPresetReplaceRequest={(preset) => setPendingReviewTimePreset(preset)}
                    existingMedications={existingMedications}
                />
            );
        }
        if (reviewEditSection === 'dosageMethod') {
            return (
                <MedicationOptionEditor
                    options={MEDICATION_DOSAGE_METHOD_OPTIONS}
                    value={draftSchedule.dosageMethod || 'ANYTIME'}
                    onChange={(value) => updateSelectedReviewSchedule({ dosageMethod: value })}
                />
            );
        }
        if (reviewEditSection === 'alertMethod') {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    <AlertMethodSection
                        form={{
                            alertType: draftSchedule.alertType || alertType,
                            alarmVolume: draftSchedule.alarmVolume ?? alarmVolume,
                        }}
                        setForm={(updater) => {
                            const current = {
                                alertType: draftSchedule.alertType || alertType,
                                alarmVolume: draftSchedule.alarmVolume ?? alarmVolume,
                            };
                            const next = typeof updater === 'function' ? updater(current) : updater;
                            updateSelectedReviewSchedule({
                                alertType: next.alertType || current.alertType,
                                alarmVolume: typeof next.alarmVolume === 'number' ? next.alarmVolume : current.alarmVolume,
                            });
                        }}
                    />
                </Box>
            );
        }
        if (reviewEditSection === 'snooze') {
            const draftReminderEnabled = draftSchedule.reminderEnabled ?? reminderEnabled;
            const draftReminderInterval = draftSchedule.reminderIntervalMinutes ?? reminderIntervalMinutes;
            const draftMaxReminder = draftSchedule.maxReminderMinutes ?? maxReminderMinutes;
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    <Box sx={{ p: 1.45, borderRadius: 3, bgcolor: '#fff', border: '1px solid #e5e7eb' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#1a1a1a', lineHeight: 1.35 }}>
                                    {draftReminderEnabled ? localize("켜짐") : localize("꺼짐")}
                                </Typography>
                                <Typography sx={{ mt: 0.35, fontSize: '0.88rem', color: '#777', lineHeight: 1.45, fontWeight: 750, wordBreak: 'keep-all' }}>
                                    {draftReminderEnabled ? localize("먹었다고 확인할 때까지 다시 알려드려요.") : localize("첫 알림 한 번만 보내요.")}
                                </Typography>
                            </Box>
                            <Switch
                                inputProps={{ 'aria-label': localize('집요한 알림 켜기 끄기') }}
                                checked={draftReminderEnabled}
                                onChange={(event) => {
                                    const checked = event.target.checked;
                                    updateSelectedReviewSchedule({
                                        reminderEnabled: checked,
                                        reminderIntervalMinutes: checked && Number(draftReminderInterval) <= 0 ? 30 : draftReminderInterval,
                                        maxReminderMinutes: checked && Number(draftMaxReminder) <= 0 ? 120 : draftMaxReminder,
                                    });
                                }}
                                sx={{
                                    width: 52,
                                    height: 34,
                                    p: 0,
                                    flexShrink: 0,
                                    '& .MuiSwitch-switchBase': {
                                        p: '4px',
                                        '&.Mui-checked': {
                                            transform: 'translateX(18px)',
                                            color: '#fff',
                                            '& + .MuiSwitch-track': {
                                                bgcolor: '#ffe566',
                                                borderColor: '#e6c800',
                                                opacity: 1,
                                            },
                                        },
                                    },
                                    '& .MuiSwitch-thumb': {
                                        width: 26,
                                        height: 26,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                                    },
                                    '& .MuiSwitch-track': {
                                        borderRadius: 999,
                                        bgcolor: '#e5e7eb',
                                        border: '1px solid #d1d5db',
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                    {draftReminderEnabled && (
                        <SnoozeSection
                            hideToggle
                            form={{
                                reminderEnabled: draftReminderEnabled,
                                reminderIntervalMinutes: draftReminderInterval,
                                maxReminderMinutes: draftMaxReminder,
                            }}
                            setForm={(updater) => {
                                const current = {
                                    reminderEnabled: draftReminderEnabled,
                                    reminderIntervalMinutes: draftReminderInterval,
                                    maxReminderMinutes: draftMaxReminder,
                                };
                                const next = typeof updater === 'function' ? updater(current) : updater;
                                updateSelectedReviewSchedule({
                                    reminderEnabled: typeof next.reminderEnabled === 'boolean' ? next.reminderEnabled : current.reminderEnabled,
                                    reminderIntervalMinutes: next.reminderIntervalMinutes ?? current.reminderIntervalMinutes,
                                    maxReminderMinutes: next.maxReminderMinutes ?? current.maxReminderMinutes,
                                });
                            }}
                        />
                    )}
                </Box>
            );
        }
        return null;
    };

    const separateMedication = (key) => {
        setMedications(prev => prev.map((med) => {
            if (med.key !== key) return med;
            return {
                ...med,
                schedule: {
                    ...medSchedule(med),
                    groupId: `separate-${key}-${Date.now()}`,
                    source: 'manual',
                },
            };
        }));
    };

    const updateGroupTime = (groupKey, index, value) => {
        const group = scheduleGroups.find(item => item.key === groupKey);
        if (!group) return;
        const currentTimes = group.schedule.times || ['08:00'];
        if (value && currentTimes.some((time, i) => i !== index && time === value)) {
            setToast({ open: true, severity: 'warning', message: localize("이미 같은 시간이 있어요") });
            return;
        }
        const nextTimes = currentTimes.map((time, i) => i === index ? value : time);
        updateGroupSchedule(groupKey, { times: nextTimes });
    };

    const addGroupTime = (groupKey) => {
        const group = scheduleGroups.find(item => item.key === groupKey);
        if (!group) return;
        const currentTimes = group.schedule.times || ['08:00'];
        if (currentTimes.length >= MAX_DAILY_REMINDER_TIMES) {
            setTimeLimitDialogOpen(true);
            return;
        }
        if (currentTimes.some(time => !time)) {
            setToast({ open: true, severity: 'warning', message: localize("비어있는 시간을 먼저 선택해주세요") });
            return;
        }
        const nextTimes = [...currentTimes, ''];
        updateGroupSchedule(groupKey, { times: nextTimes });
    };

    const removeGroupTime = (groupKey, index) => {
        const group = scheduleGroups.find(item => item.key === groupKey);
        if (!group) return;
        const currentTimes = group.schedule.times || ['08:00'];
        if (currentTimes.length <= 1) return;
        updateGroupSchedule(groupKey, { times: currentTimes.filter((_, i) => i !== index) });
    };

	    const buildManualAnalysisCacheKey = () => JSON.stringify({
            version: 'memo-v7-international-interactions',
            language,
	        meds: filledMeds.map(med => ({
	            key: String(med.key),
	            name: med.name,
	            itemSeq: med.itemSeq || null,
	            medicineSource: med.medicineSource || null,
	        })),
	        frequencyType: isAsNeededRegistration ? 'AS_NEEDED' : 'SCHEDULED',
    });

    const applyManualScheduleSuggestion = async () => {
        if (filledMeds.length === 0) return;
        const selectedIndefinite = isAsNeededRegistration || usagePeriodType === 'ongoing';
        const selectedDurationDays = isAsNeededRegistration
            ? null
            : (usagePeriodType === 'fixed' ? durationDays : null);
		const manualScheduleByKey = new Map(filledMeds.map((med) => [
	            med.key,
	            {
	                ...effectiveScheduleForManualAnalysis(med),
	                times: isAsNeededRegistration
	                    ? []
	                    : normalizeTimes(effectiveScheduleForManualAnalysis(med).times || []).slice(0, MAX_DAILY_REMINDER_TIMES),
	            },
		]));

        const cacheKey = buildManualAnalysisCacheKey();
        if (manualAnalysisCacheRef.current?.key === cacheKey && Array.isArray(bundleInteractionWarnings)) {
            setMedications(prev => prev.map(med => ({
                ...med,
                schedule: { ...med.schedule, ...manualScheduleByKey.get(med.key) },
            })));
            return;
        }
        const consentContextRevision = getScreenCacheContextRevision();
        const accepted = await requestAiConsent();
        if (consentContextRevision !== getScreenCacheContextRevision()) return false;
        if (!accepted) {
            setBundleInteractionWarnings([]);
            setBundleInteractionCheckStatus('failed');
            setInteractionCheckStatus('failed');
            setMedications(prev => prev.map(med => ({ ...med,
                schedule: manualScheduleByKey.get(med.key) || med.schedule })));
            setScheduleSource('default');
            return;
        }
        setPreparingReview(true);
        registrationJourney.event('submit', 'name_analysis');
        setBundleInteractionWarnings(null);
        const loadingStartedAt = Date.now();
        try {
            const uuid = getDeviceUuid();
            const analysis = await analyzeMedicationNames(uuid, filledMeds.map(med => ({
                key: String(med.key),
                name: med.name,
                itemSeq: language === 'ko' ? med.itemSeq || null : null,
                medicineSource: language === 'ko' ? med.medicineSource || null : null,
            })), {
                imageAnalysisToken: analysisResult?.imageAnalysisToken || null,
            });
            const suggestions = (analysis?.medications || []).map((item) => {
                const suggestion = item.suggestion || {};
                return {
                    key: Number(item.key),
                    name: item.name,
                    itemSeq: item.itemSeq || null,
                    medicineSource: item.medicineSource || (item.itemSeq ? 'DUR' : null),
                    schedule: {
                        frequencyType: isAsNeededRegistration ? 'AS_NEEDED' : 'DAILY',
                        durationDays: selectedDurationDays,
                        indefinite: selectedIndefinite,
                        times: isAsNeededRegistration ? [] : times.slice(0, MAX_DAILY_REMINDER_TIMES),
                        dosageMethod: suggestion?.dosageMethod || 'ANYTIME',
                        category: suggestion?.category || 'OTHER',
                        intakeForm: suggestion?.intakeForm || 'ORAL',
                        alertType: isAsNeededRegistration ? 'PUSH' : alertType,
                        alarmVolume: isAsNeededRegistration ? null : alarmVolume,
                        reminderEnabled: isAsNeededRegistration ? false : reminderEnabled,
                        reminderIntervalMinutes: isAsNeededRegistration ? 0 : reminderIntervalMinutes,
                        maxReminderMinutes: isAsNeededRegistration ? 0 : maxReminderMinutes,
                        source: suggestion?.source === 'default' ? 'default' : 'ai',
                    },
                    dosagePerTime: suggestion?.dosagePerTime || 1,
                    memo: sanitizeAutoMemo(suggestion?.memo),
                    warnings: item.warnings || [],
                };
            });
            setMedications(prev => prev.map(med => {
                const matched = suggestions.find(suggestion => suggestion.key === med.key);
                const currentSchedule = manualScheduleByKey.get(med.key) || effectiveManualSchedule(med);
                if (!matched) {
                    return {
                        ...med,
                        schedule: {
                            ...currentSchedule,
                            source: 'manual',
                        },
                    };
                }
	                const nextSchedule = {
	                    ...matched.schedule,
                        frequencyType: currentSchedule.frequencyType || matched.schedule.frequencyType,
                        intervalDays: currentSchedule.intervalDays,
                        specificDays: currentSchedule.specificDays,
                        hourlyInterval: currentSchedule.hourlyInterval,
                        cycleDaysOn: currentSchedule.cycleDaysOn,
                        cycleDaysOff: currentSchedule.cycleDaysOff,
                        monthlyDays: currentSchedule.monthlyDays,
	                    startDate: currentSchedule.startDate || matched.schedule.startDate || startDate,
	                    durationDays: currentSchedule.durationDays ?? matched.schedule.durationDays,
	                    endDate: currentSchedule.endDate ?? matched.schedule.endDate ?? null,
	                    indefinite: typeof currentSchedule.indefinite === 'boolean' ? currentSchedule.indefinite : matched.schedule.indefinite,
	                    times: normalizeTimes(currentSchedule.times || []).length > 0 ? currentSchedule.times : matched.schedule.times,
	                    dosageMethod: (med.photoOriginalName || currentSchedule.source === 'photo') && currentSchedule.dosageMethod ? currentSchedule.dosageMethod : matched.schedule.dosageMethod,
	                    category: currentSchedule.source === 'photo' && currentSchedule.category ? currentSchedule.category : matched.schedule.category,
	                    intakeForm: currentSchedule.source === 'photo' && currentSchedule.intakeForm && currentSchedule.intakeForm !== 'ORAL' ? currentSchedule.intakeForm : matched.schedule.intakeForm,
	                    alertType: currentSchedule.alertType || matched.schedule.alertType,
	                    alarmVolume: currentSchedule.alarmVolume ?? matched.schedule.alarmVolume,
	                    reminderEnabled: typeof currentSchedule.reminderEnabled === 'boolean' ? currentSchedule.reminderEnabled : matched.schedule.reminderEnabled,
	                    reminderIntervalMinutes: currentSchedule.reminderIntervalMinutes ?? matched.schedule.reminderIntervalMinutes,
	                    maxReminderMinutes: currentSchedule.maxReminderMinutes ?? matched.schedule.maxReminderMinutes,
	                    groupId: currentSchedule.groupId || matched.schedule.groupId,
	                    source: 'manual',
	                };
	                return {
		                    ...med,
	                    itemSeq: matched.itemSeq || med.itemSeq || null,
	                    medicineSource: matched.medicineSource || med.medicineSource || null,
		                    schedule: nextSchedule,
		                    dosagePerTime: language === 'ko' ? med.dosagePerTime ?? matched.dosagePerTime ?? 1 : matched.dosagePerTime ?? med.dosagePerTime ?? 1,
		                    memo: matched.memo || med.memo || '',
		                };
            }));
            setBundleInteractionWarnings(suggestions.flatMap(suggestion => (
                (suggestion.warnings || []).map(warning => ({
                    ...warning,
                    checkedName: suggestion.name,
                }))
            )));
            setBundleInteractionCheckStatus(analysis?.interactionCheckStatus === 'success' ? 'success' : 'failed');
            setInteractionCheckStatus(analysis?.interactionCheckStatus === 'success' ? 'success' : 'failed');
            const firstSchedule = manualScheduleByKey.get(filledMeds[0]?.key) || suggestions[0]?.schedule;
            if (firstSchedule) {
                setDurationDays(firstSchedule.durationDays || durationDays || 3);
                setTimes(firstSchedule.times || ['08:00']);
                setDosageMethod(firstSchedule.dosageMethod || 'ANYTIME');
            }
            setScheduleSource('bundle');
            manualAnalysisCacheRef.current = { key: cacheKey };
        } catch {
            setBundleInteractionWarnings(null);
            setMedications(prev => prev.map(med => ({
                ...med,
                schedule: manualScheduleByKey.get(med.key) || {
                    ...DEFAULT_MED_SCHEDULE,
                    durationDays: selectedDurationDays || DEFAULT_MED_SCHEDULE.durationDays,
                    indefinite: selectedIndefinite,
                    times: times.slice(0, MAX_DAILY_REMINDER_TIMES),
                    alertType,
                    alarmVolume,
                    reminderEnabled,
                    reminderIntervalMinutes,
                    maxReminderMinutes,
                },
            })));
            setScheduleSource('default');
        } finally {
            const elapsed = Date.now() - loadingStartedAt;
            if (elapsed < 1800) {
                await new Promise(resolve => setTimeout(resolve, 1800 - elapsed));
            }
            setPreparingReview(false);
        }
    };

    const requestNativeImage = (source, consentGiven = false) => {
        setPhotoAnalysisNotice(null);
        if (!nativePhotoSupported) return;
        if (source !== 'camera' && source !== 'library') return;
        if (!consentGiven) {
            photoConsentRef.current = false;
            setPhotoSheetOpen(true);
            return;
        }
        photoConsentRef.current = true;
        if (WebviewMessenger.isInWebView() && supportsCorrelatedImagePicker(runtimeInfo)) {
            setEntryMode('photo');
            WebviewMessenger.send({ type: source === 'camera' ? 'TAKE_PRESCRIPTION_PHOTO' : 'PICK_PRESCRIPTION_IMAGE',
                requestId: nativeImages.begin('PRESCRIPTION_IMAGE_RESULT', 'prescription') });
            return;
        }
        setEntryMode('photo');
        checkpointPermissionDraft();
        if (source === 'camera') cameraInputRef.current?.click();
        else libraryInputRef.current?.click();
    };

    const handleFallbackFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setPhotoAnalysisNotice(null);
        setAnalysisResult(null);
        setPhotoCandidatesConfirmed(false);
        setEntryMode('photo');
        setPhotoPreparing(true);
        try {
            const compressed = await compressImageUnderLimit(file, 2400, 0.94);
            if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
            setImagePreview(URL.createObjectURL(compressed));
            setPhotoPreparing(false);
            await analyzeImageBlob(compressed, false);
        } catch {
            setPhotoAnalysisNotice({
                type: 'error',
                message: localize("사진을 읽지 못했어요"),
            });
            setToast({ open: true, severity: 'error', message: localize("사진을 읽지 못했어요") });
        } finally {
            setPhotoPreparing(false);
            event.target.value = '';
        }
    };

    const buildPayload = (med) => {
        const schedule = medSchedule(med);
        const indefinite = schedule.indefinite;
        const frequencyType = schedule.frequencyType || 'DAILY';
        const asNeeded = frequencyType === 'AS_NEEDED';
        const medStartDate = schedule.startDate || startDate;
        const medEndDate = indefinite
            ? null
            : (schedule.endDate || addDays(medStartDate, Math.max((schedule.durationDays || durationDays) - 1, 0)));
        const scheduleReminderEnabled = schedule.reminderEnabled ?? reminderEnabled;
        const interval = Number(schedule.reminderIntervalMinutes ?? reminderIntervalMinutes) || 30;
        const maxReminder = Number(schedule.maxReminderMinutes ?? maxReminderMinutes) || 120;
        return ({
        name: med.name,
        memo: (med.memo || '').trim(),
        category: schedule.category || 'OTHER',
        intakeForm: schedule.intakeForm || 'ORAL',
        itemSeq: med.itemSeq || null,
        medicineSource: med.medicineSource || (med.itemSeq ? 'DUR' : null),
        frequencyType,
        specificDays: frequencyType === 'SPECIFIC_DAYS'
            ? (schedule.specificDays || []).slice().sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)).join(',')
            : null,
        hourlyInterval: frequencyType === 'HOURLY_INTERVAL' ? Number(schedule.hourlyInterval) || null : null,
        intervalDays: frequencyType === 'EVERY_N_DAYS' ? Number(schedule.intervalDays) || null : null,
        cycleDaysOn: frequencyType === 'CYCLE' ? Number(schedule.cycleDaysOn) || null : null,
        cycleDaysOff: frequencyType === 'CYCLE' ? Number(schedule.cycleDaysOff) || null : null,
        monthlyDays: frequencyType === 'MONTHLY' ? (schedule.monthlyDays || []).join(',') : null,
        dosageMethod: schedule.dosageMethod || 'ANYTIME',
        startDate: medStartDate,
        endDate: medEndDate,
        times: asNeeded ? [] : (schedule.times || ['08:00']),
        dosagePerTime: Number(med.dosagePerTime) || 1,
        alertType: schedule.alertType || alertType,
        alarmVolume: (schedule.alertType || alertType) === 'FULLSCREEN'
            ? (schedule.alarmVolume ?? alarmVolume)
            : null,
        initialIntervalMinutes: asNeeded ? 0 : (scheduleReminderEnabled ? interval : 0),
        escalationAfterMinutes: asNeeded ? 0 : (scheduleReminderEnabled ? maxReminder : 0),
        escalatedIntervalMinutes: asNeeded ? 0 : (scheduleReminderEnabled ? interval : 0),
        maxReminderMinutes: asNeeded ? 0 : (scheduleReminderEnabled ? maxReminder : 0),
    });
    };

    const handleSave = async () => {
        if (draftCheckpoint.conflict || needsDateConfirmation || recoveredReviewEdit) return;
        if (interactionCheckPending || showInteractionWarningScreen) return;
        if (!canSave || saveInFlightRef.current) return;
        const payload = filledMeds.map(buildPayload);
        if (bulkCreatePayloadRef.current && JSON.stringify(payload) !== JSON.stringify(bulkCreatePayloadRef.current)) {
            setToast({ open: true, severity: 'warning', showMedicationList: true, message: localize("이전 요청의 등록 여부를 확인하지 못했어요. 약 목록을 먼저 확인해주세요.") });
            return;
        }
        const isRetry = !!bulkCreateRequestIdRef.current;
        saveInFlightRef.current = true;
        trackProduct('registration_submit', { feature: 'medication' });
        registrationJourney.event('submit');
        setSaving(true);
        try {
            const uuid = getDeviceUuid();
            const limit = bulkCreateRequestIdRef.current ? null : await getMedicationLimit(uuid);
            if (limit && !limit.limitExempt && (limit.remaining ?? 0) < filledMeds.length) {
                setMedicationLimit(limit);
                registrationJourney.event('limit', 'slot_reward');
                setLimitDialogOpen(true);
                setToast({
                    open: true,
                    severity: 'warning',
                    message: localize("등록 가능한 약이 {v0}개 남았어요", { v0: limit.remaining ?? 0 }),
                });
                return;
            }

            const checkpoint = draftCheckpoint.flush();
            if (!checkpoint.ok || !checkpoint.draft) throw new Error(translationKey("작성 내용을 보관하지 못했어요."));
            if (!bulkCreateRequestIdRef.current) {
                // One draft always uses one server key, including competing tabs.
                bulkCreateRequestIdRef.current = checkpoint.draft.id;
                bulkCreatePayloadRef.current = payload;
            }
            draftCheckpoint.pause();
            const frozen = freezeRegistrationRequest(checkpoint.draft, bulkCreateRequestIdRef.current, bulkCreatePayloadRef.current);
            if (!frozen.ok) {
                draftCheckpoint.resume(checkpoint.draft);
                bulkCreateRequestIdRef.current = null; bulkCreatePayloadRef.current = null;
                throw new Error(translationKey("작성 내용을 보관하지 못했어요. 다시 시도해주세요."));
            }
            draftCheckpoint.expected.current = frozen.draft;
            const createdMedications = await createMedicationsBulk(uuid, bulkCreatePayloadRef.current, {
                requestId: bulkCreateRequestIdRef.current,
                expectedContext: frozen.draft.context,
            });
            if (registrationOwner()?.context !== frozen.draft.context || registrationOwner()?.generation !== draftCheckpoint.lease.current?.generation) return;
            resolveRegistrationRequest(frozen.draft, true);
            bulkCreateRequestIdRef.current = null;
            bulkCreatePayloadRef.current = null;
            const createdCount = createdMedications.length;
            const firstCreatedId = createdMedications[0]?.id || null;
            let imageUploadFailedCount = 0;
            for (let index = 0; index < filledMeds.length; index += 1) {
                const med = filledMeds[index];
                const created = createdMedications[index];
                if (med.imageFile && created?.id) {
                    await uploadMedicationImage(uuid, created.id, med.imageFile).catch((imgErr) => {
                        imageUploadFailedCount += 1;
                        if (imgErr?.response?.status === 429) {
                            setToast({ open: true, severity: 'info', message: localize("오늘 사진 업로드 횟수를 모두 사용했어요 (10회/일)") });
                        }
                    });
                }
            }
            syncWidget(uuid);
            clearMedicationCaches();
            const nextPath = isAsNeededRegistration
                ? (createdCount === 1 && firstCreatedId ? `/prn?welcome=${firstCreatedId}` : '/prn')
                : '/';
            navigate(nextPath, {
                state: {
                    refreshHome: Date.now(),
                    toast: imageUploadFailedCount > 0
                        ? localize("약은 등록됐지만 사진 {v0}개는 저장하지 못했어요", { v0: imageUploadFailedCount })
                        : isAsNeededRegistration
                        ? localize("필요할 때 쓰는 약 {v0}개를 등록했어요", { v0: createdCount })
                        : localize("약 {v0}개를 등록했어요", { v0: createdCount }),
                },
            });
        } catch (err) {
            const knownRejection = [400, 401, 403, 404, 413, 422, 429].includes(err?.response?.status)
                && err?.response?.data?.code !== 'REGISTRATION_REQUEST_CONFLICT'
                && !/이전 등록 요청과 내용이 달라/.test(err?.response?.data?.message || err?.response?.data?.error || '');
            if (!isRetry && knownRejection) {
                bulkCreateRequestIdRef.current = null;
                bulkCreatePayloadRef.current = null;
                if (resolveRegistrationRequest(draftCheckpoint.expected.current, false)) draftCheckpoint.resume(readRegistrationDraft());
            }
            trackProduct('registration_error', { feature: 'medication' });
            registrationJourney.event('error');
            const pending = readRegistrationDraft();
            if (pending?.status === 'pending') {
                navigate(pending.path, { replace: true, state: { registrationDraftId: pending.id } }); return;
            }
            if (!pending || pending.status === 'editing') { bulkCreateRequestIdRef.current = null; bulkCreatePayloadRef.current = null; }
            const msg = err?.response?.data?.message || err?.response?.data?.error || translationKey('작성 내용이나 등록 결과를 보관하지 못했어요. 다시 시도해주세요.');
            setToast({ open: true, severity: 'error', message: msg });
        } finally {
            saveInFlightRef.current = false;
            setSaving(false);
        }
    };

	    const handlePrimaryAction = async () => {
            if (draftCheckpoint.conflict || (isUsagePeriodStep && needsDateConfirmation)) return;
            draftCheckpoint.flush();
            if (medicationLimitCheckRef.current || primaryActionDisabled) return;
            if (isNameOnlyStep && hasNameDraft) {
                setNameDraftNeedsAdding(true);
                setNameSearchState(prev => ({ ...prev, open: true }));
                nameInputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                return;
            }
            if (isNameOnlyStep || isPhotoCandidateReview) {
                medicationLimitCheckRef.current = true;
                setCheckingMedicationLimit(true);
                try {
                    const limit = await getMedicationLimit(getDeviceUuid());
                    setMedicationLimit(limit);
                    if (!limit.limitExempt && (limit.remaining ?? 0) < filledMeds.length) {
                        registrationJourney.event('limit', 'slot_reward');
                        setLimitDialogOpen(true);
                        setToast({
                            open: true,
                            severity: 'warning',
                            message: localize("등록 가능한 약이 {v0}개 남았어요", { v0: limit.remaining ?? 0 }),
                        });
                        return;
                    }
                } catch {
                    setToast({ open: true, severity: 'error', message: localize("등록 가능 개수를 확인하지 못했어요. 다시 눌러주세요.") });
                    return;
                } finally {
                    medicationLimitCheckRef.current = false;
                    setCheckingMedicationLimit(false);
                }
            }
	        if (isNameOnlyStep) {
	            setMissingMedAddReturnStep(null);
	            if (isAsNeededRegistration) {
	                if (await applyManualScheduleSuggestion() === false) return;
                setEntryMode('review');
                setManualStep('schedule');
                setShowMedicineEditor(false);
                setShowScheduleEditor(false);
                setShowAlertEditor(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            setManualStep('period');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        if (isUsagePeriodStep) {
            if (showPerMedicationPeriod && !allPeriodMedsConfigured) {
                if (nextUnconfiguredPeriodMed) {
                    setSelectedPeriodMedKey(nextUnconfiguredPeriodMed.key);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                return;
            }
            setManualStep('reminder');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        if (isReminderStep) {
            if (showPerMedicationReminder && !allReminderMedsConfigured) {
                if (nextUnconfiguredReminderMed) {
                    setSelectedReminderMedKey(nextUnconfiguredReminderMed.key);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                return;
            }
            if (await applyManualScheduleSuggestion() === false) return;
            setEntryMode('review');
            setManualStep('schedule');
            setShowMedicineEditor(false);
            setShowScheduleEditor(false);
            setShowAlertEditor(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        if (isPhotoCandidateReview) {
            if (medications.some(med => !med.name.trim())) return;
            confirmPhotoCandidatesAndContinue();
            return;
        }
        handleSave();
    };

    const closeFallbackPath = typeof location.state?.from === 'string' ? location.state.from : '/';
    const closeMedicationRegistration = useCallback(() => {
        draftCheckpoint.flush();
        navigate(closeFallbackPath, { replace: true });
    }, [closeFallbackPath, navigate, draftCheckpoint]);

    const handleHeaderBack = useCallback(() => {
        if (saveInFlightRef.current || medicationLimitCheckRef.current || preparingReview) return;
        if (limitDialogOpen) {
            closeLimitDialog();
            return;
        }
        if (pendingTimePreset || pendingReviewTimePreset || pendingSelectedReminderTimePreset) {
            setPendingTimePreset(null);
            setPendingReviewTimePreset(null);
            setPendingSelectedReminderTimePreset(null);
            return;
        }
        if (photoPreviewOpen) {
            setPhotoPreviewOpen(false);
            return;
        }
        if (reviewEditSection) {
            closeReviewEditSheet();
            return;
        }
        if (photoSheetOpen) {
            setPhotoSheetOpen(false);
            return;
        }
        if (medImageSheetOpen) {
            setMedImageSheetOpen(false);
            return;
        }
        if (medListSheetOpen) {
            setMedListSheetOpen(false);
            return;
        }
        if (isNameOnlyStep) {
            if (missingMedAddReturnStep === 'candidate') {
                setMissingMedAddReturnStep(null);
                setEntryMode('review');
                setManualStep('schedule');
                setPhotoCandidatesConfirmed(false);
                setShowMedicineEditor(false);
                setShowScheduleEditor(false);
                setShowAlertEditor(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            if (missingMedAddReturnStep === 'period') {
                setMissingMedAddReturnStep(null);
                setEntryMode('manual');
                setManualStep('period');
                setPhotoCandidatesConfirmed(true);
                setShowMedicineEditor(false);
                setShowScheduleEditor(false);
                setShowAlertEditor(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            closeMedicationRegistration();
            return;
        }
        if (isReminderStep) {
            setManualStep('period');
            return;
        }
        if (isUsagePeriodStep) {
            if (analysisResult && photoCandidatesConfirmed) {
                setEntryMode('review');
                setManualStep('schedule');
                setPhotoCandidatesConfirmed(false);
                setShowMedicineEditor(false);
                setShowScheduleEditor(false);
                setShowAlertEditor(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            setManualStep('names');
            return;
        }
        if (isPhotoCandidateReview) {
            setEntryMode('photo');
            setManualStep('names');
            setShowMedicineEditor(false);
            setShowScheduleEditor(false);
            setShowAlertEditor(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        if (entryMode === 'review' && showReviewInteractionCard && !showInteractionWarningScreen) {
            setAcknowledgedInteractionSignature(null);
            window.scrollTo({ top: 0, behavior: 'auto' });
            return;
        }
        if (entryMode === 'review') {
            setEntryMode('manual');
            setManualStep('reminder');
            setShowMedicineEditor(false);
            setShowScheduleEditor(false);
            setShowAlertEditor(false);
            return;
        }
	        if (entryMode === 'photo') {
                imageAnalysisGenerationRef.current += 1;
                setAnalyzing(false);
                setPhotoPreparing(false);
	            setEntryMode('manual');
	            setManualStep('names');
	            return;
	        }
        closeMedicationRegistration();
    }, [
        preparingReview,
        limitDialogOpen,
        closeLimitDialog,
        pendingTimePreset,
        pendingReviewTimePreset,
        pendingSelectedReminderTimePreset,
        closeReviewEditSheet,
        closeMedicationRegistration,
        analysisResult,
        entryMode,
        isNameOnlyStep,
        isPhotoCandidateReview,
        isReminderStep,
        isUsagePeriodStep,
        medImageSheetOpen,
        medListSheetOpen,
        missingMedAddReturnStep,
        photoPreviewOpen,
        photoSheetOpen,
        photoCandidatesConfirmed,
        reviewEditSection,
        showReviewInteractionCard,
        showInteractionWarningScreen,
    ]);

    useBackHandler(true, handleHeaderBack, 0);

    if (preparingReview) {
        return <SmartAnalysisLoading medCount={filledMeds.length} />;
    }

    const renderPhotoPreviewShortcut = () => {
        if (!imagePreview) return null;
        return (
            <Box
                role="button"
                tabIndex={0}
                onClick={() => setPhotoPreviewOpen(true)}
                onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    setPhotoPreviewOpen(true);
                }}
                sx={{
                    position: 'sticky',
                    top: 72,
                    zIndex: 90,
                    mt: 1.35,
                    mb: 2,
                    mx: 0,
                    px: 1.5,
                    py: 1,
                    minHeight: 64,
                    boxSizing: 'border-box',
                    borderRadius: 3,
                    bgcolor: '#fff',
                    border: '1px solid #eee9d8',
                    boxShadow: '0 3px 12px rgba(40, 35, 15, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    transition: 'background-color 150ms ease, border-color 150ms ease',
                    '&:active': { bgcolor: '#fffbea', borderColor: '#e7d781' },
                    '&:focus-visible': { outline: '2px solid #9a7d00', outlineOffset: 2 },
                }}
            >
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        bgcolor: '#fff',
                        border: '2px solid #fff',
                        boxShadow: '0 1px 5px rgba(0, 0, 0, 0.12)',
                        flexShrink: 0,
                    }}
                >
                    <Box component="img" src={imagePreview} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <SingleLineText sx={{ fontSize: '1rem', fontWeight: 650, color: '#363329', lineHeight: 1.4, wordBreak: 'keep-all' }}>{localize("올린 사진 보기")}</SingleLineText>
                </Box>
                <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#fff5bf', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <OpenInFullIcon sx={{ fontSize: 18, color: '#897000' }} />
                </Box>
            </Box>
        );
    };

    if (draftCheckpoint.conflict) return <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#fff' }}>
        <Typography sx={{ fontWeight: 700, mb: 2 }}>{localize("다른 화면에서 작성 내용이나 계정이 바뀌었어요.")}</Typography>
        <Button variant="contained" sx={{ bgcolor: '#ffe566', color: '#5a4a00' }} onClick={() => navigate('/', { replace: true })}>{localize("홈에서 다시 열기")}</Button>
    </Box>;

    if (showInteractionWarningScreen) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#fff', px: 2.5, pb: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
                <IconButton aria-label={localize('뒤로가기')} onClick={handleHeaderBack}
                    sx={{ mt: 1, ml: -1.5, width: 48, height: 48, color: '#1a1a1a' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ mt: 3, mb: 4, textAlign: 'center' }}>
                    <Box sx={{
                        width: 56, height: 56, mx: 'auto', mb: 2, borderRadius: '50%',
                        bgcolor: hasCriticalReviewInteraction ? '#fff0ee' : '#fff5d6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <WarningAmberIcon sx={{ fontSize: 29, color: hasCriticalReviewInteraction ? '#c45449' : '#957515' }} />
                    </Box>
                    <Typography component="h1" sx={{ fontSize: '1.45rem', fontWeight: 800, lineHeight: 1.5, color: '#242424', wordBreak: 'keep-all' }}>
                        {hasCriticalReviewInteraction
                            ? <>{localize('함께 사용하면 안 되는')}<br />{localize('약 조합이 있어요')}</>
                            : <>{localize('함께 사용하기 전에')}<br />{localize('확인이 필요한 약이 있어요')}</>}
                    </Typography>
                </Box>
                <Typography sx={{ mb: 1.1, fontSize: '0.8rem', fontWeight: 650, color: '#777' }}>
                    {localize("확인할 약 조합 {v0}건", { v0: reviewInteractionWarnings.length })}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {reviewInteractionWarnings.map((warning, index) => {
                        const critical = warning.severity === localize('금기');
                        const reason = warning.message || warning.reason || warning.description || localize('함께 사용할 때 주의가 필요해요');
                        return (
                            <Box key={`${warning.checkedName}-${warning.medicationName}-${index}`} sx={{
                                p: 2, borderRadius: 3, border: '1px solid #eeedf0', bgcolor: '#fff',
                                boxShadow: '0 4px 16px rgba(32, 32, 40, 0.035)',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.8 }}>
                                    <Box component="span" sx={{
                                        px: 0.9, py: 0.4, borderRadius: 1.2,
                                        bgcolor: critical ? '#fff0ee' : '#fff5d6',
                                        color: critical ? '#b64138' : '#826200',
                                        fontSize: '0.73rem', fontWeight: 750, lineHeight: 1.3,
                                    }}>
                                        {critical ? localize('병용금기') : localize('확인 필요')}
                                    </Box>
                                    {warning.source === 'ai' && (
                                        <Typography component="span" sx={{ fontSize: '0.73rem', color: '#8b8b96' }}>{localize("AI 참고")}</Typography>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                    <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: '#29292f', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                                        {warning.checkedName}
                                    </Typography>
                                    <Typography aria-hidden="true" sx={{ fontSize: '0.9rem', color: '#b4b4bd', lineHeight: 1 }}>+</Typography>
                                    <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: '#29292f', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                                        {warning.medicationName}
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1.8, px: 1.3, py: 1.1, borderRadius: 1.8, bgcolor: critical ? '#fff6f5' : '#fff9eb' }}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.6,
                                        color: critical ? '#aa443c' : '#725c17', wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>
                                        {reason}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
                <Typography sx={{ mt: 2.2, px: 0.5, textAlign: 'center', fontSize: '0.83rem', color: '#777', lineHeight: 1.7, wordBreak: 'keep-all' }}>
                    {localize('사용하기 전에 의사나 약사와 확인해주세요.')}
                </Typography>
                <Button fullWidth variant="contained" onClick={() => {
                    setAcknowledgedInteractionSignature(interactionWarningSignature);
                    window.scrollTo({ top: 0, behavior: 'auto' });
                }} sx={{ mt: 3.5, py: 1.6, borderRadius: 3, bgcolor: '#ffe566', color: '#5a4a00',
                    boxShadow: 'none', fontSize: '1.05rem', fontWeight: 800,
                    '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' } }}>
                    {localize('확인했어요')}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: '#fff',
            pb: isNameOnlyStep ? 'var(--keyboard-height, 0px)' : entryMode === 'review' ? 3 : 14,
            ...(isNameOnlyStep && { display: 'flex', flexDirection: 'column' }),
        }}>
            <Box sx={{
                position: 'fixed',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                maxWidth: 480,
                zIndex: 100,
                pt: 1,
                px: 1.5,
                pb: 1,
                bgcolor: '#fff',
            }}>
                <IconButton
                    aria-label={!entryMode || isNameOnlyStep ? localize("닫기") : localize("뒤로가기")}
                    onClick={handleHeaderBack}
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{ color: '#1a1a1a', width: 48, height: 48, p: 0, ml: -0.5 }}
                >
                    {(!entryMode || isNameOnlyStep) ? <CloseIcon /> : <ArrowBackIcon />}
                </IconButton>
            </Box>
            <Box sx={{ pt: '72px' }} />

            <Box sx={{ px: 2, pt: 1.5 }}>
                {draftCheckpoint.saved && !draftCheckpoint.failed && <Typography sx={{ color: '#777', fontSize: '0.85rem', mb: 1.5, lineHeight: 1.5 }}>{localize("작성 중인 내용은 이 기기에 7일간 보관돼요")}</Typography>}
                {draftCheckpoint.failed && <Typography role="alert" sx={{ color: '#b42318', mb: 1.5, lineHeight: 1.5 }}>{localize("작성 내용을 임시 보관하지 못했어요. 지금 화면에서는 계속 입력할 수 있어요.")}</Typography>}
                {recoveredReviewEdit && <Box sx={{ bgcolor: '#fafafa', p: 2, mb: 2, borderRadius: 3 }}>
                    <Typography sx={{ color: '#666', mb: 1 }}>{localize("확인 화면에서 수정하던 내용이 남아 있어요.")}</Typography>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                        <Button variant="contained" sx={{ bgcolor: '#f1f2f4', color: '#444', boxShadow: 'none', minHeight: 48, borderRadius: 2 }} onClick={() => {
                            setSelectedReviewMedKey(recoveredReviewEdit.key); setReviewEditDraft(recoveredReviewEdit.draft);
                            setReviewEditSection(recoveredReviewEdit.section); setRecoveredReviewEdit(null);
                        }}>{localize("수정하던 내용 이어서 확인하기")}</Button>
                        <Button variant="contained" sx={{ bgcolor: '#f1f2f4', color: '#444', boxShadow: 'none', minHeight: 48, borderRadius: 2 }} onClick={() => setRecoveredReviewEdit(null)}>{localize("수정 전 내용 유지하기")}</Button>
                    </Box>
                </Box>}
                {needsDateConfirmation && isUsagePeriodStep && <Box role="status" sx={{ p: 2, mb: 2, borderRadius: 3,
                    bgcolor: colors.surface, border: `1px solid ${colors.disabled}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36,
                            flexShrink: 0, borderRadius: '50%', bgcolor: colors.brandLight, color: colors.brandText }}>
                            <AccessTimeIcon sx={{ fontSize: 21 }} />
                        </Box>
                        <Typography sx={{ color: colors.text, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.6,
                            pt: 0.25, wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>{localize("시작일이 지났어요. 복용 기간을 다시 확인해주세요.")}</Typography>
                    </Box>
                    <AppButton fullWidth onClick={() => setNeedsDateConfirmation(false)}>{localize("현재 복용 기간을 확인했어요")}</AppButton>
                </Box>}
                <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={hiddenPhotoInputStyle}
                    onChange={handleFallbackFile}
                />
                <input
                    ref={libraryInputRef}
                    type="file"
                    accept="image/*"
                    style={hiddenPhotoInputStyle}
                    onChange={handleFallbackFile}
                />
                <input
                    ref={medicationCameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={hiddenPhotoInputStyle}
                    onChange={handleMedicationImageSelect}
                />
                <input
                    ref={medicationLibraryInputRef}
                    type="file"
                    accept="image/*"
                    style={hiddenPhotoInputStyle}
                    onChange={handleMedicationImageSelect}
                />
	                {entryMode === 'photo' && nativePhotoSupported && (
                    <Section icon={<AutoAwesomeIcon sx={{ fontSize: 21 }} />} title={localize("사진만 올려주세요")}>
	                    <Box sx={{
	                        p: 1.1,
	                        borderRadius: 3,
	                        bgcolor: '#f9fafb',
	                        border: '1px solid #e5e7eb',
	                    }}>
	                        {photoBusy && (
	                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2, color: '#8a7000' }}>
	                                <CircularProgress size={18} sx={{ color: '#8a7000' }} />
	                                <Typography sx={{ fontSize: '0.93rem', fontWeight: 750 }}>
	                                    {photoPreparing ? localize("사진을 준비하고 있어요") : localize("사진에서 약 이름과 복용법을 찾는 중이에요")}
	                                </Typography>
	                            </Box>
	                        )}
	                        {imagePreview && (
	                            <Box sx={{
	                                width: '100%',
	                                borderRadius: 2.5,
	                                overflow: 'hidden',
	                                bgcolor: '#fff',
	                                mb: 1.2,
	                            }}>
	                                <Box component="img" src={imagePreview} alt={localize("분석할 처방전 또는 약봉투 사진")} sx={{ display: 'block', width: '100%', height: 'auto' }} />
	                            </Box>
	                        )}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                            <Button
                                onClick={() => requestNativeImage('camera')}
                                disabled={photoBusy || !nativePhotoSupported}
                                startIcon={<CameraAltIcon />}
                                sx={{
                                    py: 1.15,
                                    borderRadius: 3,
                                    bgcolor: '#fff',
                                    color: '#5a4a00',
                                    border: '1px solid #e5e7eb',
                                    fontWeight: 850,
                                }}
                            >{localize("사진 찍기")}</Button>
                            <Button
                                onClick={() => requestNativeImage('library')}
                                disabled={photoBusy || !nativePhotoSupported}
                                startIcon={<PhotoLibraryIcon />}
                                sx={{
                                    py: 1.15,
                                    borderRadius: 3,
                                    bgcolor: '#fff',
                                    color: '#5a4a00',
                                    border: '1px solid #e5e7eb',
                                    fontWeight: 850,
                                }}
                            >{localize("앨범 선택")}</Button>
                        </Box>

	                        {photoAnalysisNotice?.type === 'noMeds' && !photoBusy && (
	                            <Box sx={{ mt: 1.2, p: 1.25, borderRadius: 2.5, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
	                                <SingleLineText sx={{ fontSize: '0.98rem', fontWeight: 900, color: '#5a4a00', mb: 0.35 }}>{localize("약 이름을 찾지 못했어요")}</SingleLineText>
	                                <SingleLineText sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#777', lineHeight: 1.45, wordBreak: 'keep-all' }}>{localize("다시 찍거나 약 이름을 직접 입력해주세요.")}</SingleLineText>
                                    <Button
                                        fullWidth
                                        onClick={() => {
                                            setPhotoAnalysisNotice(null);
                                            setEntryMode('manual');
                                            setManualStep('names');
                                        }}
                                        sx={{ mt: 1.15, minHeight: 52, borderRadius: 2.8, bgcolor: '#ffe566', color: '#5a4a00', fontSize: '1rem', fontWeight: 900, boxShadow: 'none', '&:hover': { bgcolor: '#f4d84d', boxShadow: 'none' } }}
                                    >{localize("직접 입력할게요")}</Button>
	                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8, mt: 0.8 }}>
	                                    <Button
	                                        onClick={() => {
	                                            setPhotoAnalysisNotice(null);
	                                            requestNativeImage('camera');
	                                        }}
	                                        sx={{ minHeight: 44, borderRadius: 2.5, bgcolor: '#fff', color: '#5a4a00', border: '1px solid #e5e7eb', fontWeight: 850, boxShadow: 'none' }}
	                                    >{localize("다시 찍기")}</Button>
	                                    <Button
	                                        onClick={() => {
	                                            setPhotoAnalysisNotice(null);
	                                            requestNativeImage('library');
	                                        }}
	                                        sx={{ minHeight: 44, borderRadius: 2.5, bgcolor: '#fff', color: '#5a4a00', border: '1px solid #e5e7eb', fontWeight: 850, boxShadow: 'none' }}
	                                    >{localize("앨범 선택")}</Button>
	                                </Box>
	                            </Box>
	                        )}
		                        {photoAnalysisNotice?.type === 'error' && !photoBusy && (
		                            <Box sx={{ mt: 1.2, p: 1.25, borderRadius: 2.5, bgcolor: '#fff', border: '1px solid #e5e7eb' }}>
	                                <SingleLineText sx={{ fontSize: '0.98rem', fontWeight: 850, color: '#1a1a1a', mb: 0.35 }}>{localize("사진을 분석하지 못했어요")}</SingleLineText>
	                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 650, color: '#777', lineHeight: 1.45, wordBreak: 'keep-all' }}>
	                                    {localize(photoAnalysisNotice.message)}
	                                </Typography>
                                    <Button
                                        fullWidth
                                        onClick={() => {
                                            setPhotoAnalysisNotice(null);
                                            setEntryMode('manual');
                                            setManualStep('names');
                                        }}
                                        sx={{ mt: 1.15, minHeight: 52, borderRadius: 2.8, bgcolor: '#ffe566', color: '#5a4a00', fontSize: '1rem', fontWeight: 900, boxShadow: 'none', '&:hover': { bgcolor: '#f4d84d', boxShadow: 'none' } }}
                                    >{localize("직접 입력할게요")}</Button>
	                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8, mt: 0.8 }}>
	                                    <Button
	                                        onClick={() => {
	                                            setPhotoAnalysisNotice(null);
	                                            requestNativeImage('camera');
	                                        }}
	                                        sx={{ minHeight: 44, borderRadius: 2.5, bgcolor: '#fff', color: '#5a4a00', border: '1px solid #e5e7eb', fontWeight: 850, boxShadow: 'none' }}
	                                    >{localize("다시 찍기")}</Button>
	                                    <Button
	                                        onClick={() => {
	                                            setPhotoAnalysisNotice(null);
	                                            requestNativeImage('library');
	                                        }}
	                                        sx={{ minHeight: 44, borderRadius: 2.5, bgcolor: '#fff', color: '#5a4a00', border: '1px solid #e5e7eb', fontWeight: 850 }}
	                                    >{localize("앨범 다시 선택")}</Button>
	                                </Box>
		                            </Box>
		                        )}
		                        {photoAnalysisNotice?.type === 'quota' && !photoBusy && (
		                            <Box sx={{ mt: 1.2, p: 1.25, borderRadius: 2.5, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
		                                <SingleLineText sx={{ fontSize: '0.98rem', fontWeight: 850, color: '#1a1a1a', mb: 0.35 }}>{localize("오늘 사진 등록은 다 썼어요")}</SingleLineText>
		                                <SingleLineText sx={{ fontSize: '0.9rem', fontWeight: 650, color: '#777', lineHeight: 1.45, wordBreak: 'keep-all' }}>{localize("약 이름은 직접 입력해서 등록할 수 있어요.")}</SingleLineText>
	                                    <Button
	                                        fullWidth
	                                        onClick={() => {
	                                            setPhotoAnalysisNotice(null);
	                                            setEntryMode('manual');
	                                            setManualStep('names');
	                                        }}
	                                        sx={{ mt: 1.15, minHeight: 52, borderRadius: 2.8, bgcolor: '#ffe566', color: '#5a4a00', fontSize: '1rem', fontWeight: 900, boxShadow: 'none', '&:hover': { bgcolor: '#f4d84d', boxShadow: 'none' } }}
	                                    >{localize("직접 입력할게요")}</Button>
		                            </Box>
		                        )}
		                    </Box>
		                </Section>
		                )}

                {entryMode === 'photo' && nativePhotoSupported && !photoBusy && !analysisResult && !photoAnalysisNotice && (
                    <Button
                        fullWidth
                        onClick={() => setEntryMode('manual')}
                        sx={{
                            mt: 1.15,
                            mb: 2,
                            minHeight: 52,
                            borderRadius: 2.8,
                            bgcolor: '#fffbeb',
                            color: '#5a4a00',
                            border: '1px solid #f4e7b1',
                            fontSize: '0.98rem',
                            fontWeight: 900,
                            boxShadow: '0 2px 8px rgba(90, 74, 0, 0.05)',
                            '&:hover': { bgcolor: '#fffbeb', boxShadow: '0 2px 8px rgba(90, 74, 0, 0.05)' },
                            '&:active': { bgcolor: '#fff3c4' },
                        }}
                    >{localize("직접 입력할게요")}</Button>
                )}

                {showEditableForm && (
                    <>
                {showScheduleFields && (
                    <Box sx={{
                        mb: 2,
                    }}>
		                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.4, px: 0.2 }}>
	                            <Box>
		                                <Typography sx={{ fontSize: '1.24rem', fontWeight: 900, color: '#1a1a1a' }}>
	                                    {isPhotoCandidateReview ? localize("사진에서 찾은 약이에요") : localize("약 {v0}개를 등록할게요", { v0: filledMeds.length })}
		                                </Typography>
		                                        {isPhotoCandidateReview && (
		                                            <Typography sx={{ mt: 0.45, fontSize: '0.92rem', fontWeight: 650, color: '#777', lineHeight: 1.45, wordBreak: 'keep-all' }}>
			                                                {hasAnyPhotoDbCandidates ? localize("맞는 약이 있으면 후보를 선택해주세요.") : localize("읽은 이름이 맞는지 확인해주세요.")}
		                                            </Typography>
		                                        )}
				                            </Box>
				                        </Box>
                                        {!isPhotoCandidateReview && imagePreview && (
                                            <SingleLineText sx={{ mb: 1.5, fontSize: '0.92rem', color: '#777', lineHeight: 1.5, wordBreak: 'keep-all' }}>{localize("등록할 내용이 사진과 맞는지 확인해주세요.")}</SingleLineText>
                                        )}
                                        {renderPhotoPreviewShortcut()}
				                        {photoAnalysisNotice?.type === 'limit' && (
		                            <Box sx={{ mb: 1.25, p: 1.15, borderRadius: 2.5, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
		                                <Typography sx={{ fontSize: '0.92rem', fontWeight: 850, color: '#5a4a00', lineHeight: 1.45, wordBreak: 'keep-all' }}>
	                                    {localize(photoAnalysisNotice.message)}
	                                </Typography>
	                            </Box>
	                        )}

			                        {(isPhotoCandidateReview ? medications.length > 0 : filledMeds.length > 0) && (
		                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

	                                {(isPhotoCandidateReview ? medications : filledMeds).map((med, index) => {
                                    const schedule = medSchedule(med);
                                    const medCategoryLabel = MEDICINE_CATEGORY_LABELS[schedule.category || med.category] || medicationBadgeLabel(med);
                                    const medIntakeMeta = intakeMeta(schedule.intakeForm || med.intakeForm || 'ORAL');
                                    const medIntakeLabel = medIntakeMeta.label;
                                    const medDosageLabel = DOSAGE_METHODS.find(method => method.value === schedule.dosageMethod)?.label || translationKey('상관없음');
                                    const scheduleReminderEnabled = schedule.reminderEnabled ?? reminderEnabled;
                                    const scheduleReminderInterval = schedule.reminderIntervalMinutes ?? reminderIntervalMinutes;
                                    const scheduleMaxReminder = schedule.maxReminderMinutes ?? maxReminderMinutes;
	                                    const scheduleSnoozeText = scheduleReminderEnabled
                                        ? localize("{v0}분마다, 최대 {v1}", { v0: scheduleReminderInterval, v1: scheduleMaxReminder >= 60 && scheduleMaxReminder % 60 === 0 ? localize("{v0}시간", { v0: scheduleMaxReminder / 60 }) : localize("{v0}분", { v0: scheduleMaxReminder }) })
	                                        : localize('집요한 알림 꺼짐');
		                                    const compactTimes = (schedule.times || ['08:00']).filter(Boolean);
	                                    const compactTimeText = schedule.frequencyType === 'AS_NEEDED'
	                                        ? localize('정해진 시간 없음')
	                                        : formatTimesKorean(compactTimes);
		                                    const compactPeriodText = schedule.frequencyType === 'AS_NEEDED'
	                                        ? localize('필요할 때만')
	                                        : schedule.indefinite
	                                            ? localize('매일')
	                                            : schedule.endDate
	                                                ? localize("{v0}까지", { v0: formatDateKorean(schedule.endDate) })
	                                                : localize("{v0}일 동안", { v0: schedule.durationDays || durationDays || 3 });
	                                    const detailExpanded = expandedReviewDetails[med.key] === true;
	                                    const openEdit = (section) => {
	                                        setSelectedReviewMedKey(med.key);
	                                        setReviewEditDraft(buildReviewEditDraft(med));
                                        setReviewEditSection(section);
                                        registrationJourney.event('edit', 'registration_review');
                                    };
	                                    const reminderRows = [
	                                        { label: translationKey('약 이름'), value: med.name, section: 'name' },
	                                        { label: translationKey('주기'), value: formatFrequencyKorean(schedule), section: 'frequency' },
                                        {
                                            label: translationKey('시간'),
                                            value: schedule.frequencyType === 'AS_NEEDED' ? localize('정해진 시간 없음') : localize("하루 {v0}번", { v0: (schedule.times || []).filter(Boolean).length }),
                                            section: 'times',
                                            times: schedule.frequencyType === 'AS_NEEDED' ? [] : (schedule.times || ['08:00']).filter(Boolean),
                                        },
                                        { label: translationKey('알림 방식'), value: formatAlertMethod(schedule.alertType || alertType, schedule.alarmVolume ?? alarmVolume), section: 'alertMethod' },
                                        { label: translationKey('집요한 알림'), value: scheduleSnoozeText, section: 'snooze' },
                                    ];
		                                    const infoRows = [
		                                        { label: translationKey('종류'), value: localize(medCategoryLabel), section: 'category' },
	                                        { label: translationKey('사용 방법'), value: localize(medIntakeLabel), section: 'intakeForm' },
	                                        { label: medIntakeMeta.methodLabel, value: localize(medDosageLabel), section: 'dosageMethod' },
	                                        { label: medIntakeMeta.doseLabel, value: `${med.dosagePerTime === '' ? 1 : (med.dosagePerTime || 1)} ${localize(medIntakeMeta.unit)}`, section: 'dosage' },
		                                        { label: translationKey('복용 기간'), value: formatPeriodKorean(schedule, startDate), section: 'period' },
		                                    ];
		                                    const toggleDetails = () => {
		                                        const nextExpanded = !expandedReviewDetails[med.key];
		                                        setExpandedReviewDetails(prev => ({ ...prev, [med.key]: nextExpanded }));
		                                        setTimeout(() => {
		                                            const card = reviewCardRefs.current[med.key];
		                                            if (!card) return;
		                                            const rect = card.getBoundingClientRect();
		                                            const top = window.scrollY + rect.top - 84;
		                                            if (rect.top < 84 || nextExpanded) {
		                                                window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
		                                            }
		                                        }, nextExpanded ? 120 : 40);
		                                    };
                                    const renderEditRow = ({ label, value, section, times: rowTimes = [] }) => (
                                        <Box
                                            key={localize(label)}
                                            role="button"
                                            tabIndex={0}
                                            data-tappable="true"
                                            onClick={() => openEdit(section)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                py: 1.25,
                                                minHeight: 62,
                                                borderTop: label === reminderRows[0]?.label || label === infoRows[0]?.label ? 'none' : '1px solid #f1f5f9',
                                                cursor: 'pointer',
                                                borderRadius: 2,
                                                '&:active': { bgcolor: '#f8fafc' },
                                            }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
	                                                <SingleLineText sx={{ fontSize: '0.78rem', color: '#999', fontWeight: 600, mb: 0.25 }}>
	                                                    {localize(label)}
                                                </SingleLineText>
	                                                <Typography sx={{ fontSize: '0.98rem', color: '#1a1a1a', fontWeight: 650, lineHeight: 1.35, wordBreak: 'keep-all' }}>
                                                    {value}
                                                </Typography>
                                                {rowTimes.length > 0 && (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.55, mt: 0.75 }}>
                                                        {rowTimes.map(time => (
                                                            <Box
                                                                key={time}
                                                                component="span"
                                                                sx={{
                                                                    px: 0.85,
                                                                    py: 0.36,
                                                                    borderRadius: '999px',
                                                                    bgcolor: '#f8fafc',
                                                                    border: '1px solid #e5e7eb',
                                                                    color: '#475569',
                                                                    fontSize: '0.82rem',
	                                                                    fontWeight: 600,
                                                                    lineHeight: 1.2,
                                                                }}
                                                            >
	                                                                {formatTimesKorean([time])}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                            <IconButton
                                                aria-label={localize("{v0} 수정", { v0: localize(label) })}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    openEdit(section);
                                                }}
                                                sx={reviewEditIconButtonSx}
	                                            >
	                                                <EditOutlinedIcon sx={{ fontSize: 20 }} />
	                                            </IconButton>
	                                        </Box>
                                    );
	                                    if (isPhotoCandidateReview) {
	                                        const photoCandidateOptions = buildPhotoCandidateOptions(med);
	                                        const hasDbPhotoCandidates = photoCandidateOptions.some(candidate => candidate.source !== 'PHOTO_ORIGINAL');
	                                        if (!hasDbPhotoCandidates) {
	                                            return (
	                                                <Box
	                                                    key={med.key}
	                                                    sx={{
	                                                        mb: 1.35,
	                                                        p: 1.35,
	                                                        borderRadius: 3.2,
	                                                        bgcolor: '#fff',
	                                                        border: '1px solid #eeeeee',
	                                                        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.035)',
	                                                    }}
	                                                >
	                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, mb: 1 }}>
	                                                        <CheckCircleOutlineIcon sx={{ fontSize: 25, color: '#8a7000', flexShrink: 0 }} />
	                                                        <Box sx={{ minWidth: 0, flex: 1 }}>
	                                                            <SingleLineText sx={{ fontSize: '0.84rem', fontWeight: 700, color: '#888', lineHeight: 1.3 }}>{localize("사진에서 읽은 이름")}</SingleLineText>
	                                                            <Typography sx={{ mt: 0.2, fontSize: '1.04rem', fontWeight: 760, color: '#1a1a1a', lineHeight: 1.38, wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>
	                                                                {med.photoOriginalName || med.name}
	                                                            </Typography>
	                                                        </Box>
	                                                    </Box>
	                                                    <Box sx={{ pt: 1.05, borderTop: '1px solid #f1f5f9' }}>
	                                                        <SingleLineText sx={{ fontSize: '0.84rem', color: '#777', fontWeight: 700, mb: 0.65 }}>{localize("이름이 다르면 고쳐주세요")}</SingleLineText>
	                                                        <TextField
	                                                            fullWidth
	                                                            value={med.name}
	                                                            onChange={(event) => updateMedicationName(med.key, event.target.value)}
	                                                            placeholder={localize("등록할 약 이름")}
	                                                            variant="outlined"
	                                                            size="small"
	                                                            sx={{
	                                                                '& .MuiOutlinedInput-root': {
	                                                                    minHeight: 52,
	                                                                    borderRadius: 2.6,
	                                                                    bgcolor: '#fff',
	                                                                    fontSize: '1rem',
	                                                                    fontWeight: 700,
	                                                                },
	                                                            }}
	                                                        />
	                                                    </Box>
	                                                </Box>
	                                            );
	                                        }
	                                        return (
	                                            <Box
                                                key={med.key}
                                                sx={{
                                                    mb: 1.35,
                                                    borderRadius: 3.2,
                                                    bgcolor: '#fff',
                                                    border: '1px solid #eeeeee',
                                                    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.035)',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Box>
                                                    {photoCandidateOptions.map((candidate, candidateIndex) => {
                                                            const selected = candidate.source === 'PHOTO_ORIGINAL'
                                                                ? !med.itemSeq && med.name === candidate.itemName
                                                                : (candidate.itemSeq && candidate.itemSeq === med.itemSeq)
                                                                    || (!candidate.itemSeq && candidate.itemName === med.name);
                                                            const candidateDisplay = parseMedicineDisplayName(candidate);
                                                            const isOriginal = candidate.source === 'PHOTO_ORIGINAL';
                                                            return (
                                                                <Box
                                                                    key={`${candidate.itemSeq || candidate.itemName}-${candidate.source || ''}`}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    data-tappable="true"
                                                                    onClick={() => {
                                                                        if (candidate.source === 'PHOTO_ORIGINAL') {
                                                                            usePhotoOriginalNameForReview(med.key);
                                                                            return;
                                                                        }
                                                                        selectPhotoCandidateForReview(med.key, candidate);
                                                                    }}
                                                                    sx={{
                                                                        minHeight: isOriginal ? 74 : 64,
                                                                        px: 1.35,
                                                                        py: isOriginal ? 1.15 : 1,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        cursor: 'pointer',
                                                                        bgcolor: selected ? '#fffbeb' : '#fff',
                                                                        borderTop: candidateIndex === 0 ? 'none' : '1px solid #f1f5f9',
                                                                        '&:active': { bgcolor: '#fff7cc' },
                                                                    }}
                                                                >
                                                                    <Box sx={{
                                                                        width: 34,
                                                                        height: 34,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        flexShrink: 0,
                                                                    }}>
                                                                        {selected ? (
                                                                            <CheckCircleOutlineIcon sx={{ fontSize: 28, color: '#8a7000' }} />
                                                                        ) : (
                                                                            <RadioButtonUncheckedIcon sx={{ fontSize: 28, color: '#c7c7c7' }} />
                                                                        )}
                                                                    </Box>
                                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                        <Typography sx={{ fontSize: isOriginal ? '1.06rem' : '0.99rem', fontWeight: 720, lineHeight: 1.32, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            {candidateDisplay.title}
                                                                        </Typography>
                                                                        <Typography sx={{ mt: 0.2, fontSize: '0.8rem', fontWeight: 560, color: isOriginal ? '#8a8a8a' : '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            {candidateDisplay.subtitle}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        })}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        px: 1.35,
                                                        py: 1.15,
                                                        bgcolor: '#fbfbfb',
                                                        borderTop: '1px solid #f1f5f9',
                                                    }}
                                                >
                                                    <SingleLineText sx={{ fontSize: '0.88rem', color: '#555', fontWeight: 760, lineHeight: 1.35, wordBreak: 'keep-all' }}>{localize("후보에 없나요?")}</SingleLineText>
                                                    <TextField
                                                        fullWidth
                                                        value={med.name}
                                                        onChange={(event) => updateMedicationName(med.key, event.target.value)}
                                                        placeholder={localize("이름을 직접 입력해주세요")}
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{
                                                            mt: 0.65,
                                                            '& .MuiOutlinedInput-root': {
                                                                minHeight: 50,
                                                                borderRadius: 2.5,
                                                                bgcolor: '#fff',
                                                                fontSize: '0.98rem',
                                                                fontWeight: 650,
                                                            },
                                                        }}
                                                    />
                                                    {med.name !== med.photoOriginalName && (
                                                        <Button
                                                            type="button"
                                                            onClick={() => usePhotoOriginalNameForReview(med.key)}
                                                            sx={{
                                                                mt: 0.65,
                                                                minHeight: 34,
                                                                px: 0,
                                                                color: '#777',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 700,
                                                                justifyContent: 'flex-start',
                                                                '&:hover': { bgcolor: 'transparent' },
                                                            }}
                                                        >{localize("사진에서 읽은 이름으로 되돌리기")}</Button>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    }
	                                    return (
	                                        <Box
	                                            key={med.key}
	                                            ref={(node) => {
	                                                if (node) reviewCardRefs.current[med.key] = node;
	                                                else delete reviewCardRefs.current[med.key];
	                                            }}
	                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.85,
                                                p: 1,
                                                mb: 1.15,
                                                borderRadius: 3,
                                                bgcolor: '#fff',
                                                border: '1.5px solid #e5e7eb',
                                                boxShadow: '0 2px 10px rgba(15, 23, 42, 0.05)',
                                            }}
	                                        >
	                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.3 }}>
		                                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#8a7000' }}>
	                                                    {localize("{v0} 약", { v0: localize(KOREAN_ORDER_LABELS[index]) || localize("{v0}번째", { v0: index + 1 }) })}
                                                </Typography>
	                                                <Typography sx={{ fontSize: '0.76rem', fontWeight: 600, color: '#c0a000' }}>
	                                                    {index + 1}/{filledMeds.length}
	                                                </Typography>
	                                            </Box>
	                                            <Box sx={{
	                                                bgcolor: '#fff',
	                                                borderRadius: 2.5,
	                                                p: 1.25,
	                                                border: '1px solid #eef2f7',
	                                                display: 'flex',
	                                                alignItems: 'flex-start',
	                                                gap: 1.2,
	                                            }}>
	                                                <Box
	                                                    role="button"
                                                    tabIndex={0}
                                                    data-tappable="true"
                                                    onClick={() => chooseMedImage(med.key)}
	                                                    sx={{
	                                                        width: 48,
	                                                        height: 48,
	                                                        borderRadius: 2.5,
	                                                        bgcolor: '#f8fafc',
                                                        overflow: 'hidden',
                                                        border: '1px solid #e5e7eb',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {med.imageLoading ? (
                                                        <CircularProgress size={22} sx={{ color: '#8a7000' }} />
                                                    ) : med.imagePreview ? (
                                                        <Box component="img" src={med.imagePreview} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <>
                                                            <CameraAltIcon sx={{ fontSize: 22, color: '#94a3b8' }} />
	                                                            <SingleLineText sx={{ mt: 0.1, fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600, lineHeight: 1 }}>
	                                                                {localize('사진')}
                                                            </SingleLineText>
                                                        </>
                                                    )}
	                                                </Box>
			                                                <Box sx={{ flex: 1, minWidth: 0 }}>
				                                                    <Typography sx={{
                                                                        fontSize: '1.08rem',
                                                                        fontWeight: 680,
                                                                        color: '#1a1a1a',
                                                                        lineHeight: 1.35,
                                                                        wordBreak: 'keep-all',
                                                                        overflowWrap: 'anywhere',
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: 2,
                                                                        WebkitBoxOrient: 'vertical',
                                                                        overflow: 'hidden',
                                                                    }}>
			                                                        {med.name}
			                                                    </Typography>
                                                            {isPhotoCandidateReview && med.photoOriginalName && (
                                                                <Box sx={{
                                                                    mt: 1.15,
                                                                    borderRadius: 2.8,
                                                                    bgcolor: '#f8fafc',
                                                                    border: '1px solid #eef2f7',
                                                                    overflow: 'hidden',
                                                                }}>
                                                                    <Box sx={{ px: 1.25, pt: 1.15, pb: 0.75 }}>
                                                                        <SingleLineText sx={{ fontSize: '0.84rem', color: '#777', fontWeight: 700, lineHeight: 1.35 }}>{localize("이 중에 맞는 약이 있나요?")}</SingleLineText>
                                                                    </Box>
                                                                    {(med.photoCandidates || []).length > 0 ? (
                                                                    <Box sx={{ bgcolor: '#fff' }}>
                                                                        {(med.photoCandidates || []).slice(0, 4).map((candidate, candidateIndex) => {
                                                                            const selected = (candidate.itemSeq && candidate.itemSeq === med.itemSeq)
                                                                                || (!candidate.itemSeq && candidate.itemName === med.name);
                                                                            const candidateDisplay = parseMedicineDisplayName(candidate);
                                                                            return (
                                                                                <Box
                                                                                    key={`${candidate.itemSeq || candidate.itemName}-${candidate.source || ''}`}
                                                                                    role="button"
                                                                                    tabIndex={0}
                                                                                    data-tappable="true"
                                                                                    onClick={() => selectPhotoCandidateForReview(med.key, candidate)}
                                                                                    sx={{
                                                                                        minHeight: 64,
                                                                                        px: 1.2,
                                                                                        py: 1.05,
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 1.15,
                                                                                        cursor: 'pointer',
                                                                                        bgcolor: selected ? '#fffbeb' : '#fff',
                                                                                        borderTop: candidateIndex === 0 ? 'none' : '1px solid #eef2f7',
                                                                                        '&:active': { bgcolor: '#fff7cc' },
                                                                                    }}
                                                                                >
                                                                                    <Box sx={{
                                                                                        width: 25,
                                                                                        height: 25,
                                                                                        borderRadius: '50%',
                                                                                        border: selected ? '2px solid #8a7000' : '2px solid #c7c7c7',
                                                                                        bgcolor: selected ? '#ffe566' : '#fff',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        flexShrink: 0,
                                                                                    }}>
                                                                                        {selected && <CheckCircleOutlineIcon sx={{ fontSize: 19, color: '#5a4a00' }} />}
                                                                                    </Box>
                                                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.32, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                            {candidateDisplay.title}
                                                                                        </Typography>
                                                                                        <Typography sx={{ mt: 0.18, fontSize: '0.8rem', fontWeight: 560, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                            {candidateDisplay.subtitle}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                    ) : (
                                                                        <Box sx={{ px: 1.25, pb: 1.05 }}>
                                                                            <Typography sx={{ fontSize: '0.88rem', color: '#777', fontWeight: 620, lineHeight: 1.45, wordBreak: 'keep-all' }}>{localize("비슷한 후보를 못 찾았어요. 약 이름이 맞는지 확인해주세요.")}</Typography>
                                                                        </Box>
                                                                    )}
                                                                    <Box
                                                                        role="button"
                                                                        tabIndex={0}
                                                                        data-tappable="true"
                                                                        onClick={() => usePhotoOriginalNameForReview(med.key)}
                                                                        sx={{
                                                                            minHeight: 52,
                                                                            px: 1.25,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between',
                                                                            gap: 1,
                                                                            bgcolor: '#f8fafc',
                                                                            borderTop: '1px solid #eef2f7',
                                                                            cursor: 'pointer',
                                                                            '&:active': { bgcolor: '#f1f5f9' },
                                                                        }}
                                                                    >
                                                                        <SingleLineText sx={{ fontSize: '0.86rem', color: '#666', fontWeight: 700, lineHeight: 1.35, wordBreak: 'keep-all' }}>{localize("후보에 없으면 읽은 이름으로 등록")}</SingleLineText>
                                                                        <ChevronRightIcon sx={{ fontSize: 19, color: '#9ca3af', flexShrink: 0 }} />
                                                                    </Box>
                                                                </Box>
                                                            )}
				                                                    {!isPhotoCandidateReview && (
				                                                    <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.9 }}>
				                                                        {[
			                                                            [translationKey('알림'), compactTimeText],
			                                                            [translationKey('기간'), compactPeriodText],
			                                                        ].map(([label, value]) => (
		                                                            <Box
		                                                                key={localize(label)}
		                                                                sx={{
		                                                                    display: 'grid',
	                                                                    gridTemplateColumns: language === 'ko' ? '38px 1fr' : '52px minmax(0, 1fr)',
		                                                                    gap: 0.75,
			                                                                    alignItems: 'baseline',
			                                                                }}
			                                                            >
				                                                                <SingleLineText sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#9ca3af', lineHeight: 1.35, ...(language !== 'ko' ? { whiteSpace: 'nowrap' } : {}) }}>
	                                                                    {localize(language !== 'ko' && label === translationKey('알림') ? '시간' : label)}
			                                                                </SingleLineText>
			                                                                {label === translationKey('알림') && compactTimes.length >= 3 && schedule.frequencyType !== 'AS_NEEDED' ? (
			                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.45, pt: 0.05 }}>
			                                                                        {compactTimes.map(time => (
			                                                                            <Box
			                                                                                key={time}
			                                                                                component="span"
			                                                                                sx={{
			                                                                                    px: 0.65,
			                                                                                    py: 0.28,
			                                                                                    borderRadius: '999px',
			                                                                                    bgcolor: '#fffbeb',
			                                                                                    border: '1px solid #fde68a',
			                                                                                    color: '#8a7000',
			                                                                                    fontSize: '0.8rem',
			                                                                                    fontWeight: 600,
			                                                                                    lineHeight: 1.2,
			                                                                                    whiteSpace: 'nowrap',
			                                                                                }}
			                                                                            >
	                                                                                {formatTimesKorean([time])}
			                                                                            </Box>
			                                                                        ))}
			                                                                    </Box>
			                                                                ) : (
			                                                                    <Typography sx={{ fontSize: '0.94rem', fontWeight: 620, color: label === translationKey('알림') ? '#8a7000' : '#374151', lineHeight: 1.35, wordBreak: 'keep-all' }}>
			                                                                        {value}
			                                                                    </Typography>
			                                                                )}
			                                                            </Box>
			                                                        ))}
			                                                    </Box>
				                                                    )}
		                                                </Box>
		                                            </Box>

                                                    {!isPhotoCandidateReview && (
		                                            <Button
		                                                onClick={toggleDetails}
		                                                endIcon={<ChevronRightIcon sx={{ transform: detailExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }} />}
		                                                sx={{
		                                                    alignSelf: 'stretch',
		                                                    mt: -0.05,
		                                                    minHeight: 42,
		                                                    px: 1.4,
		                                                    borderRadius: 2.5,
		                                                    bgcolor: detailExpanded ? '#fffbe6' : '#f9fafb',
		                                                    border: detailExpanded ? '1px solid #fde68a' : '1px solid #eef2f7',
		                                                    color: '#5a4a00',
		                                                    fontSize: '0.92rem',
		                                                    fontWeight: 680,
		                                                    boxShadow: 'none',
		                                                    '&:hover': { bgcolor: detailExpanded ? '#fffbe6' : '#f9fafb', boxShadow: 'none' },
		                                                }}
		                                            >
	                                                {detailExpanded ? localize('접기') : localize('자세히 보고 수정하기')}
		                                            </Button>
                                                    )}

	                                            {!isPhotoCandidateReview && detailExpanded && (
	                                                <>
		                                                    <Box sx={{
		                                                        borderRadius: 2.5,
		                                                        p: 1.45,
		                                                        bgcolor: '#fff',
		                                                        border: '1px solid #eef2f7',
		                                                    }}>
			                                                        <SingleLineText sx={{ fontSize: '1.06rem', fontWeight: 700, color: '#1a1a1a', mb: 0.6, lineHeight: 1.25 }}>
	                                                            {localize('알림')}
		                                                        </SingleLineText>
		                                                        {reminderRows.map(renderEditRow)}
		                                                    </Box>

		                                                    <Box sx={{
		                                                        bgcolor: '#fff',
		                                                        borderRadius: 2.5,
		                                                        p: 1.45,
		                                                        border: '1px solid #eef2f7',
		                                                    }}>
			                                                        <Typography sx={{ fontSize: '1.06rem', fontWeight: 700, color: '#1a1a1a', mb: 0.6, lineHeight: 1.25 }}>
		                                                            {localize(medIntakeMeta.title)}
			                                                        </Typography>
		                                                        {infoRows.map(renderEditRow)}
		                                                    </Box>

		                                                    <Box sx={{
		                                                        bgcolor: '#fff',
		                                                        borderRadius: 2.5,
		                                                        p: 1.45,
		                                                        border: '1px solid #eef2f7',
		                                                    }}>
		                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.6 }}>
			                                                            <SingleLineText sx={{ fontSize: '1.06rem', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.25 }}>{localize("메모")}</SingleLineText>
		                                                            <IconButton
		                                                                aria-label={localize("메모 수정")}
		                                                                onClick={() => openEdit('memo')}
		                                                                sx={reviewEditIconButtonSx}
		                                                            >
		                                                                <EditOutlinedIcon sx={{ fontSize: 20 }} />
		                                                            </IconButton>
		                                                        </Box>
		                                                        <Typography sx={{
		                                                            fontSize: '0.95rem',
		                                                            color: med.memo ? '#444' : '#9ca3af',
		                                                            fontWeight: med.memo ? 500 : 600,
		                                                            lineHeight: 1.6,
		                                                            whiteSpace: 'pre-wrap',
		                                                            wordBreak: 'keep-all',
		                                                        }}>
		                                                            {med.memo || localize("필요하면 메모를 남길 수 있어요")}
		                                                        </Typography>
		                            </Box>
		                        </>
		                    )}

	                                        </Box>
	                                    );
	                                })}
	                                {isPhotoCandidateReview && showPhotoMissingMedAction && (
	                                    <Button
	                                        type="button"
	                                        onClick={() => {
	                                            setMissingMedAddReturnStep('candidate');
	                                            setPhotoCandidatesConfirmed(true);
	                                            setPhotoPreviewOpen(false);
	                                            setEntryMode('manual');
	                                            setManualStep('names');
	                                            window.scrollTo({ top: 0, behavior: 'smooth' });
	                                        }}
	                                        endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
	                                        sx={{
	                                            mt: 0.35,
	                                            mb: 0.25,
	                                            minHeight: 46,
	                                            px: 1.25,
	                                            borderRadius: 2.5,
	                                            bgcolor: '#fffbeb',
	                                            border: '1px solid #f4e7b1',
	                                            color: '#8a7000',
	                                            fontSize: '0.92rem',
	                                            fontWeight: 850,
	                                            justifyContent: 'space-between',
	                                            boxShadow: 'none',
	                                            '&:hover': { bgcolor: '#fff7cc', boxShadow: 'none' },
	                                        }}
	                                    >{localize("빠진 약이 있나요?")}</Button>
	                                )}
		                            </Box>
		                        )}

	                        {showScheduleEditor && entryMode !== 'review' && (
                            <Box sx={{ mb: 1.2 }}>
                                <SingleLineText sx={{ fontSize: '0.84rem', fontWeight: 850, color: '#999', mb: 0.7 }}>{localize("언제부터 먹나요")}</SingleLineText>
                                <TextField
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value || todayKey())}
                                    fullWidth
                                    size="small"
                                    inputProps={{ max: '9999-12-31' }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#fff' } }}
                                />
                            </Box>
                        )}

                        {showScheduleGroupCards && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.2 }}>
                            {scheduleGroups.map((group) => {
                                const groupDosageLabel = DOSAGE_METHODS.find(method => method.value === group.schedule.dosageMethod)?.label || translationKey('상관없음');
                                const groupStableKey = group.meds.map(med => med.key).join('-');
                                const groupCustomOpen = groupCustomDurationOpen[groupStableKey] === true;
                                const groupCustomDraft = groupCustomDurationDraft[groupStableKey] ?? String(group.schedule.durationDays || 3);
                                return (
                                    <Box
                                        key={groupStableKey}
                                        sx={{
                                            p: 1.4,
                                            borderRadius: 2.5,
                                            bgcolor: '#f9fafb',
                                            border: '1px solid #eeeeee',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                            <Typography sx={{ fontSize: '0.92rem', fontWeight: 900, color: '#1a1a1a' }}>
                                                {group.meds.length > 1 ? localize("{v0}개가 같은 일정이에요", { v0: group.meds.length }) : localize("이렇게 등록돼요")}
                                            </Typography>
                                            {scheduleGroups.length > 1 && (
                                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 850, color: '#8a7000' }}>{localize("일정 ")}{scheduleGroups.findIndex(item => item.key === group.key) + 1}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{
                                            p: 1,
                                            mb: 1,
                                            borderRadius: 2.3,
                                            bgcolor: '#fff',
                                            border: '1px solid #eeeeee',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.45,
                                        }}>
                                            {[
                                                [localize('기간'), localize("{v0}부터 {v1}", { v0: formatDateKorean(startDate), v1: group.schedule.indefinite ? localize("계속") : localize("{v0}일 동안", { v0: group.schedule.durationDays || 3 }) })],
                                                [localize('시간'), localize("하루 {v0}번 · {v1}", { v0: (group.schedule.times || []).length, v1: formatTimesKorean(group.schedule.times || ['08:00']) })],
                                                [localize('기준'), localize(groupDosageLabel)],
                                            ].map(([label, value]) => (
                                                <Box
                                                    key={localize(label)}
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '42px 1fr',
                                                        gap: 0.7,
                                                        alignItems: 'start',
                                                    }}
                                                >
                                                    <SingleLineText sx={{ fontSize: '0.8rem', color: '#999', fontWeight: 850, lineHeight: 1.45 }}>
                                                        {localize(label)}
                                                    </SingleLineText>
                                                    <Typography sx={{ fontSize: '0.9rem', color: '#1a1a1a', fontWeight: 850, lineHeight: 1.45, wordBreak: 'keep-all' }}>
                                                        {value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                            {group.meds.map((med) => {
                                                const schedule = medSchedule(med);
                                                const categoryLabel = MEDICINE_CATEGORY_LABELS[schedule.category || med.category] || medicationBadgeLabel(med);
                                                const intakeLabel = INTAKE_FORM_LABELS[schedule.intakeForm || med.intakeForm] || translationKey('기타');
                                                return (
                                                    <Box
                                                        key={med.key}
                                                        sx={{
                                                            p: 1.05,
                                                            borderRadius: 2.3,
                                                            bgcolor: '#fff',
                                                            border: '1px solid #eeeeee',
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
                                                            <MedicationIcon sx={{ mt: 0.12, fontSize: 20, color: '#8a7000', flexShrink: 0 }} />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, minWidth: 0 }}>
                                                                    <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#1a1a1a', lineHeight: 1.35 }} noWrap>
                                                                        {med.name}
                                                                    </Typography>
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            px: 0.55,
                                                                            py: 0.18,
                                                                            borderRadius: 1.2,
                                                                            bgcolor: '#fffbeb',
                                                                            color: '#8a7000',
                                                                            fontSize: '0.68rem',
                                                                            fontWeight: 850,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        {localize(medicationBadgeLabel(med))}
                                                                    </Box>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.55, mt: 0.7 }}>
                                                                    {[categoryLabel, intakeLabel, localize("1회 {v0}개", { v0: med.dosagePerTime || 1 })].map(label => (
                                                                        <Box
                                                                            key={localize(label)}
                                                                            component="span"
                                                                            sx={{
                                                                                px: 0.75,
                                                                                py: 0.32,
                                                                                borderRadius: '999px',
                                                                                bgcolor: '#f7f7f7',
                                                                                color: '#555',
                                                                                fontSize: '0.78rem',
                                                                                fontWeight: 800,
                                                                            }}
                                                                        >
                                                                            {localize(label)}
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                            {group.meds.length > 1 && (
                                                                <Button
                                                                    size="small"
                                                                    onClick={() => separateMedication(med.key)}
                                                                    sx={{
                                                                        minWidth: 0,
                                                                        px: 0.8,
                                                                        py: 0.35,
                                                                        borderRadius: 2,
                                                                        color: '#777',
                                                                        fontSize: '0.74rem',
                                                                        fontWeight: 850,
                                                                        flexShrink: 0,
                                                                    }}
                                                                >{localize("따로 설정")}</Button>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                        {showScheduleEditor && (
                                            <Box sx={{ mt: 1.2, pt: 1.2, borderTop: '1px solid #eeeeee' }}>
                                                <SingleLineText sx={{ fontSize: '0.82rem', fontWeight: 850, color: '#999', mb: 0.7 }}>{localize("복용 기간")}</SingleLineText>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: group.schedule.indefinite ? 1.1 : 0.85 }}>
                                                    <Button
                                                        onClick={() => updateGroupSchedule(group.key, { indefinite: true, durationDays: null })}
	                                                        sx={{
	                                                            minHeight: 50,
	                                                            py: 1.15,
	                                                            borderRadius: 2.5,
                                                            bgcolor: group.schedule.indefinite ? '#ffe566' : '#fff',
                                                            color: group.schedule.indefinite ? '#5a4a00' : '#666',
                                                            border: '1px solid #e5e7eb',
                                                            fontSize: '0.9rem',
                                                            fontWeight: 850,
                                                        }}
                                                    >{localize("계속 복용해요")}</Button>
                                                    <Button
                                                        onClick={() => updateGroupSchedule(group.key, { indefinite: false, durationDays: group.schedule.durationDays || 3 })}
	                                                        sx={{
	                                                            minHeight: 50,
	                                                            py: 1.15,
	                                                            borderRadius: 2.5,
                                                            bgcolor: !group.schedule.indefinite ? '#ffe566' : '#fff',
                                                            color: !group.schedule.indefinite ? '#5a4a00' : '#666',
                                                            border: '1px solid #e5e7eb',
                                                            fontSize: '0.9rem',
                                                            fontWeight: 850,
                                                        }}
                                                    >{localize("기간을 정해서 복용해요")}</Button>
                                                </Box>
                                                {!group.schedule.indefinite && (
                                                    <Box sx={{ mb: 1.15 }}>
                                                        <Box sx={{ display: 'flex', gap: 0.65, overflowX: 'auto', pb: 0.25, WebkitOverflowScrolling: 'touch' }}>
                                                            {DAY_PRESETS.map(day => {
                                                                const selected = !groupCustomOpen && (group.schedule.durationDays || 3) === day;
                                                                return (
                                                                <Chip
                                                                    key={day}
                                                                    label={localize("{v0}일 동안", { v0: day })}
                                                                    onClick={() => {
                                                                        updateGroupSchedule(group.key, { durationDays: day, indefinite: false });
                                                                        setGroupCustomDurationOpen(prev => ({ ...prev, [groupStableKey]: false }));
                                                                        setGroupCustomDurationDraft(prev => ({ ...prev, [groupStableKey]: String(day) }));
                                                                    }}
                                                                    sx={{
                                                                        height: 44,
                                                                        minWidth: 78,
                                                                        backgroundColor: selected ? '#ffe566' : '#fff',
                                                                        color: selected ? '#5a4a00' : '#666',
                                                                        border: selected ? '1px solid #e6c800' : '1px solid #e5e7eb',
                                                                        fontSize: '0.92rem',
                                                                        fontWeight: 850,
                                                                        borderRadius: '999px',
                                                                        flex: '0 0 auto',
                                                                        '& .MuiChip-label': { px: 1.05 },
                                                                        '&:hover, &:focus, &.Mui-focusVisible': {
                                                                            backgroundColor: selected ? '#ffe566' : '#fff',
                                                                        },
                                                                        '&:active': {
                                                                            backgroundColor: selected ? '#f4d84d' : '#fff',
                                                                        },
                                                                    }}
                                                                />
                                                                );
                                                            })}
                                                            <Chip
                                                                label={localize("직접 입력")}
                                                                onClick={() => {
                                                                    setGroupCustomDurationOpen(prev => ({ ...prev, [groupStableKey]: true }));
                                                                    setGroupCustomDurationDraft(prev => ({ ...prev, [groupStableKey]: String(group.schedule.durationDays || 3) }));
                                                                }}
                                                                sx={{
                                                                    height: 44,
                                                                    minWidth: 78,
                                                                    backgroundColor: groupCustomOpen ? '#ffe566' : '#fff',
                                                                    color: groupCustomOpen ? '#5a4a00' : '#666',
                                                                    border: groupCustomOpen ? '1px solid #e6c800' : '1px solid #e5e7eb',
                                                                    fontSize: '0.92rem',
                                                                    fontWeight: 850,
                                                                    borderRadius: '999px',
                                                                    flex: '0 0 auto',
                                                                    '& .MuiChip-label': { px: 1.05 },
                                                                    '&:hover, &:focus, &.Mui-focusVisible': {
                                                                        backgroundColor: groupCustomOpen ? '#ffe566' : '#fff',
                                                                    },
                                                                    '&:active': {
                                                                        backgroundColor: groupCustomOpen ? '#f4d84d' : '#fff',
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                        {groupCustomOpen && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mt: 0.85 }}>
                                                            <TextField
                                                                type="text"
                                                                autoFocus
                                                                value={groupCustomDraft}
                                                                onBeforeInput={preventNonNumericInput}
                                                                onChange={(e) => {
                                                                    const nextDraft = normalizeDurationInput(e.target.value);
                                                                    setGroupCustomDurationDraft(prev => ({ ...prev, [groupStableKey]: nextDraft }));
                                                                    if (nextDraft) {
                                                                        updateGroupSchedule(group.key, { durationDays: commitDurationDraft(nextDraft, group.schedule.durationDays || 3), indefinite: false });
                                                                    }
                                                                }}
                                                                onBlur={() => {
                                                                    const next = commitDurationDraft(groupCustomDraft, group.schedule.durationDays || 3);
                                                                    updateGroupSchedule(group.key, { durationDays: next, indefinite: false });
                                                                    setGroupCustomDurationDraft(prev => ({ ...prev, [groupStableKey]: String(next) }));
                                                                }}
                                                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                                size="small"
                                                                sx={{
                                                                    width: 112,
                                                                    '& .MuiOutlinedInput-root': {
                                                                        minHeight: 48,
                                                                        borderRadius: 2.2,
                                                                        bgcolor: '#fff',
                                                                        fontWeight: 850,
                                                                    },
                                                                }}
                                                            />
                                                            <SingleLineText sx={{ fontSize: '0.86rem', color: '#666', fontWeight: 800 }}>{localize("일 동안 복용해요")}</SingleLineText>
                                                        </Box>
                                                        )}
                                                    </Box>
                                                )}
                                                <SingleLineText sx={{ fontSize: '0.82rem', fontWeight: 850, color: '#999', mb: 0.7 }}>{localize("하루에 몇 번")}</SingleLineText>
                                                <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mb: 1 }}>
                                                    {COUNT_PRESETS.map(preset => (
                                                        <Chip
                                                            key={preset.count}
                                                            label={localize("{v0}번", { v0: preset.count })}
                                                            onClick={() => updateGroupSchedule(group.key, { times: preset.times })}
                                                            sx={{
                                                                height: 44,
                                                                minWidth: 58,
                                                                bgcolor: (group.schedule.times || []).length === preset.count ? '#ffe566' : '#fff',
                                                                color: (group.schedule.times || []).length === preset.count ? '#5a4a00' : '#666',
                                                                border: '1px solid #e5e7eb',
                                                                fontWeight: 800,
                                                                fontSize: '0.97rem',
                                                                borderRadius: '999px',
                                                                '& .MuiChip-label': { px: 1.3 },
                                                            }}
                                                        />
                                                    ))}
                                                </Box>

                                                {(group.schedule.times || ['08:00']).map((time, index) => (
                                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.75 }}>
                                                        <TextField
                                                            type="time"
                                                            value={time}
                                                            onChange={(e) => updateGroupTime(group.key, index, e.target.value)}
                                                            onClick={(e) => {
                                                                const input = e.currentTarget.querySelector('input[type="time"]');
                                                                if (!input) return;
                                                                if (e.target === input) {
                                                                    try {
                                                                        if (typeof input.showPicker === 'function') {
                                                                            input.showPicker();
                                                                            return;
                                                                        }
                                                                    } catch (_) {}
                                                                    try { input.focus(); } catch (_) {}
                                                                    return;
                                                                }
                                                                openNativePicker(input);
                                                            }}
                                                            fullWidth
                                                            size="small"
                                                            inputProps={{ step: 300 }}
                                                            sx={{ '& .MuiOutlinedInput-root': { minHeight: 48, borderRadius: 2.5, bgcolor: '#fff' } }}
                                                        />
                                                        <IconButton
                                                            disabled={(group.schedule.times || []).length <= 1}
                                                            onClick={() => removeGroupTime(group.key, index)}
                                                            sx={{ width: 44, height: 44, color: '#aaa', '&:disabled': { color: '#ddd' } }}
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                                <Button
                                                    onClick={() => addGroupTime(group.key)}
                                                    startIcon={<AddIcon />}
                                                    sx={{ color: '#5a4a00', fontWeight: 850, borderRadius: 2.5, mb: 0.7 }}
                                                >{localize("시간 추가")}</Button>

                                                <SingleLineText sx={{ fontSize: '0.82rem', fontWeight: 850, color: '#999', mb: 0.7 }}>{localize("언제 먹나요")}</SingleLineText>
                                                <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                                                    {DOSAGE_METHODS.map(method => (
                                                        <Chip
                                                            key={method.value}
                                                            label={localize(method.label)}
                                                            onClick={() => updateGroupSchedule(group.key, { dosageMethod: method.value })}
                                                            sx={{
                                                                height: 44,
                                                                minWidth: 64,
                                                                bgcolor: group.schedule.dosageMethod === method.value ? '#ffe566' : '#fff',
                                                                color: group.schedule.dosageMethod === method.value ? '#5a4a00' : '#666',
                                                                border: '1px solid #e5e7eb',
                                                                fontWeight: 800,
                                                                fontSize: '0.97rem',
                                                                borderRadius: '999px',
                                                                '& .MuiChip-label': { px: 1.3 },
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                        )}

                    </Box>
                )}

                {(isNameOnlyStep || (showMedicineEditor && entryMode !== 'review')) && (
                <Section icon={<MedicationIcon sx={{ fontSize: 21 }} />} title={isNameOnlyStep ? null : localize("약을 고쳐주세요")}>
                    {isNameOnlyStep && (
                        <>
                            <Box sx={{ mb: 2.4 }}>
                                <SingleLineText sx={{ fontSize: '1.5rem', fontWeight: 850, color: '#1a1a1a', lineHeight: 1.3 }}>
                                    {localize('어떤 약을 드시나요?')}
                                </SingleLineText>
		                                <Typography sx={{ mt: 0.65, fontSize: '0.9rem', color: '#777', fontWeight: 650, lineHeight: 1.45, wordBreak: 'keep-all' }}>
	                                    {language === 'ko'
                                        ? <>{localize("복용할 약을 ")}<Box component="span" sx={{ fontWeight: 750, color: '#5a4a00' }}>{localize("모두")}</Box>{localize(" 입력해주세요.")}</>
                                        : localize('복용할 약을 모두 입력해주세요.')}
	                                </Typography>
	                            </Box>
	                            {missingMedAddReturnStep && analysisResult && (
	                                <Button
	                                    type="button"
	                                    onClick={() => {
	                                        setMissingMedAddReturnStep(null);
	                                        setEntryMode('review');
	                                        setManualStep('schedule');
	                                        setPhotoCandidatesConfirmed(false);
	                                        setShowMedicineEditor(false);
	                                        setShowScheduleEditor(false);
	                                        setShowAlertEditor(false);
	                                        window.scrollTo({ top: 0, behavior: 'smooth' });
	                                    }}
	                                    endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
	                                    sx={{
	                                        mt: -1.25,
	                                        mb: 1.4,
	                                        minHeight: 44,
	                                        px: 1.15,
	                                        borderRadius: 2.5,
	                                        bgcolor: '#fffbeb',
	                                        border: '1px solid #f4e7b1',
	                                        color: '#8a7000',
	                                        fontSize: '0.92rem',
	                                        fontWeight: 850,
	                                        justifyContent: 'space-between',
	                                        boxShadow: 'none',
	                                        '&:hover': { bgcolor: '#fff7cc', boxShadow: 'none' },
	                                    }}
	                                >{localize("사진에서 찾은 약 보기")}</Button>
	                            )}
	                        </>
	                    )}
	                    <Box sx={{ position: 'relative', mb: 2 }}>
                            {reachedMedicationLimit ? (
                                <Box>
                                    <Typography sx={{ fontSize: '0.92rem', color: '#777', fontWeight: 650, lineHeight: 1.45, wordBreak: 'keep-all' }}>
                                        {usedLastAvailableSlot ? (
                                            <>{localize("이번 약까지는 등록할 수 있어요.")}<br />{localize("다음 약부터는 광고 시청이 필요해요.")}</>
                                        ) : (
                                            localize("더 등록하려면 광고 시청이 필요해요.")
                                        )}
                                    </Typography>
                                    <Button
                                        fullWidth
	                                        onClick={requestMedicationSlotReward}
                                        sx={{
                                            mt: 0.8,
                                            minHeight: 46,
                                            borderRadius: 2.5,
                                            bgcolor: '#ffe566',
                                            color: '#5a4a00',
                                            fontSize: '0.96rem',
                                            fontWeight: 850,
                                            boxShadow: 'none',
                                            '&:hover': { bgcolor: '#ffdf33', boxShadow: 'none' },
                                        }}
                                    >{localize("광고 보고 더 등록하기")}</Button>
                                </Box>
                            ) : (
		                        <TextField
		                            fullWidth
		                            autoFocus={isNameOnlyStep}
                                inputRef={nameInputRef}
		                            disabled={hasKnownLimit && remainingSlots <= 0}
		                            value={nameDraft}
	                            onChange={(e) => handleNameDraftChange(e.target.value)}
                            onFocus={() => {
                                if (language === 'ko' && nameDraft.trim()) setNameSearchState(prev => ({ ...prev, open: true }));
                            }}
                            onBlur={() => setTimeout(() => setNameSearchState(prev => ({ ...prev, open: false })), 180)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addMedicationFromName(nameDraft);
                                }
                            }}
                                    placeholder={localize('약 이름을 입력해주세요')}
                            variant="standard"
                            inputProps={{ maxLength: 40, 'aria-describedby': nameDraftNeedsAdding ? 'registration-name-draft-notice' : undefined }}
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: '1.15rem',
                                    fontWeight: 650,
                                    color: '#1a1a1a',
                                    pb: 0.25,
                                },
                            }}
                            sx={{
                                '& .MuiInput-root': {
                                    minHeight: 48,
                                    borderBottom: '2px solid #ffe566',
                                    bgcolor: '#fff',
                                },
                                '& .MuiInput-input': {
                                    pt: 1.15,
                                    pb: 0.8,
                                },
	                            }}
	                        />
                            )}
	                        {nameDraftNeedsAdding && (
                            <Typography id="registration-name-draft-notice" role="alert" sx={{ mt: 0.7, fontSize: '0.86rem', color: '#8a7000', lineHeight: 1.45, wordBreak: 'keep-all' }}>
                                {localize('입력한 약을 추가하거나 이름을 지운 뒤 진행해주세요.')}
                            </Typography>
                        )}
                        {!reachedMedicationLimit && hasNameDraft && (isNameOnlyStep || nameSearchState.open) && (
                            <Card ref={nameSearchCardRef} sx={{ mt: 0.6, borderRadius: 2.5, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden' }}>
                                {'ko' !== 'ko'}
                                {language === 'ko' && (nameSearchState.loading ? (
                                    <Box sx={{ p: 1.4 }}>
                                        <SingleLineText sx={{ fontSize: '0.9rem', color: '#888', fontWeight: 700 }}>
                                            {localize('약 목록에서 찾는 중이에요')}
                                        </SingleLineText>
                                    </Box>
                                ) : nameSearchState.results.length > 0 ? (
                                    <Box sx={{ maxHeight: 128, overflow: 'auto' }}>
                                        {nameSearchState.results.map((item, resultIndex) => (
                                            <Box
                                                key={item.itemSeq || `${item.itemName}-${resultIndex}`}
                                                role="button"
                                                tabIndex={0}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => selectMedicineFromDraft(item)}
                                                sx={{
                                                    px: 1.4,
                                                    py: 1.05,
                                                    cursor: 'pointer',
                                                    borderBottom: '1px solid #f2f2f2',
                                                    bgcolor: '#fff',
                                                    '&:active': { bgcolor: '#fff3c4' },
                                                }}
                                            >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '0.98rem', fontWeight: 800, color: '#1a1a1a', minWidth: 0, overflowWrap: 'anywhere' }}>
                                                    {item.itemName}
                                                </Typography>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        flexShrink: 0,
                                                        px: 0.65,
                                                        py: 0.15,
                                                        borderRadius: 1,
                                                        bgcolor: '#f5f5f5',
                                                        color: '#777',
                                                        fontSize: '0.66rem',
                                                        fontWeight: 850,
                                                    }}
                                                >
                                                    {['DUR', 'MFDS_PERMISSION', 'REVIEWED'].includes(item.source) ? localize('식약처') : localize(MEDICINE_SOURCE_LABELS[item.source]) || localize('식약처')}
                                                </Box>
                                            </Box>
                                        {'ko' !== 'ko'}
                                        {item.entpName && (
                                            <Typography sx={{ fontSize: '0.82rem', color: '#888' }} noWrap>
                                                {item.entpName}
                                            </Typography>
                                        )}
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Box sx={{ px: 1.5, py: 1.3 }}>
                                        <SingleLineText sx={{ fontSize: '0.96rem', fontWeight: 850, color: '#1a1a1a', mb: 0.35 }}>
                                            {localize(nameSearchState.failed ? '약 목록을 불러오지 못했어요' : '검색 결과가 없어요')}
                                        </SingleLineText>
	                                        <Typography sx={{ fontSize: '0.86rem', color: '#777', lineHeight: 1.45, wordBreak: 'keep-all' }}>
	                                            {localize('입력한 이름 그대로 추가할 수 있어요.')}
	                                        </Typography>
                                    </Box>
                                ))}
                                        <Box sx={{ px: 1.5, pt: 1, pb: 1.5, bgcolor: '#fff' }}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => addMedicationFromName(nameDraft)}
                                                sx={{
                                                    px: 2,
                                                    py: 1.5,
                                                    minHeight: 52,
                                                    borderRadius: 2.5,
                                                    bgcolor: '#ffe566',
                                                    color: '#5a4a00',
                                                    fontSize: '1rem',
                                                    lineHeight: 1.5,
                                                    fontWeight: 800,
                                                    textTransform: 'none',
                                                    boxShadow: 'none',
                                                    '&:hover': { bgcolor: '#ffdf33', boxShadow: 'none' },
                                                }}
                                            >
                                                <Typography component="span" sx={{ font: 'inherit', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'keep-all' }}>
                                                    {t("'{name}' 추가하기", { name: nameDraft.trim() })}
                                                </Typography>
                                            </Button>
                                        </Box>
                            </Card>
                        )}
                    </Box>

                    {filledMeds.length === 0 ? (
                        <Box sx={{ px: 0.1, mt: 0.2 }}>
                            {hasKnownLimit && remainingSlots <= 0 ? (
	                                <MedicationLimitCard onRewardClick={requestMedicationSlotReward} />
                            ) : (
                                <NameInputGuideCard
                                    photoSupported={language === 'ko' && isNameOnlyStep && (nativePhotoSupported || needsFeatureUpdate)}
                                    onPhotoClick={openPhotoSheet}
                                />
                            )}
                        </Box>
                    ) : (
	                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.4 }}>
	                            {filledMeds.map((med, index) => (
	                                <Box
	                                    key={med.key}
	                                    sx={{
	                                        display: 'flex',
	                                        alignItems: 'center',
	                                        gap: 1,
	                                        pl: 1.2,
	                                        pr: 0.45,
		                                        py: 0.8,
		                                        minHeight: 54,
	                                        borderRadius: 2.8,
	                                        bgcolor: '#fff',
	                                        border: '1px solid #e9ecef',
                                            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.035)',
	                                    }}
	                                >
	                                    <Box
	                                        sx={{
	                                            width: 30,
	                                            height: 30,
	                                            borderRadius: '50%',
	                                            bgcolor: '#fff8d6',
	                                            color: '#8a7000',
	                                            display: 'flex',
	                                            alignItems: 'center',
	                                            justifyContent: 'center',
	                                            flexShrink: 0,
                                                fontSize: '0.86rem',
                                                fontWeight: 850,
	                                        }}
	                                    >
	                                        {index + 1}
	                                    </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
	                                        <Typography sx={{
                                                fontSize: '1.03rem',
                                                fontWeight: 720,
                                                color: '#1a1a1a',
                                                lineHeight: 1.32,
                                                overflowWrap: 'anywhere',
                                                wordBreak: 'keep-all',
                                            }}>
	                                            {language !== 'ko' && med.localizedLanguage === language && med.localizedName ? med.localizedName : med.name}
	                                        </Typography>
                                            {'ko' !== 'ko'}
                                        </Box>
	                                    <IconButton
                                            aria-label={localize("{v0} 삭제", { v0: med.name })}
	                                        onClick={() => removeMedication(med.key)}
	                                        sx={{
		                                            width: 44,
		                                            height: 44,
	                                            color: '#aaa',
                                                flexShrink: 0,
                                                borderRadius: 2.2,
	                                            '&:hover': { bgcolor: '#f7f7f7' },
                                                '&:active': { bgcolor: '#f1f5f9' },
	                                        }}
	                                    >
	                                        <CloseIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Box>
                            ))}
	                        </Box>
                    )}
                </Section>
                )}

                {isUsagePeriodStep && (
                    <Section icon={<AccessTimeIcon sx={{ fontSize: 21 }} />} title={null}>
	                        <Box sx={{ mb: 2.2 }}>
	                            <SingleLineText sx={{ fontSize: '1.5rem', fontWeight: 850, color: '#1a1a1a', lineHeight: 1.3 }}>
	                                {localize('얼마나 약을 복용하시나요?')}
	                            </SingleLineText>
		                                {isPhotoScheduleReview && (
		                                    <SingleLineText sx={{ mt: 0.65, fontSize: '0.9rem', fontWeight: 650, color: '#777', lineHeight: 1.45, wordBreak: 'keep-all' }}>{localize("복용 기간이 사진의 내용과 맞는지 확인해주세요.")}</SingleLineText>
		                                )}
		                                {isPhotoScheduleReview && renderPhotoPreviewShortcut()}
			                            {filledMeds.length > 1 && (
			                                <Box sx={{ mt: 1.25 }}>
			                                    <Box
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => {
                                                        const nextShowPerMedication = !showPerMedicationPeriod;
                                                        setShowPerMedicationPeriod(nextShowPerMedication);
                                                    }}
                                                    onKeyDown={(event) => {
                                                        if (event.key !== 'Enter' && event.key !== ' ') return;
                                                        event.preventDefault();
                                                        const nextShowPerMedication = !showPerMedicationPeriod;
                                                        setShowPerMedicationPeriod(nextShowPerMedication);
                                                    }}
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 1.1,
                                                        minHeight: 44,
                                                        cursor: 'pointer',
                                                        WebkitTapHighlightColor: 'transparent',
                                                    }}
                                                >
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: '0.94rem', color: '#333', fontWeight: 850, lineHeight: 1.25 }}>
                                                                {t(showPerMedicationPeriod ? '약마다 다른 기간' : '모든 약 같은 기간')}
                                                            </Typography>
                                                        </Box>
			                                        <Switch
			                                            inputProps={{ 'aria-label': localize('모든 약 복용 기간 동일하게 설정') }}
			                                            checked={!showPerMedicationPeriod}
                                                        onClick={(event) => event.stopPropagation()}
				                                            onChange={(event) => {
				                                                const useSameSetting = event.target.checked;
				                                                setShowPerMedicationPeriod(!useSameSetting);
				                                            }}
		                                            sx={{
		                                                width: 52,
		                                                height: 34,
		                                                p: 0,
		                                                '& .MuiSwitch-switchBase': {
		                                                    p: '4px',
		                                                    '&.Mui-checked': {
		                                                        transform: 'translateX(18px)',
		                                                        color: '#fff',
		                                                        '& + .MuiSwitch-track': {
		                                                            bgcolor: '#ffe566',
		                                                            borderColor: '#e6c800',
		                                                            opacity: 1,
		                                                        },
		                                                    },
		                                                },
		                                                '& .MuiSwitch-thumb': {
		                                                    width: 26,
		                                                    height: 26,
		                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
		                                                },
		                                                '& .MuiSwitch-track': {
		                                                    borderRadius: 999,
		                                                    bgcolor: '#e5e7eb',
		                                                    border: '1px solid #d1d5db',
		                                                    opacity: 1,
		                                                },
		                                            }}
		                                        />
		                                    </Box>
		                                    {showPerMedicationPeriod && (
		                                        <Box sx={medicationSelectionScrollSx}>
		                                            {filledMeds.map((med, index) => {
		                                                const selected = med.key === selectedPeriodMed?.key;
		                                                const configured = isMedicationPeriodConfigured(med);
		                                                const completed = configured && !selected;
		                                                const statusLabel = selected ? translationKey('설정 중') : (configured ? translationKey('완료') : translationKey('설정 필요'));
		                                                return (
		                                                    <Box
		                                                        key={med.key}
		                                                        ref={(node) => {
		                                                            if (node) periodMedicationChipRefs.current[med.key] = node;
		                                                            else delete periodMedicationChipRefs.current[med.key];
		                                                        }}
		                                                        role="button"
		                                                        tabIndex={0}
		                                                        onClick={() => setSelectedPeriodMedKey(med.key)}
		                                                        sx={{
		                                                            flex: '0 0 auto',
		                                                            maxWidth: 172,
		                                                            minHeight: 58,
		                                                            px: 1.15,
		                                                            display: 'flex',
		                                                            alignItems: 'center',
		                                                            gap: 0.75,
		                                                            borderRadius: 2.7,
		                                                            bgcolor: selected ? '#fffbeb' : (completed ? '#f5faf6' : '#fff'),
		                                                            border: selected ? '1.5px solid #ffe566' : (completed ? '1.5px solid #d7eadb' : '1px solid #e5e7eb'),
		                                                            boxShadow: selected
		                                                                ? '0 3px 10px rgba(90,74,0,0.08)'
		                                                                : (completed ? '0 2px 8px rgba(79, 127, 93, 0.08)' : '0 1px 3px rgba(17,24,39,0.04)'),
		                                                            cursor: 'pointer',
		                                                            '&:active': { bgcolor: selected ? '#fff6cc' : '#f9fafb' },
		                                                        }}
		                                                    >
		                                                        <Box sx={{
		                                                            width: 24,
		                                                            height: 24,
		                                                            borderRadius: '50%',
		                                                            display: 'flex',
		                                                            alignItems: 'center',
		                                                            justifyContent: 'center',
		                                                            bgcolor: completed ? '#e6f3e9' : (selected ? '#ffe566' : '#f4f4f5'),
		                                                            color: completed ? '#4f7f5d' : (selected ? '#6b5600' : '#777'),
		                                                            fontSize: '0.76rem',
		                                                            fontWeight: 900,
		                                                            flexShrink: 0,
		                                                        }}>
		                                                            {completed ? <CheckCircleOutlineIcon sx={{ fontSize: 17, color: '#4f7f5d' }} /> : index + 1}
		                                                        </Box>
		                                                        <Box sx={{ minWidth: 0 }}>
		                                                            <Typography sx={{ fontSize: '0.92rem', fontWeight: 850, color: '#1f1f1f', minWidth: 0, lineHeight: 1.25 }} noWrap>
		                                                                {med.name}
		                                                            </Typography>
		                                                            <SingleLineText sx={{ mt: 0.15, fontSize: '0.72rem', fontWeight: 800, color: selected ? '#8a7000' : (completed ? '#4f7f5d' : '#aaa'), lineHeight: 1.2 }} >
		                                                                {localize(statusLabel)}
		                                                            </SingleLineText>
		                                                        </Box>
		                                                    </Box>
		                                                );
		                                            })}
		                                        </Box>
		                                    )}
		                                </Box>
		                            )}
		                        </Box>

	                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
	                            <Box
	                                role="button"
	                                tabIndex={0}
		                                onClick={() => {
		                                    if (showPerMedicationPeriod) setSelectedPeriodType('ongoing');
		                                    else {
		                                        setUsagePeriodType('ongoing');
		                                        setCustomDurationAutoOpened(false);
		                                    }
		                                }}
	                                sx={{
                                    px: 1.55,
	                                    py: 1.15,
	                                    minHeight: 84,
                                    borderRadius: 3,
	                                    bgcolor: activeUsagePeriodType === 'ongoing' ? '#fffbeb' : '#fff',
	                                    border: activeUsagePeriodType === 'ongoing' ? '1.5px solid #fde68a' : '1px solid #eeeeee',
                                    cursor: 'pointer',
                                    '&:active': { bgcolor: '#fff6cc' },
                                }}
                            >
	                                <Box sx={{ minHeight: 56, display: 'flex', alignItems: 'center', gap: 1.1 }}>
	                                    {activeUsagePeriodType === 'ongoing' ? (
                                        <CheckCircleOutlineIcon sx={{ fontSize: 22, color: '#8a7000', flexShrink: 0 }} />
                                    ) : (
                                        <RadioButtonUncheckedIcon sx={{ fontSize: 22, color: '#c7c7c7', flexShrink: 0 }} />
                                    )}
                                    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
	                                        <SingleLineText sx={{ fontSize: '1.02rem', fontWeight: 900, color: '#1a1a1a' }}>
	                                            {localize('계속 복용해요')}
	                                        </SingleLineText>
	                                        <SingleLineText sx={{ mt: 0.25, fontSize: '0.9rem', color: '#777', lineHeight: 1.45, wordBreak: 'keep-all' }}>
	                                            {localize('종료일을 정하지 않아요.')}
	                                        </SingleLineText>
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                role="button"
                                tabIndex={0}
		                                onClick={() => {
		                                    if (showPerMedicationPeriod) setSelectedPeriodType('fixed');
		                                    else {
		                                        setUsagePeriodType('fixed');
		                                        setCustomDurationAutoOpened(false);
		                                    }
		                                }}
                                sx={{
                                    px: 1.55,
	                                    py: 1.15,
	                                    minHeight: 84,
                                    borderRadius: 3,
	                                    bgcolor: activeUsagePeriodType === 'fixed' ? '#fffbeb' : '#fff',
	                                    border: activeUsagePeriodType === 'fixed' ? '1.5px solid #fde68a' : '1px solid #eeeeee',
                                    cursor: 'pointer',
                                    '&:active': { bgcolor: '#fff6cc' },
                                }}
                            >
	                                <Box sx={{ minHeight: 56, display: 'flex', alignItems: 'center', gap: 1.1 }}>
	                                    {activeUsagePeriodType === 'fixed' ? (
                                        <CheckCircleOutlineIcon sx={{ fontSize: 22, color: '#8a7000', flexShrink: 0 }} />
                                    ) : (
                                        <RadioButtonUncheckedIcon sx={{ fontSize: 22, color: '#c7c7c7', flexShrink: 0 }} />
                                    )}
                                    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
	                                        <SingleLineText sx={{ fontSize: '1.02rem', fontWeight: 900, color: '#1a1a1a' }}>
	                                            {localize('기간을 정해서 복용해요')}
	                                        </SingleLineText>
	                                        <SingleLineText sx={{ mt: 0.25, fontSize: '0.9rem', color: '#777', lineHeight: 1.45, wordBreak: 'keep-all' }}>
	                                            {localize('며칠 동안 복용할지 정해요.')}
	                                        </SingleLineText>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {usagePeriodSelected && (
                            <Box
	                                sx={{
	                                    mt: 1.25,
	                                    display: 'grid',
	                                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
	                                    gap: 0.9,
	                                }}
	                            >
                                <DateInputCard
                                    label={localize("시작일")} ariaLabel={localize("시작일 선택")}
                                    value={activePeriodStartDate}
                                    max="9999-12-31"
                                    onChange={(e) => {
                                        const nextStartDate = e.target.value || todayKey();
                                        if (showPerMedicationPeriod) {
                                            markPeriodConfigured();
                                            updateSelectedPeriodSchedule({
                                                startDate: nextStartDate,
                                                endDate: selectedPeriodSchedule.indefinite
                                                    ? null
                                                    : addDays(nextStartDate, Math.max((selectedPeriodSchedule.durationDays || durationDays || 3) - 1, 0)),
                                            });
                                            return;
                                        }
                                        setStartDate(nextStartDate);
                                    }}
                                />
                                <DateInputCard
                                    label={localize("종료일")} ariaLabel={localize("종료일 선택")}
                                    disabled={activeUsagePeriodType === 'ongoing'}
                                    value={activeEndDate}
                                    min={activePeriodStartDate}
                                    max="9999-12-31"
                                    onChange={(e) => {
                                        const nextEndDate = e.target.value || endDate;
                                        const nextDuration = daysBetweenInclusive(activePeriodStartDate, nextEndDate);
                                        if (showPerMedicationPeriod) {
                                            markPeriodConfigured();
                                            updateSelectedPeriodSchedule({
                                                indefinite: false,
                                                durationDays: nextDuration,
                                                endDate: nextEndDate,
                                                startDate: activePeriodStartDate,
                                            });
                                            setManualPeriodCustomDraft(prev => selectedPeriodMed ? ({ ...prev, [selectedPeriodMed.key]: String(nextDuration) }) : prev);
                                        } else {
                                            setDurationDays(nextDuration);
                                            setCustomDurationDraft(String(nextDuration));
                                        }
                                        setCustomDurationOpen(false);
                                        setCustomDurationAutoOpened(false);
                                    }}
                                />
	                            </Box>
	                        )}

	                        {activeUsagePeriodType === 'fixed' && (
                            <Box sx={{ mt: 1.1 }}>
                                <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'hidden', pb: 0.25, ...localizedPeriodChipRowSx() }}>
                                    {DAY_PRESETS.map(day => {
	                                        const durationCustomOpen = showPerMedicationPeriod
	                                            ? !!manualPeriodCustomOpen[selectedPeriodMed?.key]
	                                            : customDurationOpen;
	                                        const selected = !durationCustomOpen && Number(activeDurationDays) === day;
                                        return (
                                        <Chip
                                            key={day}
                                            label={localize("{v0}일 동안", { v0: day })}
                                            onClick={() => {
	                                                if (showPerMedicationPeriod) {
	                                                    setManualPeriodCustomOpen(prev => selectedPeriodMed ? ({ ...prev, [selectedPeriodMed.key]: false }) : prev);
	                                                    setManualPeriodCustomDraft(prev => selectedPeriodMed ? ({ ...prev, [selectedPeriodMed.key]: String(day) }) : prev);
	                                                    setSelectedPeriodDuration(day);
	                                                    return;
	                                                }
		                                                setDurationDays(day);
		                                                setCustomDurationOpen(false);
		                                                setCustomDurationAutoOpened(false);
			                                                setCustomDurationDraft(String(day));
                                            }}
                                            sx={{
                                                height: 42,
                                                minWidth: 0,
                                                flex: '1 1 0',
                                                backgroundColor: selected ? '#ffe566' : '#fff',
                                                color: selected ? '#5a4a00' : '#666',
                                                border: selected ? '1px solid #e6c800' : '1px solid #e5e7eb',
                                                fontSize: '0.82rem',
                                                fontWeight: 850,
                                                borderRadius: '999px',
                                                '& .MuiChip-label': { px: 0.5, overflow: 'visible', whiteSpace: 'nowrap' },
                                                '&:hover, &:focus, &.Mui-focusVisible': {
                                                    backgroundColor: selected ? '#ffe566' : '#fff',
                                                },
                                                '&:active': {
                                                    backgroundColor: selected ? '#f4d84d' : '#fff',
                                                },
                                            }}
                                        />
                                        );
                                    })}
                                    <Chip
                                        label={localize("다른 기간")}
                                        onClick={() => {
	                                            if (showPerMedicationPeriod) {
	                                                if (!selectedPeriodMed) return;
	                                                setManualPeriodCustomOpen(prev => ({ ...prev, [selectedPeriodMed.key]: !prev[selectedPeriodMed.key] }));
	                                                setManualPeriodCustomDraft(prev => ({ ...prev, [selectedPeriodMed.key]: String(activeDurationDays || 3) }));
	                                                return;
	                                            }
		                                            setCustomDurationOpen(prev => !prev);
		                                            setCustomDurationAutoOpened(false);
			                                            setCustomDurationDraft(String(durationDays || 3));
                                        }}
                                        sx={{
                                                height: 42,
                                                minWidth: 0,
	                                            flex: '1 1 0',
	                                            backgroundColor: (showPerMedicationPeriod ? manualPeriodCustomOpen[selectedPeriodMed?.key] : customDurationOpen) ? '#ffe566' : '#fff',
	                                            color: (showPerMedicationPeriod ? manualPeriodCustomOpen[selectedPeriodMed?.key] : customDurationOpen) ? '#5a4a00' : '#666',
	                                            border: (showPerMedicationPeriod ? manualPeriodCustomOpen[selectedPeriodMed?.key] : customDurationOpen) ? '1px solid #e6c800' : '1px solid #e5e7eb',
                                            fontSize: '0.82rem',
                                            fontWeight: 850,
                                            borderRadius: '999px',
                                            '& .MuiChip-label': { px: 0.5, overflow: 'visible', whiteSpace: 'nowrap' },
                                            '&:hover, &:focus, &.Mui-focusVisible': {
	                                                backgroundColor: (showPerMedicationPeriod ? manualPeriodCustomOpen[selectedPeriodMed?.key] : customDurationOpen) ? '#ffe566' : '#fff',
                                            },
                                            '&:active': {
	                                                backgroundColor: (showPerMedicationPeriod ? manualPeriodCustomOpen[selectedPeriodMed?.key] : customDurationOpen) ? '#f4d84d' : '#fff',
                                            },
                                        }}
                                    />
                                </Box>
	                                {(showPerMedicationPeriod ? manualPeriodCustomOpen[selectedPeriodMed?.key] : customDurationOpen) && (
                                    <Box sx={{
                                        mt: 1,
                                        p: 1,
                                        borderRadius: 3,
                                        bgcolor: '#fafafa',
                                        border: '1px solid #eeeeee',
                                    }}>
	                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
	                                                <TextField
	                                                    type="text"
		                                                    autoFocus={showPerMedicationPeriod || !customDurationAutoOpened}
		                                                    value={showPerMedicationPeriod ? (manualPeriodCustomDraft[selectedPeriodMed?.key] ?? String(activeDurationDays || 3)) : customDurationDraft}
                                                    onBeforeInput={preventNonNumericInput}
                                                    onChange={(e) => {
                                                        const nextDraft = normalizeDurationInput(e.target.value);
	                                                        if (showPerMedicationPeriod && selectedPeriodMed) {
	                                                            setManualPeriodCustomDraft(prev => ({ ...prev, [selectedPeriodMed.key]: nextDraft }));
	                                                            if (nextDraft) setSelectedPeriodDuration(commitDurationDraft(nextDraft, activeDurationDays || 3));
	                                                            return;
	                                                        }
	                                                        setCustomDurationDraft(nextDraft);
	                                                        if (nextDraft) setDurationDays(commitDurationDraft(nextDraft));
                                                    }}
                                                    onBlur={() => {
	                                                        if (showPerMedicationPeriod && selectedPeriodMed) {
	                                                            const next = commitDurationDraft(manualPeriodCustomDraft[selectedPeriodMed.key], activeDurationDays || 3);
	                                                            setManualPeriodCustomDraft(prev => ({ ...prev, [selectedPeriodMed.key]: String(next) }));
	                                                            setSelectedPeriodDuration(next);
	                                                            return;
	                                                        }
	                                                        const next = commitDurationDraft(customDurationDraft);
	                                                        setDurationDays(next);
	                                                        setCustomDurationDraft(String(next));
                                                    }}
                                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                    size="small"
                                                    sx={{
                                                        width: 112,
                                                        '& .MuiOutlinedInput-root': {
                                                            minHeight: 48,
                                                            borderRadius: 2.5,
                                                            bgcolor: '#fff',
                                                            fontWeight: 850,
                                                        },
                                                    }}
                                                />
	                                                <SingleLineText sx={{ fontSize: '0.95rem', color: '#666', fontWeight: 800 }}>{localize("일 동안 복용해요")}</SingleLineText>
	                                        </Box>
	                                    </Box>
	                                )}
			                            </Box>
			                        )}
			                        {filledMeds.length > 1 && (
			                            <Box sx={{ mt: 1.15 }}>
		                                <Button
		                                    fullWidth
		                                    onClick={() => setShowPerMedicationPeriod(prev => !prev)}
		                                    sx={{
		                                        display: 'none',
	                                        minHeight: 48,
	                                        justifyContent: 'space-between',
	                                        px: 1.35,
	                                        borderRadius: 2.6,
	                                        bgcolor: showPerMedicationPeriod ? '#fffbeb' : '#fff',
	                                        color: '#5a4a00',
	                                        border: showPerMedicationPeriod ? '1px solid #f4e7b1' : '1px solid #eeeeee',
	                                        fontWeight: 900,
	                                        boxShadow: 'none',
	                                        '&:hover': { bgcolor: showPerMedicationPeriod ? '#fffbeb' : '#fff', boxShadow: 'none' },
	                                        '&:active': { bgcolor: '#fff3c4' },
	                                    }}
	                                    endIcon={<ChevronRightIcon sx={{ transform: showPerMedicationPeriod ? 'rotate(90deg)' : 'none', transition: 'transform 160ms ease' }} />}
	                                >{localize("약별로 다르게 설정할까요?")}</Button>
			                                {false}
	                            </Box>
	                        )}

	                        <Box
	                            role="button"
                            tabIndex={0}
                            onClick={openMedicationListSheet}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.45,
                                alignSelf: 'flex-start',
                                px: 1,
                                py: 0.65,
                                mt: 2.4,
                                borderRadius: 2,
                                bgcolor: '#fffbeb',
                                cursor: 'pointer',
                                '&:active': { bgcolor: '#fff3c4' },
                            }}
                        >
                            <MedicationIcon sx={{ fontSize: 18, color: '#8a7000', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: '#5a4a00', lineHeight: 1.25 }} noWrap>
                                {localize('등록한 약 확인하기')}
                            </Typography>
                            <ChevronRightIcon sx={{ fontSize: 18, color: '#8a7000', flexShrink: 0 }} />
                        </Box>
                    </Section>
                )}

                {isReminderStep && (
                    <Section icon={<AccessTimeIcon sx={{ fontSize: 21 }} />} title={null}>
                        <Box sx={{ mb: 2.7 }}>
		                            <SingleLineText sx={{ fontSize: '1.5rem', fontWeight: 850, color: '#1a1a1a', lineHeight: 1.3 }}>
	                                {localize('언제 알려드릴까요?')}
		                            </SingleLineText>
			                            <Typography sx={{ mt: 0.65, fontSize: '0.9rem', color: '#777', fontWeight: 650, lineHeight: 1.45, wordBreak: 'keep-all' }}>
			                                {isPhotoScheduleReview
                                            ? localize("알림 횟수가 사진의 내용과 맞는지 확인하고, 시간을 정해주세요.")
	                                    : localize('하루 몇 번 받을지 정해주세요.')}
			                            </Typography>
		                            {isPhotoScheduleReview && renderPhotoPreviewShortcut()}
		                            {filledMeds.length > 1 && (
		                                <Box sx={{ mt: 1.25 }}>
				                                    <Box
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => {
                                                        const nextShowPerMedication = !showPerMedicationReminder;
                                                        setShowPerMedicationReminder(nextShowPerMedication);
                                                    }}
                                                    onKeyDown={(event) => {
                                                        if (event.key !== 'Enter' && event.key !== ' ') return;
                                                        event.preventDefault();
                                                        const nextShowPerMedication = !showPerMedicationReminder;
                                                        setShowPerMedicationReminder(nextShowPerMedication);
                                                    }}
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 1.1,
                                                        minHeight: 44,
                                                        cursor: 'pointer',
                                                        WebkitTapHighlightColor: 'transparent',
                                                    }}
                                                >
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: '0.94rem', color: '#333', fontWeight: 850, lineHeight: 1.25 }}>
                                                                {showPerMedicationReminder ? localize("약마다 다른 알림") : localize("모든 약 같은 알림")}
                                                            </Typography>
                                                        </Box>
			                                        <Switch
			                                            inputProps={{ 'aria-label': localize('모든 약 알림 동일하게 설정') }}
			                                            checked={!showPerMedicationReminder}
                                                        onClick={(event) => event.stopPropagation()}
				                                            onChange={(event) => {
				                                                const useSameSetting = event.target.checked;
				                                                setShowPerMedicationReminder(!useSameSetting);
				                                            }}
		                                            sx={{
		                                                width: 52,
		                                                height: 34,
		                                                p: 0,
		                                                '& .MuiSwitch-switchBase': {
		                                                    p: '4px',
		                                                    '&.Mui-checked': {
		                                                        transform: 'translateX(18px)',
		                                                        color: '#fff',
		                                                        '& + .MuiSwitch-track': {
		                                                            bgcolor: '#ffe566',
		                                                            borderColor: '#e6c800',
		                                                            opacity: 1,
		                                                        },
		                                                    },
		                                                },
		                                                '& .MuiSwitch-thumb': {
		                                                    width: 26,
		                                                    height: 26,
		                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
		                                                },
		                                                '& .MuiSwitch-track': {
		                                                    borderRadius: 999,
		                                                    bgcolor: '#e5e7eb',
		                                                    border: '1px solid #d1d5db',
		                                                    opacity: 1,
		                                                },
		                                            }}
		                                        />
		                                    </Box>
		                                    {showPerMedicationReminder && (
		                                        <Box sx={medicationSelectionScrollSx}>
			                                            {filledMeds.map((med, index) => {
			                                                const selected = med.key === selectedReminderMed?.key;
			                                                const configured = isMedicationReminderConfigured(med);
			                                                const completed = configured && !selected;
			                                                const statusLabel = selected ? translationKey('설정 중') : (configured ? translationKey('완료') : translationKey('설정 필요'));
			                                                return (
		                                                    <Box
		                                                        key={med.key}
		                                                        ref={(node) => {
		                                                            if (node) reminderMedicationChipRefs.current[med.key] = node;
		                                                            else delete reminderMedicationChipRefs.current[med.key];
		                                                        }}
		                                                        role="button"
		                                                        tabIndex={0}
		                                                        onClick={() => setSelectedReminderMedKey(med.key)}
		                                                        sx={{
		                                                            flex: '0 0 auto',
		                                                            maxWidth: 172,
		                                                            minHeight: 58,
		                                                            px: 1.15,
		                                                            display: 'flex',
		                                                            alignItems: 'center',
		                                                            gap: 0.75,
		                                                            borderRadius: 2.7,
		                                                            bgcolor: selected ? '#fffbeb' : (completed ? '#f5faf6' : '#fff'),
		                                                            border: selected ? '1.5px solid #ffe566' : (completed ? '1.5px solid #d7eadb' : '1px solid #e5e7eb'),
		                                                            boxShadow: selected
		                                                                ? '0 3px 10px rgba(90,74,0,0.08)'
		                                                                : (completed ? '0 2px 8px rgba(79, 127, 93, 0.08)' : '0 1px 3px rgba(17,24,39,0.04)'),
		                                                            cursor: 'pointer',
		                                                            '&:active': { bgcolor: selected ? '#fff6cc' : '#f9fafb' },
		                                                        }}
		                                                    >
		                                                        <Box sx={{
		                                                            width: 24,
		                                                            height: 24,
		                                                            borderRadius: '50%',
		                                                            display: 'flex',
		                                                            alignItems: 'center',
		                                                            justifyContent: 'center',
			                                                            bgcolor: completed ? '#e6f3e9' : (selected ? '#ffe566' : '#f4f4f5'),
			                                                            color: completed ? '#4f7f5d' : (selected ? '#6b5600' : '#777'),
		                                                            fontSize: '0.76rem',
		                                                            fontWeight: 900,
		                                                            flexShrink: 0,
		                                                        }}>
			                                                            {completed ? <CheckCircleOutlineIcon sx={{ fontSize: 17, color: '#4f7f5d' }} /> : index + 1}
		                                                        </Box>
		                                                        <Box sx={{ minWidth: 0 }}>
		                                                            <Typography sx={{ fontSize: '0.92rem', fontWeight: 850, color: '#1f1f1f', minWidth: 0, lineHeight: 1.25 }} noWrap>
		                                                                {med.name}
		                                                            </Typography>
		                                                            <SingleLineText sx={{ mt: 0.15, fontSize: '0.72rem', fontWeight: 800, color: selected ? '#8a7000' : (completed ? '#4f7f5d' : '#aaa'), lineHeight: 1.2 }} >
		                                                                {localize(statusLabel)}
		                                                            </SingleLineText>
		                                                        </Box>
		                                                    </Box>
		                                                );
		                                            })}
		                                        </Box>
		                                    )}
		                                </Box>
		                            )}
		                        </Box>

		                        <Box sx={{ mb: 3 }}>
	                            <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 900, color: '#1a1a1a', mb: 1.1 }}>
	                                {localize('하루에 몇 번 알려드릴까요?')}
	                            </SingleLineText>
	                            <Box sx={{ display: 'flex', gap: 0.95, rowGap: 0.9, flexWrap: 'wrap', mb: 1.65 }}>
		                                {COUNT_PRESETS.map(preset => {
		                                    const selected = activeTimes.length === preset.count;
		                                    return (
		                                        <Chip
	                                            key={preset.count}
	                                            label={language === 'en' && preset.count === 1 ? '1 time' : t('{count}번', { count: preset.count })}
		                                            onClick={() => {
		                                                if (showPerMedicationReminder) {
		                                                    handleSelectedReminderPresetClick(preset);
		                                                    return;
		                                                }
		                                                handlePresetCountClick(preset);
		                                            }}
                                            sx={{
	                                                height: 42,
	                                                minWidth: 60,
                                                backgroundColor: selected ? '#ffe566' : '#fff',
                                                color: selected ? '#5a4a00' : '#666',
                                                border: selected ? '1px solid #e6c800' : '1px solid #e5e7eb',
	                                                fontSize: '0.98rem',
	                                                fontWeight: 850,
                                                borderRadius: '999px',
	                                                '& .MuiChip-label': { px: 1.25 },
                                                '&:hover, &:focus, &.Mui-focusVisible': {
                                                    backgroundColor: selected ? '#ffe566' : '#fff',
                                                },
                                                '&:active': {
                                                    backgroundColor: selected ? '#f4d84d' : '#fff',
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </Box>

	                            <MedicationTimeOptionList
	                                options={existingTimeOptions}
	                                selectedOptionKey={showPerMedicationReminder
	                                    ? selectedReminderExistingTimeKeys[selectedReminderMed?.key]
	                                    : selectedExistingTimeKey}
	                                onSelect={(option) => {
	                                    if (showPerMedicationReminder) {
	                                        handleSelectedReminderExistingTimeOptionClick(option);
	                                        return;
	                                    }
	                                    handleExistingTimeOptionClick(option);
	                                }}
	                            />

	                            {activeTimes.map((time, index) => (
	                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.05 }}>
		                                    <TextField
		                                        type="time"
		                                        value={time}
		                                        inputRef={(node) => {
	                                            timeInputRefs.current[index] = node;
	                                        }}
		                                        onChange={(e) => {
		                                            if (showPerMedicationReminder) updateSelectedReminderTime(index, e.target.value);
		                                            else updateTime(index, e.target.value);
		                                        }}
                                            onClick={(event) => {
                                                const input = timeInputRefs.current[index];
                                                if (!input) return;
                                                if (event.target === input) {
                                                    try {
                                                        if (typeof input.showPicker === 'function') {
                                                            input.showPicker();
                                                            return;
                                                        }
                                                    } catch (_) {}
                                                    try { input.focus(); } catch (_) {}
                                                    return;
                                                }
                                                openNativePicker(input);
                                            }}
                                        fullWidth
                                        size="small"
                                        inputProps={{ step: 300 }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                minHeight: 52,
                                                borderRadius: 2.5,
                                                bgcolor: '#fff',
                                                fontSize: '1.02rem',
                                                fontWeight: 800,
                                            },
                                        }}
                                    />
                                    <IconButton
	                                        disabled={activeTimes.length <= 1}
	                                        onClick={() => {
	                                            if (showPerMedicationReminder) removeSelectedReminderTime(index);
	                                            else removeTime(index);
	                                        }}
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            color: '#aaa',
                                            '&:disabled': { color: '#ddd' },
                                        }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button
	                                onClick={() => {
	                                    if (showPerMedicationReminder) addSelectedReminderTime();
	                                    else addTime();
	                                }}
                                startIcon={<AddIcon />}
                                sx={{
                                    color: '#5a4a00',
                                    fontWeight: 900,
                                    borderRadius: 2.5,
                                    px: 1.2,
                                    minHeight: 42,
                                }}
                            >
                                {localize('시간 추가')}
                            </Button>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 900, color: '#1a1a1a', mb: 1.1 }}>
                                {localize('알림 방식')}
                            </SingleLineText>
	                            <AlertMethodSection
	                                form={{
	                                    alertType: showPerMedicationReminder ? (selectedReminderSchedule.alertType || alertType) : alertType,
	                                    alarmVolume: showPerMedicationReminder ? (selectedReminderSchedule.alarmVolume ?? alarmVolume) : alarmVolume,
	                                }}
	                                setForm={(updater) => {
	                                    const next = typeof updater === 'function'
	                                        ? updater({
	                                            alertType: showPerMedicationReminder ? (selectedReminderSchedule.alertType || alertType) : alertType,
	                                            alarmVolume: showPerMedicationReminder ? (selectedReminderSchedule.alarmVolume ?? alarmVolume) : alarmVolume,
	                                        })
	                                        : updater;
	                                    if (showPerMedicationReminder) {
	                                        updateSelectedReminderSchedule({
	                                            alertType: next.alertType || selectedReminderSchedule.alertType || alertType,
	                                            alarmVolume: typeof next.alarmVolume === 'number' ? next.alarmVolume : (selectedReminderSchedule.alarmVolume ?? alarmVolume),
	                                        });
	                                        return;
	                                    }
	                                    if (next.alertType) setAlertType(next.alertType);
	                                    if (typeof next.alarmVolume === 'number') setAlarmVolume(next.alarmVolume);
	                                }}
                            />
                        </Box>

                        <Box sx={{ mt: 0 }}>
                            <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 900, color: '#1a1a1a', mb: 1.1 }}>
                                {localize('집요한 알림')}
                            </SingleLineText>
                            <Box
                                sx={{
                                    p: 1.75,
                                    borderRadius: 3,
                                    bgcolor: '#fff',
                                    border: '1px solid #e5e7eb',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.35, minHeight: 58 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#1a1a1a', lineHeight: 1.35 }}>
	                                            {t(activeSnoozeEnabled ? '켜짐' : '꺼짐')}
                                        </Typography>
                                        <Typography sx={{ mt: 0.35, fontSize: '0.88rem', color: '#777', lineHeight: 1.45, fontWeight: 750, wordBreak: 'keep-all' }}>
	                                            {t(activeSnoozeEnabled ? '먹었다고 확인할 때까지 다시 알려드려요.' : '첫 알림 한 번만 보내요.')}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flexShrink: 0 }}>
                                        <Switch
                                            inputProps={{ 'aria-label': localize('집요한 알림 켜기 끄기') }}
	                                            checked={activeSnoozeEnabled}
	                                            onChange={(event) => {
	                                                const checked = event.target.checked;
	                                                if (showPerMedicationReminder) {
	                                                    updateSelectedReminderSchedule({
	                                                        reminderEnabled: checked,
	                                                        reminderIntervalMinutes: checked && Number(selectedReminderSchedule.reminderIntervalMinutes ?? reminderIntervalMinutes) <= 0 ? 30 : (selectedReminderSchedule.reminderIntervalMinutes ?? reminderIntervalMinutes),
	                                                        maxReminderMinutes: checked && Number(selectedReminderSchedule.maxReminderMinutes ?? maxReminderMinutes) <= 0 ? 120 : (selectedReminderSchedule.maxReminderMinutes ?? maxReminderMinutes),
	                                                    });
	                                                    return;
	                                                }
	                                                setReminderEnabled(checked);
                                                if (!checked) {
                                                    setShowSnoozeEditor(false);
                                                    return;
                                                }
                                                setReminderIntervalMinutes(prev => Number(prev) > 0 ? prev : 30);
                                                setMaxReminderMinutes(prev => Number(prev) > 0 ? prev : 120);
                                            }}
                                            sx={{
                                                width: 52,
                                                height: 34,
                                                p: 0,
                                                '& .MuiSwitch-switchBase': {
                                                    p: '4px',
                                                    '&.Mui-checked': {
                                                        transform: 'translateX(18px)',
                                                        color: '#fff',
                                                        '& + .MuiSwitch-track': {
                                                            bgcolor: '#ffe566',
                                                            borderColor: '#e6c800',
                                                            opacity: 1,
                                                        },
                                                    },
                                                },
                                                '& .MuiSwitch-thumb': {
                                                    width: 26,
                                                    height: 26,
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                                                },
                                                '& .MuiSwitch-track': {
                                                    borderRadius: 999,
                                                    bgcolor: '#e5e7eb',
                                                    border: '1px solid #d1d5db',
                                                    opacity: 1,
                                                },
                                            }}
                                        />
                                    </Box>
                                </Box>
	                                {activeSnoozeEnabled && (
                                    <Box
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setShowSnoozeEditor(prev => !prev)}
                                        sx={{
                                            mt: 1.35,
                                            pt: 1.25,
                                            borderTop: '1px solid #f1f5f9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            cursor: 'pointer',
                                            '&:active': { opacity: 0.72 },
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <SingleLineText sx={{ fontSize: '0.82rem', color: '#999', fontWeight: 850, lineHeight: 1.25 }}>
                                                {localize('알림 간격')}
                                            </SingleLineText>
                                            <Typography sx={{ mt: 0.2, fontSize: '0.9rem', color: '#555', fontWeight: 850, lineHeight: 1.35, wordBreak: 'keep-all' }}>
	                                                {showPerMedicationReminder
	                                                    ? formatSnoozeSummary(selectedReminderSchedule.reminderIntervalMinutes ?? reminderIntervalMinutes, selectedReminderSchedule.maxReminderMinutes ?? maxReminderMinutes)
	                                                    : snoozeSummaryText}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1, color: '#8a7000', flexShrink: 0 }}>
                                            <SingleLineText sx={{ fontSize: '0.86rem', fontWeight: 900 }}>
                                                {localize('변경')}
                                            </SingleLineText>
                                            <ChevronRightIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                    </Box>
                                )}
                                {showSnoozeEditor && (
                                    <Box ref={snoozeEditorRef} sx={{ mt: 1.65, pt: 1.65, borderTop: '1px solid #eeeeee' }}>
                                        <SnoozeSection
                                            hideToggle
                                            form={{
	                                                reminderEnabled: activeSnoozeEnabled,
	                                                reminderIntervalMinutes: showPerMedicationReminder ? (selectedReminderSchedule.reminderIntervalMinutes ?? reminderIntervalMinutes) : reminderIntervalMinutes,
	                                                maxReminderMinutes: showPerMedicationReminder ? (selectedReminderSchedule.maxReminderMinutes ?? maxReminderMinutes) : maxReminderMinutes,
                                            }}
                                            setForm={(updater) => {
                                                const current = {
	                                                    reminderEnabled: activeSnoozeEnabled,
	                                                    reminderIntervalMinutes: showPerMedicationReminder ? (selectedReminderSchedule.reminderIntervalMinutes ?? reminderIntervalMinutes) : reminderIntervalMinutes,
	                                                    maxReminderMinutes: showPerMedicationReminder ? (selectedReminderSchedule.maxReminderMinutes ?? maxReminderMinutes) : maxReminderMinutes,
	                                                };
	                                                const next = typeof updater === 'function' ? updater(current) : updater;
	                                                if (showPerMedicationReminder) {
	                                                    updateSelectedReminderSchedule({
	                                                        reminderEnabled: typeof next.reminderEnabled === 'boolean' ? next.reminderEnabled : current.reminderEnabled,
	                                                        reminderIntervalMinutes: next.reminderIntervalMinutes ?? current.reminderIntervalMinutes,
	                                                        maxReminderMinutes: next.maxReminderMinutes ?? current.maxReminderMinutes,
	                                                    });
	                                                    return;
	                                                }
	                                                if (typeof next.reminderEnabled === 'boolean') setReminderEnabled(next.reminderEnabled);
                                                if (next.reminderIntervalMinutes !== undefined) setReminderIntervalMinutes(next.reminderIntervalMinutes);
                                                if (next.maxReminderMinutes !== undefined) setMaxReminderMinutes(next.maxReminderMinutes);
                                            }}
                                        />
                                    </Box>
		                                )}
		                            </Box>
		                        </Box>
			                        {filledMeds.length > 1 && (
		                            <Box sx={{ mt: 1.15 }}>
		                                <Button
		                                    fullWidth
		                                    onClick={() => setShowPerMedicationReminder(prev => !prev)}
		                                    sx={{
		                                        display: 'none',
	                                        minHeight: 48,
	                                        justifyContent: 'space-between',
	                                        px: 1.35,
	                                        borderRadius: 2.6,
	                                        bgcolor: showPerMedicationReminder ? '#fffbeb' : '#fff',
	                                        color: '#5a4a00',
	                                        border: showPerMedicationReminder ? '1px solid #f4e7b1' : '1px solid #eeeeee',
	                                        fontWeight: 900,
	                                        boxShadow: 'none',
	                                        '&:hover': { bgcolor: showPerMedicationReminder ? '#fffbeb' : '#fff', boxShadow: 'none' },
	                                        '&:active': { bgcolor: '#fff3c4' },
	                                    }}
	                                    endIcon={<ChevronRightIcon sx={{ transform: showPerMedicationReminder ? 'rotate(90deg)' : 'none', transition: 'transform 160ms ease' }} />}
	                                >{localize("약별로 다르게 설정할까요?")}</Button>
		                                {false}
	                            </Box>
	                        )}
	                    </Section>
	                )}

                {showScheduleFields && showAlertEditor && entryMode !== 'review' && (
                    <Section
                        icon={<AccessTimeIcon sx={{ fontSize: 21 }} />}
                        title={localize("알림 방식을 정해주세요")}
                    >
                        <Box sx={{
                            p: 1.5,
                            borderRadius: 3,
                            bgcolor: '#f9fafb',
                            border: '1px solid #e5e7eb',
                        }}>
                            <AlertMethodSection
                                form={{ alertType, alarmVolume }}
                                setForm={(updater) => {
                                    const next = typeof updater === 'function'
                                        ? updater({ alertType, alarmVolume })
                                        : updater;
                                    if (next.alertType) setAlertType(next.alertType);
                                    if (typeof next.alarmVolume === 'number') setAlarmVolume(next.alarmVolume);
                                }}
                            />
                        </Box>
                    </Section>
                )}
                    </>
                )}

                {entryMode === 'review' && (
                    <Box sx={{ pt: 1, pb: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}>
                        {finalReviewVisible && <AlertPermissionNotices medications={filledMeds.map(buildPayload)}
                            onOpenAlertSettings={() => {
                                const med = filledMeds.find(row => buildPayload(row).alertType === 'FULLSCREEN');
                                if (!med) return;
                                setSelectedReviewMedKey(med.key);
                                setReviewEditDraft(buildReviewEditDraft(med));
                                setReviewEditSection('alertMethod');
                                registrationJourney.event('edit', 'registration_review');
                            }} />}
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={primaryActionDisabled || saving || preparingReview || checkingMedicationLimit || interactionCheckPending}
                            aria-busy={saving || preparingReview || checkingMedicationLimit || interactionCheckPending}
                            onClick={handlePrimaryAction}
                            sx={{
                                py: 1.45,
                                borderRadius: 3,
                                bgcolor: '#ffe566',
                                color: '#5a4a00',
                                boxShadow: 'none',
                                fontSize: '1.05rem',
                                fontWeight: 900,
                                '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                                '&:disabled': { bgcolor: '#e5e7eb', color: '#999' },
                            }}
                        >
                            {saving || preparingReview || checkingMedicationLimit || interactionCheckPending ? (
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} sx={{ color: '#777' }} />
                                    {saving ? localize("등록 중이에요") : localize("확인 중이에요")}
                                </Box>
                            ) : isPhotoCandidateReview ? (
                                localize("다음으로")
                            ) : filledMeds.length > 0 ? (
                                t(translationKey('등록하기'))
                            ) : t(translationKey('약 이름을 입력해주세요'))}
                        </Button>
                    </Box>
                )}
            </Box>

            <BottomSheet
                open={entryMode === 'review' && !!reviewEditSection}
                onClose={closeReviewEditSheet}
                maxHeight="88vh"
                footer={
                    <>
                        <Button
                            fullWidth
                            onClick={saveReviewEditSheet}
                            disabled={!!reviewDraftError(reviewEditDraft)}
                            sx={{
                                py: 1.45,
                                borderRadius: 3.2,
                                bgcolor: '#ffe566',
                                color: '#5a4a00',
                                fontSize: '1rem',
                                fontWeight: 950,
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#ffdf33', boxShadow: 'none' },
                            }}
                        >{localize("저장")}</Button>
                    </>
                }
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
	                            <SingleLineText sx={{ fontSize: '1.15rem', fontWeight: 950, color: '#111827', lineHeight: 1.25 }}>
	                                {localize(reviewEditTitle)}
	                            </SingleLineText>
	                        </Box>
                        <IconButton onClick={closeReviewEditSheet} sx={{ width: 44, height: 44, color: '#9ca3af', flexShrink: 0 }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    {renderReviewEditContent()}
                    {reviewEditDraft && reviewDraftError(reviewEditDraft) && (
                        <Typography role="status" sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
                            {localize(reviewDraftError(reviewEditDraft))}
                        </Typography>
                    )}
                </Box>
            </BottomSheet>

            {showEditableForm && entryMode !== 'review' && (
                <Box sx={{
                // Keep the name-step action in document flow so a resized WebView cannot cover Add.
                position: isNameOnlyStep ? 'static' : 'fixed',
                left: isNameOnlyStep ? 'auto' : '50%',
                bottom: isNameOnlyStep ? 'auto' : 0,
                transform: isNameOnlyStep ? 'none' : 'translateX(-50%)',
                ...(isNameOnlyStep && { mt: 'auto', mx: 'auto' }),
                width: '100%',
                maxWidth: 480,
                bgcolor: '#fff',
                borderTop: '1px solid #f0f0f0',
                px: 2,
                pt: 1.5,
                pb: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
                }}>
                <Button
                    fullWidth
                    variant={showPendingNameNext ? 'outlined' : 'contained'}
                    disabled={primaryActionDisabled || saving || preparingReview || checkingMedicationLimit}
                    onClick={handlePrimaryAction}
                    sx={{
                        py: 1.45,
                        borderRadius: 3,
                        bgcolor: showPendingNameNext ? '#fff' : '#ffe566',
                        color: showPendingNameNext ? '#555' : '#5a4a00',
                        borderColor: showPendingNameNext ? '#d9d9d9' : 'transparent',
                        boxShadow: 'none',
                        fontSize: '1.05rem',
                        fontWeight: 900,
                        '&:hover': {
                            bgcolor: showPendingNameNext ? '#f8f8f8' : '#e8d040',
                            borderColor: showPendingNameNext ? '#c8c8c8' : 'transparent',
                            boxShadow: 'none',
                        },
                        '&:disabled': {
                            bgcolor: '#e5e7eb',
                            color: '#999',
                            borderColor: 'transparent',
                        },
                    }}
                >
                    {saving || preparingReview || checkingMedicationLimit ? (
                        <CircularProgress size={22} sx={{ color: '#999' }} />
                    ) : t(primaryActionLabel)}
                </Button>
            </Box>
            )}

	            {showEditableForm && entryMode !== 'review' && (!isNameOnlyStep || !hasNameDraft) && !reviewEditSection && !limitDialogOpen && !photoSheetOpen && (
	                <ScrollDownIndicator
	                    bottom={96}
	                />
	            )}

            <Dialog open={timeLimitDialogOpen} onClose={() => setTimeLimitDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 900 }}>{localize("시간을 더 추가할 수 없어요")}</DialogTitle>
                <DialogContent><Typography sx={{ color: '#555', fontWeight: 650 }}>{localize("하루 ")}{MAX_DAILY_REMINDER_TIMES}{localize("번까지만 설정할 수 있어요.")}</Typography></DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}><Button onClick={() => setTimeLimitDialogOpen(false)} sx={{ minHeight: 46, px: 3, bgcolor: '#ffe566', color: '#5a4a00', fontWeight: 850, borderRadius: 2.5, '&:hover': { bgcolor: '#ffdf33' } }}>{localize("확인")}</Button></DialogActions>
            </Dialog>
	            {aiConsentDialog}
            <AppToast
	                open={toast.open}
                message={localize(toast.message)}
                severity={toast.severity}
                autoHideDuration={toast.showMedicationList ? null : 3000}
                action={toast.showMedicationList ? (
                    <Button onClick={() => navigate('/medications')} sx={{ minHeight: 48, flexShrink: 0 }}>{localize("약 목록 보기")}</Button>
                ) : null}
                onClose={() => setToast(prev => ({ ...prev, open: false }))}
            />

	            <Dialog
	                open={limitDialogOpen}
	                onClose={closeLimitDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, color: '#1a1a1a', pb: 0.8 }}>{localize("더 등록하려면 광고가 필요해요")}</DialogTitle>
                <DialogContent sx={{ color: '#666', fontWeight: 650, lineHeight: 1.55, wordBreak: 'keep-all', pt: 0.5 }}>{localize("광고를 보거나 광고 제거 혜택으로 약 ")}{medicationLimit?.rewardSlots || 3}{localize("개를 더 추가할 수 있어요.")}</DialogContent>
	                <DialogActions sx={{ p: 2, pt: 0, ...(language !== 'ko' && { flexDirection: 'column', alignItems: 'stretch', gap: 1, '& > :not(style) ~ :not(style)': { ml: 0 } }) }}>
                    <Button onClick={closeLimitDialog} disabled={rewardSubmitting} sx={{ color: '#666', fontWeight: 750 }}>{localize("취소")}</Button>
                    <Button
                        onClick={requestMedicationSlotReward}
                        disabled={rewardSubmitting}
                        variant="contained"
                        startIcon={<PlayCircleIcon />}
                        sx={{
                            bgcolor: '#ffe566',
                            color: '#5a4a00',
                            fontWeight: 850,
                            borderRadius: 2,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                        }}
                    >
                        {rewardSubmitting ? <LoadingLabel /> : language === 'ko' ? localize("등록 개수 늘리기") : <Typography component="span" sx={{ font: 'inherit', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{localize("등록 개수 늘리기")}</Typography>}
                    </Button>
	                </DialogActions>
	            </Dialog>

	            <Dialog
	                open={photoPreviewOpen && !!imagePreview}
	                onClose={() => setPhotoPreviewOpen(false)}
	                maxWidth="xs"
	                fullWidth
	                PaperProps={{
	                    sx: {
	                        borderRadius: 3,
	                        overflow: 'hidden',
	                    },
	                }}
	            >
	                <DialogTitle sx={{ fontWeight: 900, color: '#1a1a1a', pb: 0.8 }}>{localize("올린 사진")}</DialogTitle>
	                <DialogContent sx={{ p: 1.5, pt: 0 }}>
	                    <Box
	                        sx={{
	                            width: '100%',
	                            maxHeight: '72vh',
	                            borderRadius: 2.5,
	                            overflow: 'hidden',
	                            bgcolor: '#f8fafc',
	                            border: '1px solid #eef2f7',
	                            display: 'flex',
	                            alignItems: 'center',
	                            justifyContent: 'center',
	                        }}
	                    >
	                        <Box
	                            component="img"
	                            src={imagePreview || ''}
	                            alt=""
	                            sx={{
	                                width: '100%',
	                                maxHeight: '72vh',
	                                objectFit: 'contain',
	                                display: 'block',
	                            }}
	                        />
	                    </Box>
	                </DialogContent>
	                <DialogActions sx={{ p: 1.5, pt: 0 }}>
	                    <Button
	                        fullWidth
	                        onClick={() => setPhotoPreviewOpen(false)}
	                        sx={{
	                            minHeight: 46,
	                            borderRadius: 2.5,
	                            bgcolor: '#ffe566',
	                            color: '#5a4a00',
	                            fontSize: '0.98rem',
	                            fontWeight: 900,
	                            boxShadow: 'none',
	                            '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
	                        }}
	                    >{localize("닫기")}</Button>
	                </DialogActions>
	            </Dialog>

	            <Dialog
	                open={!!pendingTimePreset || !!pendingReviewTimePreset || !!pendingSelectedReminderTimePreset}
                onClose={() => {
                    setPendingTimePreset(null);
                    setPendingReviewTimePreset(null);
                    setPendingSelectedReminderTimePreset(null);
                }}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, color: '#1a1a1a', pb: 0.8 }}>{localize("알림 시간을 바꿀까요?")}</DialogTitle>
                <DialogContent sx={{ pt: 0.5 }}>
                    <SingleLineText sx={{ color: '#666', fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.55, wordBreak: 'keep-all' }}>{localize("직접 설정한 시간이 아래 시간으로 바뀌어요.")}</SingleLineText>
                    {(pendingTimePreset || pendingReviewTimePreset || pendingSelectedReminderTimePreset)?.times && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65, mt: 1 }}>
                            {(pendingTimePreset || pendingReviewTimePreset || pendingSelectedReminderTimePreset).times.map(time => (
                                <Box
                                    key={time}
                                    component="span"
                                    sx={{
                                        px: 0.9,
                                        py: 0.45,
                                        borderRadius: '999px',
                                        bgcolor: '#fffbeb',
                                        border: '1px solid #fde68a',
                                        color: '#5a4a00',
                                        fontSize: '0.88rem',
                                        fontWeight: 850,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {formatTimesKorean([time])}
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, p: 2, pt: 0 }}>
                    <Button
                        onClick={() => {
                            setPendingTimePreset(null);
                            setPendingReviewTimePreset(null);
                            setPendingSelectedReminderTimePreset(null);
                        }}
                        sx={{
                            minHeight: 48,
                            borderRadius: 2.5,
                            bgcolor: '#f3f4f6',
                            color: '#555',
                            fontSize: '0.98rem',
                            fontWeight: 850,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#e5e7eb', boxShadow: 'none' },
                        }}
                    >{localize("취소")}</Button>
                    <Button
                        onClick={() => {
                            if (pendingTimePreset) setPresetCount(pendingTimePreset);
                            if (pendingReviewTimePreset) applyReviewTimePreset(pendingReviewTimePreset);
                            if (pendingSelectedReminderTimePreset) {
                                applyReminderPresetToMedication(
                                    pendingSelectedReminderTimePreset.medKey,
                                    pendingSelectedReminderTimePreset,
                                );
                            }
                            setPendingTimePreset(null);
                            setPendingReviewTimePreset(null);
                            setPendingSelectedReminderTimePreset(null);
                        }}
                        variant="contained"
                        sx={{
                            minHeight: 48,
                            bgcolor: '#ffe566',
                            color: '#5a4a00',
                            fontSize: '0.98rem',
                            fontWeight: 900,
                            borderRadius: 2.5,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                        }}
                    >{localize("바꾸기")}</Button>
                </DialogActions>
            </Dialog>

	            <BottomSheet open={medListSheetOpen} onClose={() => setMedListSheetOpen(false)} maxHeight="70vh">
	                <Box sx={{ pb: 0.5 }}>
                    <SingleLineText sx={{ fontSize: '1.18rem', fontWeight: 900, color: '#1a1a1a', mb: 1 }}>{localize("등록한 약")}</SingleLineText>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.2 }}>
                        {filledMeds.map((med, index) => (
                            <Box
                                key={med.key}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    pl: 1.2,
                                    pr: 1.2,
                                    py: 0.9,
                                    minHeight: 54,
                                    borderRadius: 2.8,
                                    bgcolor: '#fff',
                                    border: '1px solid #e9ecef',
                                    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.035)',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: '50%',
                                        bgcolor: '#fff8d6',
                                        color: '#8a7000',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        fontSize: '0.86rem',
                                        fontWeight: 850,
                                    }}
                                >
                                    {index + 1}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '1.03rem', fontWeight: 780, color: '#1a1a1a', lineHeight: 1.3 }} noWrap>
                                        {med.name}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    <Button
                        fullWidth
                        onClick={() => setMedListSheetOpen(false)}
                        sx={{
                            py: 1.15,
                            borderRadius: 3,
                            bgcolor: '#ffe566',
                            color: '#5a4a00',
                            fontWeight: 900,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                        }}
                    >{localize("확인")}</Button>
	                </Box>
	            </BottomSheet>

		            <FeatureUpdateDialog platform={window.NATIVE_PLATFORM || runtimeInfo?.platform} open={featureUpdateOpen && needsFeatureUpdate} onClose={() => setFeatureUpdateOpen(false)} />
		            <MedicationPhotoSheet
		                open={medImageSheetOpen}
		                onClose={() => setMedImageSheetOpen(false)}
		                onSelectFile={handleMedicationImageSelect}
		                subtitle={localize("선택 사항이에요. 나중에 약을 알아보기 쉽게 도와줘요.")}
		                cameraInputRef={medicationCameraInputRef}
		                libraryInputRef={medicationLibraryInputRef}
		                onPickCamera={nativeMedicationPhotoBridgeSupported ? () => {
		                    setMedImageSheetOpen(false);
		                    setTimeout(() => requestNativeMedicationImage('camera'), 180);
		                } : undefined}
		                onPickLibrary={nativeMedicationPhotoBridgeSupported ? () => {
		                    setMedImageSheetOpen(false);
		                    setTimeout(() => requestNativeMedicationImage('library'), 180);
		                } : undefined}
		                onDelete={medications.find(med => med.key === pendingMedImageKeyRef.current)?.imagePreview ? clearPendingMedicationImage : undefined}
		                singlePickerMode={isNativeApp && !nativeMedicationPhotoBridgeSupported}
		                singlePickerLabel={medications.find(med => med.key === pendingMedImageKeyRef.current)?.imagePreview ? translationKey('사진 변경') : translationKey('사진 선택')}
		            />

		            <MedicationPhotoSheet
		                open={photoSheetOpen && nativePhotoSupported}
		                onClose={() => { photoConsentRef.current = false; setPhotoSheetOpen(false); }}
		                title={localize("처방전·약봉투 사진")}
                        analysisConsent
		                subtitle={localize("사진에서 약 이름과 복용법을 찾아볼게요.")}
		                cameraDisabled={photoBusy}
		                libraryDisabled={photoBusy}
		                disabledReason=""
	                onPickCamera={() => {
	                    setPhotoSheetOpen(false);
	                    requestNativeImage('camera', true);
	                }}
	                onPickLibrary={() => {
	                    setPhotoSheetOpen(false);
		                    requestNativeImage('library', true);
		                }}
		            />
	        </Box>
	    );
}
